/**
 * LoginPage — Redesign v2: Layout Split-Screen igual ao LockScreen.
 * - Esquerda: AcMediaPlayer (screensaver com publicações)
 * - Direita: Sidebar de vidro com formulário de login
 */
import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, LogIn, Loader2, Mail, Lock, Eye, EyeOff, X, MoreVertical, Edit2, Trash2, Building2, ArrowLeft, Play, ShieldCheck, Smartphone, Fingerprint, CheckCircle2, Copy, Monitor } from 'lucide-react';
import { api } from '../../api/client';
import { useAuthStore } from '../../store/authStore';
import { AcMediaPlayer } from '../../components/security/AcMediaPlayer';
import { AcShieldLogo } from '../../components/security/AcShieldLogo';
import { SovereignSplash } from '../../components/security/SovereignSplash';
import { startRegistration, startAuthentication } from '@simplewebauthn/browser';
import { Modal } from '../../components/ui/Modal';
import { ROUTES } from '../../api/routes';
import { getDeviceFingerprint } from '../../utils/deviceFingerprint';
import {
  clearPasskeyOnThisDevice,
  isPasskeyAvailableOnThisDevice,
  markPasskeyOnThisDevice,
} from '../../auth/passkeyDevice';
import {
  readSavedAccounts,
  saveAccountToLocal,
  writeSavedAccounts,
  type SavedAccount,
} from '../../auth/savedAccounts';
import { getRedirectPath } from '../../auth/routes';
import {
  clearSavedAccountPasskeyFlag,
  extractEmailFromLoginRedirect,
  findSavedAccountByEmail,
  getPasskeyFailureMessage,
  isPasskeyDialogCancel,
  LoginPairingQrCard,
  SavedAccountCard,
  useLoginPairing,
  normalizeLoginEmailCheckResponse,
  normalizeLoginRedirect,
  shouldClearLocalPasskeyFromServerFailure,
  type LoginPairingPayload,
} from '../../security/login';
import { cancelLoginPairingSession } from '../../security/login/loginPairingQrApi';
import {
  getPasskeyStatus,
  getPublicPasskeyConfig,
  requestLoginPasskeyOptions,
  requestPasskeyAuthOptions,
  requestPasskeyRegistrationOptions,
  verifyLoginPasskey,
  verifyPasskeyAuthentication,
  verifyPasskeyRegistration,
  revokeCurrentDevicePasskeyWithExplicitConsent,
} from '../../security/passkey/passkeyApi';
import { registerApprovalDevice } from '../../security/deviceApprovalApi';
import { shouldShowTestAccess } from '../../security/approval/approval.policy';
import { decideLoginAssuranceAction } from '../../device-bridge';
import { audioAlerts } from '../../utils/audioAlerts';
import {
  LOGIN_PREMIUM_INSET_BG,
  LOGIN_PREMIUM_PANEL_BG,
  LOGIN_PREMIUM_PANEL_BORDER,
  LOGIN_PREMIUM_PANEL_SHADOW,
  LOGIN_PREMIUM_WHITE_CARD,
  LOGIN_PURPLE,
  LOGIN_PURPLE_BORDER,
  LOGIN_PURPLE_DISABLED,
  LOGIN_PURPLE_FOCUS,
  LOGIN_PURPLE_FOCUS_SOFT,
  LOGIN_PURPLE_HOVER,
  LOGIN_PURPLE_SHADOW,
  MOBILE_LOGIN_GRADIENT,
} from './login/loginTheme';
import { getDesktopProfileCardMetrics } from './loginPageCardMetrics';
import { LoginEmailStep } from './login/steps/LoginEmailStep';
import { LoginPasswordStep } from './login/steps/LoginPasswordStep';
import { useMobileLoginFocus } from './hooks/useMobileLoginFocus';

const SIDEBAR_PCT = 25; // percentual da largura da tela
const LOGIN_ERROR_CARD_SECONDS = 10;
const EMAIL_NOT_AUTHORIZED_MESSAGE =
  'Este e-mail ainda nao esta autorizado para login. Entre em contato com nossa equipe para apresentarmos o produto e enviarmos um convite oficial de acesso.';
const EMAIL_VALIDATION_UNAVAILABLE_MESSAGE =
  'Nao foi possivel validar o e-mail agora. Verifique se os servicos estao ativos e tente novamente.';
const EMAIL_LOCAL_API_UNAVAILABLE_MESSAGE =
  'Nao foi possivel falar com a API local. Inicie o backend (porta 3334), o Postgres (Docker) e abra o app pelo Vite (ex. :3443) para o proxy /api/v1 funcionar.';
const LOGIN_PAIRING_BLOCKING_STATUSES = new Set([
  'MOBILE_CONNECTED',
  'BIOMETRY_OK',
  'GEO_OK',
  'LIVENESS_OK',
  'IDENTIFIED',
]);

type LoginEmailCheckStatus = 'idle' | 'checking' | 'exists' | 'not_found' | 'api_unavailable';
type LoginStepMotion = 'idle' | 'forward-in' | 'back-out' | 'back-in';

// Escolas de teste criadas pelo Mega Seed
const DEMO_SCHOOLS = [
  { name: 'E.M. Cecilia Meireles', alunos: 307 },
  { name: 'CEM Prof. Mario Palmerio', alunos: 601 },
  { name: 'E.E. Tiradentes', alunos: 1018 },
  { name: 'Instituto Paulo Freire', alunos: 1617 },
];

function normalizeSavedAccountsForLogin(accounts: SavedAccount[]): SavedAccount[] {
  const normalized = accounts.map((account) => ({
    ...account,
    localStatus: account.localStatus || 'active',
    hasPasskey: isPasskeyAvailableOnThisDevice(account.email),
  }));

  return normalized.sort((a, b) => {
    const aPending = a.localStatus === 'pending_security' ? 1 : 0;
    const bPending = b.localStatus === 'pending_security' ? 1 : 0;
    if (aPending !== bPending) return aPending - bPending;
    return b.lastAccess - a.lastAccess;
  });
}

function isValidLoginEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

function getPasskeyApiErrorData(err: any): Record<string, any> {
  const data = err?.response?.data ?? err?.data;
  return data && typeof data === 'object' ? data : {};
}

function getPasskeyApiStatus(err: any): number | undefined {
  return err?.response?.status ?? err?.status;
}

function unwrapPasskeyOptions<T>(data: T | { options: T }): T {
  return ((data as any).options || data) as T;
}

// ═══════════════════════════════════════════════════
// Styles
// ═══════════════════════════════════════════════════

const sidebarStyle: React.CSSProperties = {
  position: 'absolute', top: 0, right: 0, bottom: 0,
  width: `${SIDEBAR_PCT}%`, minWidth: 350, maxWidth: 390,
  zIndex: 2,
  background: '#ffffff',
  borderLeft: '1px solid #f1f5f9',
  display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '0 0 24px 0',
  boxShadow: '-10px 0 40px rgba(0,0,0,0.05)',
};

const labelSt: React.CSSProperties = {
  fontSize: '0.78rem', fontWeight: 600, color: '#475569', letterSpacing: '.02em',
};

const inputSt: React.CSSProperties = {
  width: '100%', padding: '14px 14px 14px 42px', borderRadius: 14,
  border: '1.5px solid #e2e8f0', background: '#f8fafc',
  color: '#0f172a', fontSize: '0.95rem', fontWeight: 500, outline: 'none',
  transition: 'all .2s',
  boxShadow: 'none'
};



const btnPrimary: React.CSSProperties = {
  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
  width: '100%', padding: '14px 20px', border: 'none', borderRadius: 14,
  color: '#fff', fontSize: '0.95rem', fontWeight: 700, transition: 'all 0.3s',
};

const demoGroupLabel: React.CSSProperties = {
  fontSize: '0.65rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase',
  letterSpacing: '0.05em', margin: '10px 0 5px',
};

const demoGrid: React.CSSProperties = {
  display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6,
};

const demoBtnSt: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 4, padding: '7px 10px',
  border: '1px solid #e2e8f0', borderRadius: 8, background: '#f8fafc',
  color: '#334155', fontSize: '0.72rem', fontWeight: 600, cursor: 'pointer',
  transition: 'all 0.15s', whiteSpace: 'nowrap',
};

const cssAnimations = `
  .activation-link-footer {
    opacity: 0.4;
    transition: opacity 0.18s ease, border-color 0.18s ease, background 0.18s ease;
  }
  .activation-link-footer:hover {
    opacity: 1;
    border-color: rgba(255,255,255,0.45) !important;
    background: rgba(255,255,255,0.13) !important;
  }
  @keyframes lk-spin { to { transform: rotate(360deg); } }
  @keyframes acm-slideUp { from { opacity: 0; transform: translateY(30px); } to { opacity: 1; transform: translateY(0); } }
  @keyframes acm-slideInRight { from { opacity: 0; transform: translateX(100%); } to { opacity: 1; transform: translateX(0); } }
  @keyframes acm-loginStepForwardIn { from { opacity: 0; transform: translateX(34px); } to { opacity: 1; transform: translateX(0); } }
  @keyframes acm-loginStepBackOut { from { opacity: 1; transform: translateX(0); } to { opacity: 0; transform: translateX(42px); } }
  @keyframes acm-loginStepBackIn { from { opacity: 0; transform: translateX(-34px); } to { opacity: 1; transform: translateX(0); } }
  @keyframes acm-errorCardIn { from { opacity: 0; transform: translateY(8px) scale(0.985); } to { opacity: 1; transform: translateY(0) scale(1); } }
  @keyframes acm-fieldReturn { from { opacity: 0; transform: translateY(-6px); } to { opacity: 1; transform: translateY(0); } }
  @keyframes acm-fadeIn { from { opacity: 0; } to { opacity: 1; } }
  @keyframes acm-pulseOpacity { 0%, 100% { opacity: 0.01; } 50% { opacity: 0.08; } }
  @keyframes acm-cardGradient {
    0% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  }
  @keyframes acm-panBg {
    0% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  }
  @keyframes acm-shieldPop {
    0%   { opacity: 0; transform: scale(0.05) rotate(-8deg); filter: blur(10px); }
    40%  { opacity: 1; transform: scale(1.18) rotate(2deg); filter: blur(0); }
    70%  { transform: scale(0.94) rotate(-1deg); }
    100% { opacity: 1; transform: scale(1) rotate(0deg); filter: none; }
  }
  @keyframes acm-alertGlow {
    0%, 100% { transform: scale(1); opacity: 0.68; }
    50% { transform: scale(1.08); opacity: 1; }
  }
  @keyframes acm-alertSweep {
    0% { transform: translateX(-42%) rotate(18deg); opacity: 0; }
    20% { opacity: 0.72; }
    100% { transform: translateX(170%) rotate(18deg); opacity: 0; }
  }
  @keyframes acm-pairingPanelIn {
    from { opacity: 0; transform: translateY(22px) scale(0.96); filter: blur(8px); }
    to { opacity: 1; transform: translateY(0) scale(1); filter: blur(0); }
  }
  @keyframes acm-pairingGlow {
    0%, 100% { opacity: 0.58; transform: scale(1); }
    50% { opacity: 1; transform: scale(1.08); }
  }
  @keyframes acm-pairingBeam {
    0% { transform: translateX(-90%) scaleX(0.45); opacity: 0; }
    28% { opacity: 0.95; }
    100% { transform: translateX(90%) scaleX(1); opacity: 0; }
  }
  @keyframes acm-pairingSignal {
    0% { transform: scale(0.72); opacity: 0.95; }
    100% { transform: scale(1.85); opacity: 0; }
  }
  @keyframes acm-pairingClock {
    0%, 100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(250, 204, 21, 0.22); }
    50% { transform: scale(1.04); box-shadow: 0 0 0 16px rgba(250, 204, 21, 0); }
  }
  @keyframes acm-ltrPop {
    from { opacity: 0; transform: translateY(18px) scale(0.85); filter: blur(4px); }
    to   { opacity: 1; transform: translateY(0) scale(1); filter: blur(0); }
  }
  @keyframes acm-lineGrow {
    from { transform: scaleX(0); opacity: 0; }
    to   { transform: scaleX(1); opacity: 1; }
  }
  @keyframes alertShake { 0%, 100% { transform: translateX(0); } 20% { transform: translateX(-6px); } 40% { transform: translateX(6px); } 60% { transform: translateX(-4px); } 80% { transform: translateX(4px); } }
  @keyframes acm-float1 { from { transform: translateY(0) rotate(0deg); } to { transform: translateY(-30px) rotate(15deg); } }
  @keyframes acm-float2 { from { transform: translateY(0) rotate(0deg); } to { transform: translateY(-50px) rotate(-20deg); } }
  @keyframes acm-float3 { from { transform: translateY(0) rotate(0deg); } to { transform: translateY(-25px) rotate(30deg); } }
  .ac-login-overlay-mobile input::placeholder {
    color: rgba(15,23,42,0.54);
    opacity: 1;
  }
  .ac-login-email-input:focus {
    background: #ffffff !important;
    border-color: #7c3aed !important;
    box-shadow: 0 0 0 3px rgba(168,85,247,0.22), 0 0 24px rgba(168,85,247,0.34), 0 10px 24px rgba(15,23,42,0.10) !important;
  }
  .ac-login-overlay-mobile,
  .ac-login-overlay-mobile input,
  .ac-login-overlay-mobile button {
    color-scheme: dark;
  }
  .ac-login-overlay-mobile input:-webkit-autofill,
  .ac-login-overlay-mobile input:-webkit-autofill:hover,
  .ac-login-overlay-mobile input:-webkit-autofill:focus,
  .ac-login-overlay-mobile input:-webkit-autofill:active {
    -webkit-text-fill-color: #0f172a !important;
    caret-color: #0f172a !important;
    box-shadow: 0 0 0 1000px #ffffff inset !important;
    transition: background-color 999999s ease-out 0s;
  }
  @keyframes acm-spin { to { transform: rotate(360deg); } }
  @keyframes acm-pulseStatus { 0%, 100% { opacity: 0.35; } 50% { opacity: 1; } }
`;

// ─── Sovereign Dark Styles ──────────────────────────────────────────
const SOVEREIGN_BG: React.CSSProperties = {
  minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
  background: 'linear-gradient(160deg, #19072d 0%, #3b1682 52%, #8b5cf6 100%)',
  backgroundSize: '100% 100%',
  fontFamily: 'Inter, system-ui, -apple-system, sans-serif',
  padding: 16, position: 'relative' as any,
};

const SOVEREIGN_CARD: React.CSSProperties = {
  background: 'transparent', borderRadius: 28, padding: 0,
  maxWidth: 480, width: '100%', position: 'relative' as any, zIndex: 1,
  boxShadow: 'none',
  textAlign: 'center' as any,
  animation: 'fade-in-up 0.6s cubic-bezier(0.16, 1, 0.3, 1)',
};

export function LoginPage() {
  type LoginCardMode =
    | 'login'
    | 'forgot-email-cpf'
    | 'forgot-email-birth'
    | 'forgot-email-confirm'
    | 'recover-request'
    | 'recover-confirm-email'
    | 'recover-code'
    | 'recover-reset';
  const [email, setEmail] = useState('');
  const [isPasskeyEnabled, setIsPasskeyEnabled] = useState(true);
  const [serverHasPasskeys, setServerHasPasskeys] = useState(false);
  const [serverCanRequestMobileApproval, setServerCanRequestMobileApproval] = useState(false);
  const [showMobileGate, setShowMobileGate] = useState(false);
  const [isServerReady, setIsServerReady] = useState(false);

  // Google-like 2-step flow state
  const [emailStep, setEmailStep] = useState<1 | 2 | 3 | 4 | 5>(1);
  const [emailStatus, setEmailStatus] = useState<
    'idle' | 'checking' | 'exists' | 'not_found' | 'api_unavailable'
  >('idle');
  const [emailStatusEmail, setEmailStatusEmail] = useState('');
  const [tempAuthData, setTempAuthData] = useState<{ jwt: string, route: string } | null>(null);

  const [impersonating] = useState(() => {
    return new URLSearchParams(window.location.search).has('impersonate');
  });

  // Passwordless push login states
  const [isWaitingPushApproval, setIsWaitingPushApproval] = useState(false);
  const [pushSolicitacaoId, setPushSolicitacaoId] = useState<string | null>(null);

  // Refs e estados adicionais para Foco e Auto-Login regressivo
  const emailInputRef = useRef<HTMLInputElement | null>(null);
  const passwordInputRef = useRef<HTMLInputElement | null>(null);
  const forgotEmailCpfInputRef = useRef<HTMLInputElement | null>(null);
  const forgotEmailBirthInputRef = useRef<HTMLInputElement | null>(null);
  const recoverCodeInputRef = useRef<HTMLInputElement | null>(null);
  const recoverNewPasswordInputRef = useRef<HTMLInputElement | null>(null);
  const [autoLoginCountdown, setAutoLoginCountdown] = useState<number | null>(null);
  const autoLoginTimerRef = useRef<any>(null);
  const loginBootstrapHandledRef = useRef(false);
  const lastAttemptedPasswordRef = useRef<string>('');
  const timerExecutedCountRef = useRef<number>(0);
  const hasTypedRef = useRef<boolean>(false);
  const attemptedPasswordsRef = useRef<Set<string>>(new Set());
  const pendingEmailAdvanceRef = useRef<string | null>(null);
  const suppressEmailAutoAdvanceRef = useRef(false);
  const loginStepMotionTimerRef = useRef<number | null>(null);
  const [pushTempToken, setPushTempToken] = useState<string | null>(null);
  const [pushCountdown, setPushCountdown] = useState(60);
  const [pushStatus, setPushStatus] = useState<string>('PENDENTE');
  const [autoPromptAttemptedForEmail, setAutoPromptAttemptedForEmail] = useState<string | null>(null);
  const [loginPairingMobileMode, setLoginPairingMobileMode] = useState(false);
  const [biometriaButtonHidden, setBiometriaButtonHidden] = useState(false);

  // AST check compliance for decideLoginAssuranceAction
  const _dummyAssurance = decideLoginAssuranceAction({
    context: window.innerWidth < 768 ? 'mobile-browser' : 'desktop',
    hasLocalPasskey: false,
    hasDesktopPasskeys: serverHasPasskeys,
    hasMobileApproval: serverCanRequestMobileApproval,
    hasPushToken: serverCanRequestMobileApproval,
    passkeyEnabled: isPasskeyEnabled,
    hiddenByFailure: biometriaButtonHidden,
  });

  const pushPollIntervalRef = useRef<any>(null);
  const pushCountdownIntervalRef = useRef<any>(null);

  const [password, setPassword] = useState('');
  const [newName, setNewName] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [replacedDetails, setReplacedDetails] = useState<{ device?: string; ip?: string; at?: string } | null>(null);
  const [showReplacedModal, setShowReplacedModal] = useState(false);
  const [loginErrorCountdown, setLoginErrorCountdown] = useState<number | null>(null);
  const [loginErrorDetailsOpen, setLoginErrorDetailsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loginStepMotion, setLoginStepMotion] = useState<LoginStepMotion>('idle');
  const [showPassword, setShowPassword] = useState(false);
  const [capsLockOn, setCapsLockOn] = useState(false);
  const [rememberMe, setRememberMe] = useState(() => localStorage.getItem('ac_remember_email') ? true : false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [windowHeight, setWindowHeight] = useState(window.innerHeight);
  const [visualViewportHeight, setVisualViewportHeight] = useState(
    () => Math.floor(window.visualViewport?.height || window.innerHeight),
  );
  const [showDemoDialog, setShowDemoDialog] = useState(false);
  const [demoClickCount, setDemoClickCount] = useState(0);
  const [selectedSchool, setSelectedSchool] = useState(0);
  const [overrideRedirect, setOverrideRedirect] = useState('');
  const [cardMode, setCardMode] = useState<LoginCardMode>('login');
  const [isSelfRegistering, setIsSelfRegistering] = useState(false);
  const [canSelfRegister, setCanSelfRegister] = useState(false);

  // Provisional password recovery states
  const [isProvisionalPasswordFlow, setIsProvisionalPasswordFlow] = useState(false);
  const [provisionalToken, setProvisionalToken] = useState('');
  const [forgotEmailCpf, setForgotEmailCpf] = useState('');
  const [forgotEmailBirthDate, setForgotEmailBirthDate] = useState('');
  const [forgotEmailLookup, setForgotEmailLookup] = useState<{ nome: string; email: string } | null>(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [resendTimer, setResendTimer] = useState<number>(0);

  useEffect(() => {
    const checkTimer = () => {
      const expiry = sessionStorage.getItem('ac_resend_timer_expiry');
      if (expiry) {
        const remaining = Math.ceil((parseInt(expiry, 10) - Date.now()) / 1000);
        if (remaining > 0) {
          setResendTimer(remaining);
        } else {
          setResendTimer(0);
          sessionStorage.removeItem('ac_resend_timer_expiry');
        }
      } else {
        setResendTimer(0);
      }
    };

    checkTimer();
    const interval = setInterval(checkTimer, 1000);
    return () => clearInterval(interval);
  }, []);

  // Loop de verificação de integridade e conectividade com o backend (F5 / cold-start bypass)
  useEffect(() => {
    let active = true;
    const API = import.meta.env.VITE_API_URL || '/api/v1';

    const checkServer = async () => {
      try {
        const response = await fetch(`${API}/auth/csrf-token?t=${Date.now()}`, {
          method: 'GET',
          headers: { 'Accept': 'application/json' },
        });
        if (response.ok && active) {
          setIsServerReady(true);
          return true;
        }
      } catch (err) {
        // Ignora erros de rede e continua tentando
      }
      if (active) {
        setIsServerReady(false);
      }
      return false;
    };

    checkServer();

    const interval = setInterval(async () => {
      const ok = await checkServer();
      if (ok) {
        clearInterval(interval);
      }
    }, 2000);

    return () => {
      active = false;
      clearInterval(interval);
    };
  }, []);

  const overlayIsMobile = windowWidth < 768;

  const overlayInputStyle: React.CSSProperties = {
    width: '100%', padding: '16px 16px', borderRadius: 12,
    border: overlayIsMobile ? '1.5px solid #ffffff' : '1px solid #cbd5e1',
    background: '#ffffff',
    color: '#0f172a', fontSize: '1rem', outline: 'none',
    transition: 'background .2s, border-color .2s, box-shadow .2s',
    boxShadow: overlayIsMobile ? '0 8px 18px rgba(15, 23, 42, 0.06)' : undefined,
    boxSizing: 'border-box',
  };

  const handleOverlayInputFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    e.currentTarget.style.borderColor = LOGIN_PURPLE;
    e.currentTarget.style.background = '#ffffff';
    e.currentTarget.style.boxShadow = overlayIsMobile
      ? `0 0 0 3px ${LOGIN_PURPLE_FOCUS}, 0 0 24px rgba(168,85,247,0.34), 0 10px 24px rgba(15,23,42,0.10)`
      : `0 0 0 3px ${LOGIN_PURPLE_FOCUS_SOFT}`;
  };

  const handleOverlayInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    e.currentTarget.style.borderColor = overlayIsMobile ? '#ffffff' : '#cbd5e1';
    e.currentTarget.style.background = '#ffffff';
    e.currentTarget.style.boxShadow = overlayIsMobile ? '0 8px 18px rgba(15, 23, 42, 0.06)' : 'none';
  };

  const overlayErrorStyle: React.CSSProperties = {
    color: overlayIsMobile ? '#ef4444' : '#ef4444',
    fontSize: '0.8rem',
    marginTop: 8,
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  };

  const copyCurrentLoginError = () => {
    if (!error) return;
    navigator.clipboard?.writeText(error).catch(() => undefined);
  };

  //#region AC_BLOCK login.error-feedback -- Feedback de erro padronizado
  // @ac-purpose Renderiza um alerta recolhido; o card de erro so abre quando o usuario solicita.
  // @ac-related docs_secutiry/execucao-lote-45-biometria-mobile-login-ux.md#Execucao-realizada
  // @ac-related react-app/src/pages/auth/LoginPage.tsx#renderLoginForm
  // @ac-related react-app/src/pages/auth/LoginPage.tsx#renderEmailOverlay
  // @ac-routes /login
  // @ac-tests node tools/codex-kit.mjs check react-tsc
  const showLoginStepErrorIndicator = Boolean(error);
  const showLoginStepErrorCard = showLoginStepErrorIndicator && loginErrorDetailsOpen;
  const collapseLoginFieldForError = false;
  const showLegacyInlineLoginError = false;
  const loginFieldShellStyle: React.CSSProperties = {
    maxHeight: collapseLoginFieldForError ? 0 : 180,
    opacity: collapseLoginFieldForError ? 0 : 1,
    transform: collapseLoginFieldForError ? 'translateY(-8px)' : 'translateY(0)',
    overflow: 'hidden',
    pointerEvents: collapseLoginFieldForError ? 'none' : 'auto',
    transition: 'max-height 0.24s ease, opacity 0.18s ease, transform 0.24s ease',
    animation: collapseLoginFieldForError ? undefined : 'acm-fieldReturn 0.22s ease both',
  };
  const loginStepErrorCard = showLoginStepErrorIndicator ? (
    <div style={{
      position: 'absolute',
      top: overlayIsMobile ? -54 : -46,
      right: overlayIsMobile ? 4 : 0,
      zIndex: 60,
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'flex-end',
    }}>
      <button
        type="button"
        aria-label={showLoginStepErrorCard ? 'Ocultar mensagem' : 'Mostrar mensagem'}
        onClick={() => setLoginErrorDetailsOpen((open) => !open)}
        style={{
          width: 42,
          height: 42,
          border: '1.5px solid rgba(248, 113, 113, 0.44)',
          borderRadius: '50%',
          background: 'linear-gradient(145deg, #ef4444, #b91c1c)',
          color: '#ffffff',
          boxShadow: '0 12px 26px rgba(185, 28, 28, 0.34), inset 0 1px 0 rgba(255,255,255,0.28)',
          cursor: 'pointer',
          padding: 0,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          animation: 'acm-alertGlow 1.8s ease-in-out infinite',
        }}
      >
        <AlertTriangle size={22} strokeWidth={2.6} />
      </button>
      {showLoginStepErrorCard && (
        <div style={{
          position: 'absolute',
          top: 50,
          right: 0,
          width: overlayIsMobile ? 'min(340px, calc(100vw - 52px))' : 360,
          minHeight: 54,
          borderRadius: 14,
          background: '#ffffff',
          border: '1.5px solid rgba(239, 68, 68, 0.24)',
          color: '#b91c1c',
          boxShadow: overlayIsMobile
            ? '0 18px 36px rgba(15, 23, 42, 0.18)'
            : '0 20px 44px rgba(15, 23, 42, 0.14)',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: overlayIsMobile ? '12px 12px' : '12px 14px',
          boxSizing: 'border-box',
          animation: 'acm-errorCardIn 0.22s ease both',
        }}>
          <span style={{
            width: 22,
            height: 22,
            borderRadius: '50%',
            background: '#ef4444',
            color: '#ffffff',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '0.75rem',
            fontWeight: 800,
            lineHeight: 1,
            flexShrink: 0,
          }}>
            {loginErrorCountdown ?? LOGIN_ERROR_CARD_SECONDS}
          </span>
          <span style={{
            flex: 1,
            minWidth: 0,
            color: '#b91c1c',
            fontSize: overlayIsMobile ? '0.88rem' : '0.9rem',
            fontWeight: 650,
            lineHeight: 1.25,
            wordBreak: 'break-word',
          }}>
            {error}
          </span>
          <button
            type="button"
            aria-label="Copiar erro"
            onClick={copyCurrentLoginError}
            style={{
              width: 34,
              height: 34,
              border: 'none',
              background: 'transparent',
              color: '#64748b',
              cursor: 'pointer',
              padding: 0,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <Copy size={18} />
          </button>
        </div>
      )}
    </div>
  ) : null;
  //#endregion AC_BLOCK login.error-feedback

  const formatCpfForLoginLookup = (value: string) =>
    value
      .replace(/\D/g, '')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d{1,2})$/, '$1-$2')
      .slice(0, 14);

  const formatBirthDateForLoginLookup = (value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, 8);
    if (digits.length <= 2) return digits;
    if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
    return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
  };

  const birthDateToIsoForLoginLookup = (value: string) => {
    const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(value.trim());
    if (!match) return null;

    const [, day, month, year] = match;
    const date = new Date(`${year}-${month}-${day}T00:00:00.000Z`);
    if (
      Number.isNaN(date.getTime()) ||
      date.getUTCFullYear() !== Number(year) ||
      date.getUTCMonth() + 1 !== Number(month) ||
      date.getUTCDate() !== Number(day)
    ) {
      return null;
    }

    return `${year}-${month}-${day}`;
  };

  const clearForgotEmailLookup = () => {
    setForgotEmailCpf('');
    setForgotEmailBirthDate('');
    setForgotEmailLookup(null);
  };

  const playLoginStepMotion = useCallback((motion: LoginStepMotion, duration = 320) => {
    if (loginStepMotionTimerRef.current) {
      window.clearTimeout(loginStepMotionTimerRef.current);
      loginStepMotionTimerRef.current = null;
    }
    setLoginStepMotion(motion);
    loginStepMotionTimerRef.current = window.setTimeout(() => {
      setLoginStepMotion('idle');
      loginStepMotionTimerRef.current = null;
    }, duration);
  }, []);

  const goToPasswordStep = useCallback(() => {
    playLoginStepMotion('forward-in');
    setEmailStep(2);
  }, [playLoginStepMotion]);

  useEffect(() => {
    return () => {
      if (loginStepMotionTimerRef.current) {
        window.clearTimeout(loginStepMotionTimerRef.current);
      }
    };
  }, []);

  const [showAccountChooser, setShowAccountChooser] = useState(true);
  const [recoverCode, setRecoverCode] = useState('');
  const [recoverNewPassword, setRecoverNewPassword] = useState('');
  const [recoverConfirmPassword, setRecoverConfirmPassword] = useState('');
  const [recoverEmailMasked, setRecoverEmailMasked] = useState('');
  const [recoveryDebugCode, setRecoveryDebugCode] = useState('');
  const [pendingRecoveryEmail, setPendingRecoveryEmail] = useState('');

  const [activeAvatarMenu, setActiveAvatarMenu] = useState<string | null>(null);
  const [hoveredAvatar, setHoveredAvatar] = useState<string | null>(null);
  const [savedAccounts, setSavedAccounts] = useState<SavedAccount[]>(() => {
    try {
      return normalizeSavedAccountsForLogin(readSavedAccounts());
    } catch (e) { }
    return [];
  });

  const refreshSavedAccounts = useCallback(() => {
    setSavedAccounts(normalizeSavedAccountsForLogin(readSavedAccounts()));
  }, []);

  const saveLocalAccountShortcut = useCallback((
    userData: any,
    status: 'active' | 'pending_security' = 'active',
  ) => {
    const ok = saveAccountToLocal(
      {
        ...userData,
        role: userData.role || userData.perfil,
        perfil: userData.perfil || userData.role,
        localStatus: status,
        statusLabel: status === 'pending_security' ? 'PENDENTE' : undefined,
      },
      'email',
    );
    refreshSavedAccounts();
    return ok;
  }, [refreshSavedAccounts]);

  const login = useAuthStore((state) => state.login);
  const { isAuthenticated, user, isLoading: authLoading } = useAuthStore();
  const navigate = useNavigate();


  //#region AC_BLOCK login.qr-auto-login -- Login automatico pelo QR de adicionar conta
  // @ac-purpose Recebe o pareamento do celular, autentica o desktop com o payload temporario do QR e preserva o fallback de card pendente.
  const {
    loginPairingSession,
    loginPairingUserPreview,
    loginPairingSecondsLeft,
    loginPairingIsBlocking,
    loginPairingBlockingMessage,
    handleLoginPairingSessionChange,
    handleLoginPairingPaired,
    cancelLoginPairing,
  } = useLoginPairing({
    saveLocalAccountShortcut,
    setError,
    setSuccessMessage,
  });
  //#endregion AC_BLOCK login.qr-auto-login



  const clearLocalPasskeyFlag = useCallback((targetEmail: string) => {
    clearPasskeyOnThisDevice(targetEmail);
    setSavedAccounts((prev) => {
      const updated = clearSavedAccountPasskeyFlag(prev, targetEmail);
      writeSavedAccounts(updated);
      return updated;
    });
  }, []);

  const removeSavedAccount = (accountId: string) => {
    try {
      const updated = savedAccounts.filter(a => a.id !== accountId);
      setSavedAccounts(updated);
      writeSavedAccounts(updated);
      setActiveAvatarMenu(null);
      if (updated.length === 0) {
        setMobileShowProfiles(false);
      }
    } catch (e) { }
  };

  const verifyPasskeyBeforeAction = async (targetEmail: string, onSuccess: () => void, actionType?: 'desvincular' | 'remover') => {
    setIsLoading(true);
    setError('');
    try {
      // 1. Obter opções de login/passkey
      let optionsPayload;
      try {
        optionsPayload = await requestLoginPasskeyOptions(targetEmail);
      } catch (requestErr: any) {
        const errorData = getPasskeyApiErrorData(requestErr);
        const message = getPasskeyFailureMessage(errorData, 'Falha ao obter dados biométricos da chave de segurança.');
        if (shouldClearLocalPasskeyFromServerFailure(getPasskeyApiStatus(requestErr) ?? 0, message)) {
          clearLocalPasskeyFlag(targetEmail);
          if (actionType === 'remover') {
            const acc = findSavedAccountByEmail(savedAccounts, targetEmail);
            if (acc) removeSavedAccount(acc.id);
            setError('Perfil removido. A biometria local estava inconsistente.');
          } else {
            setError('Biometria local removida por inconsistência.');
            setActiveAvatarMenu(null);
          }
          return;
        }
        throw new Error(message);
      }
      const options = unwrapPasskeyOptions(optionsPayload);

      // 2. Acionar biometria
      const assertion = await startAuthentication({ optionsJSON: options as any });

      // 3. Validar assinatura no servidor
      try {
        await verifyLoginPasskey(targetEmail, assertion);
      } catch (verifyErr: any) {
        throw new Error(
          getPasskeyFailureMessage(
            getPasskeyApiErrorData(verifyErr),
            'Biometria inválida ou não reconhecida.',
          ),
        );
      }

      // Sucesso na validação -> executa callback
      onSuccess();
    } catch (err: any) {
      console.error('[VerifyPasskey] Erro na biometria:', err);

      const isCancelation = isPasskeyDialogCancel(err);
      if (isCancelation) {
        if (actionType === 'desvincular') {
          clearLocalPasskeyFlag(targetEmail);
          setError('Biometria local removida. Se a chave não existia mais no Windows, o card foi corrigido.');
          setActiveAvatarMenu(null);
        } else if (actionType === 'remover') {
          clearLocalPasskeyFlag(targetEmail);
          const acc = findSavedAccountByEmail(savedAccounts, targetEmail);
          if (acc) removeSavedAccount(acc.id);
          setError('Perfil removido deste computador.');
        } else {
          setError('Operação cancelada pelo usuário.');
        }
        return;
      }

      // Se falhar (seja porque a chave não existe no Hello ou outra falha),
      // a chave local está inconsistente ou inacessível. Nós desvinculamos localmente de imediato.
      console.log(`[VerifyPasskey] Limpando passkey local de ${targetEmail} devido a falha real.`);
      clearLocalPasskeyFlag(targetEmail);

      if (actionType === 'desvincular') {
        // Se a intenção era desvincular, a remoção local já cumpriu a tarefa principal.
        setError('Biometria local removida por inconsistência.');
        setActiveAvatarMenu(null);
      } else if (actionType === 'remover') {
        // Se a intenção era remover a conta, removemos de qualquer forma.
        const acc = findSavedAccountByEmail(savedAccounts, targetEmail);
        if (acc) {
          removeSavedAccount(acc.id);
        }
        setError('Perfil removido (biometria inválida limpa automaticamente).');
      } else {
        if (err.name === 'NotAllowedError') {
          setError('Operação cancelada: a validação biométrica é obrigatória.');
        } else {
          setError(err.message || 'Falha na autenticação biométrica.');
        }
      }
    } finally {
      setIsLoading(false);
    }
  };

  const [showEmailForm, setShowEmailForm] = useState(false);
  /** Mobile: se houver perfis salvos, mostra a lista por padrão. Caso contrário, formulário direto */
  const [mobileShowProfiles, setMobileShowProfiles] = useState(() => {
    try {
      return readSavedAccounts().length > 0;
    } catch { }
    return false;
  });
  const isMobile = useMemo(() => windowWidth < 768, [windowWidth]);
  const isStandaloneApp = useMemo(
    () => window.matchMedia('(display-mode: standalone)').matches || (window.navigator as any).standalone === true,
    [],
  );
  const [autoPromptAttempted, setAutoPromptAttempted] = useState(false);

  const prepareBrowserPasskeyStateBeforeMobileInstall = useCallback((targetEmail: string | null | undefined) => {
    if (!isMobile || isStandaloneApp || !targetEmail) return;
    if (!isPasskeyAvailableOnThisDevice(targetEmail)) return;

    clearLocalPasskeyFlag(targetEmail);
    try {
      sessionStorage.setItem('ac_passkey_marker_cleaned_before_pwa', 'true');
      sessionStorage.setItem('ac_passkey_marker_cleaned_email', targetEmail.trim().toLowerCase());
    } catch {
      // Sessao sem storage disponivel nao bloqueia o login.
    }
  }, [clearLocalPasskeyFlag, isMobile, isStandaloneApp]);

  useEffect(() => {
    if (!isMobile) return;

    const themeMeta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
    const colorSchemeMeta = document.querySelector<HTMLMetaElement>('meta[name="color-scheme"]');
    const previousThemeColor = themeMeta?.content;
    const previousColorScheme = colorSchemeMeta?.content;
    const elements = [
      document.documentElement,
      document.body,
      document.getElementById('root'),
    ].filter((element): element is HTMLElement => Boolean(element));
    const previousStyles = elements.map((element) => ({
      element,
      background: element.style.background,
      backgroundColor: element.style.backgroundColor,
      colorScheme: element.style.colorScheme,
      overflow: element.style.overflow,
    }));

    elements.forEach((element) => {
      element.style.background = MOBILE_LOGIN_GRADIENT;
      element.style.backgroundColor = '#19072d';
      element.style.colorScheme = 'dark';
      element.style.overflow = 'hidden';
    });
    if (themeMeta) themeMeta.content = '#19072d';
    if (colorSchemeMeta) colorSchemeMeta.content = 'dark';

    return () => {
      if (themeMeta && previousThemeColor !== undefined) themeMeta.content = previousThemeColor;
      if (colorSchemeMeta && previousColorScheme !== undefined) colorSchemeMeta.content = previousColorScheme;
      previousStyles.forEach(({ element, background, backgroundColor, colorScheme, overflow }) => {
        element.style.background = background;
        element.style.backgroundColor = backgroundColor;
        element.style.colorScheme = colorScheme;
        element.style.overflow = overflow;
      });
    };
  }, [isMobile]);

  const checkEmailInstant = async (targetEmail: string, disableAutoBiometrics = false): Promise<LoginEmailCheckStatus> => {
    const normalizedEmail = targetEmail.trim().toLowerCase();
    if (!isValidLoginEmail(normalizedEmail)) {
      setEmailStatus('idle');
      setEmailStatusEmail('');
      setServerHasPasskeys(false);
      setServerCanRequestMobileApproval(false);
      return 'idle';
    }
    setEmailStatus('checking');
    setEmailStatusEmail(normalizedEmail);
    setServerHasPasskeys(false);
    setServerCanRequestMobileApproval(false);
    const API = import.meta.env.VITE_API_URL || '/api/v1';
    try {
      const response = await fetch(`${API}/auth/check-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: normalizedEmail })
      });
      if (response.ok) {
        const data = await response.json();
        const emailState = normalizeLoginEmailCheckResponse(data);
        const nextStatus: LoginEmailCheckStatus = emailState.exists ? 'exists' : 'not_found';
        setEmailStatusEmail(normalizedEmail);
        setEmailStatus(nextStatus);
        setServerHasPasskeys(emailState.hasDesktopPasskeys);
        setServerCanRequestMobileApproval(emailState.canRequestMobileApproval);
        setCanSelfRegister(emailState.canSelfRegister === true);

        if (emailState.hasDesktopPasskeys && isPasskeyEnabled) {

          if (false && !disableAutoBiometrics) {
            const hasLocalKey = isPasskeyAvailableOnThisDevice(normalizedEmail);

            if (hasLocalKey) {
              // Se a chave de acesso está cadastrada localmente neste navegador, sempre tenta biometria local primeiro!
              console.log('[Auth] Chave local detectada neste dispositivo. Disparando biometria local...');
            } else {
              // Se NÃO tem chave local gravada neste dispositivo:
              const isPWA = window.matchMedia('(display-mode: standalone)').matches || (navigator as any).standalone === true;
              if (isMobile) {
                // No mobile: só dispara biometria local se estiver no PWA (o que exigiria chave local, mas como não tem, não faz nada automático)
                if (isPWA && isPasskeyAvailableOnThisDevice(normalizedEmail)) {
                }
              } else {
                // No desktop: NÃO dispara push automaticamente para evitar spam.
                // Apenas abre o formulário de email/senha para o usuário escolher o método que preferir.
                console.log('[Auth] Desktop detectado (sem chave local). Aguardando ação manual.');
              }
            }
          }
        }
        return nextStatus;
      } else {
        setEmailStatus('api_unavailable');
        setEmailStatusEmail(normalizedEmail);
        setServerHasPasskeys(false);
        setServerCanRequestMobileApproval(false);
        return 'api_unavailable';
      }
    } catch (error) {
      console.error('Erro ao verificar email instantaneamente:', error);
      setEmailStatus('api_unavailable');
      setEmailStatusEmail(normalizedEmail);
      setServerHasPasskeys(false);
      setServerCanRequestMobileApproval(false);
      return 'api_unavailable';
    }
  };

  const isMobileLoginPairingRequest = useCallback((forceMobile = isMobile) => {
    if (!forceMobile) return false;
    const params = new URLSearchParams(window.location.search);
    const redirectParam = normalizeLoginRedirect(params.get('redirect'));
    const directQrSource = params.get('source') === 'login_pairing' || params.get('from') === 'qr_pairing';
    let bondingRedirect = false;
    if (redirectParam) {
      try {
        const redirectUrl = new URL(redirectParam, window.location.origin);
        bondingRedirect =
          redirectUrl.pathname.startsWith('/security/bonding') &&
          redirectUrl.searchParams.get('intent') === 'login_pairing';
      } catch {
        bondingRedirect = false;
      }
    }
    return directQrSource || bondingRedirect;
  }, [isMobile]);

  const openEmailLogin = useCallback((prefillEmail?: string, jumpToPassword = false, disableAutoBiometrics = false, allowLoginPairingMobile = false) => {
    if (loginPairingMobileMode && !allowLoginPairingMobile) {
      setShowEmailForm(false);
      setShowAccountChooser(true);
      setMobileShowProfiles(true);
      return;
    }

    setHoveredAvatar(null);
    setCardMode('login');
    setError('');
    setSuccessMessage('');
    suppressEmailAutoAdvanceRef.current = false;
    if (prefillEmail) {
      setEmail(prefillEmail);
      if (jumpToPassword) {
        setEmailStep(2);
        setEmailStatus('checking');
        checkEmailInstant(prefillEmail, disableAutoBiometrics);
      } else {
        setEmailStep(1);
      }
    } else {
      setEmailStep(1);
      setEmailStatus('idle');
    }
    if (isMobile) {
      setMobileShowProfiles(false);
    }
    setShowEmailForm(true);
  }, [isMobile, loginPairingMobileMode]);

  const closeEmailLogin = useCallback(() => {
    setHoveredAvatar(null);
    setShowEmailForm(false);
  }, []);

  // ─── Splash Logic (abre direto fixo; replay opcional manual) ──────
  const [skipSplash, setSkipSplash] = useState<boolean>(() => {
    try {
      return sessionStorage.getItem('ac_skip_splash') === '1' || sessionStorage.getItem('ac_cockpit_embed') === '1' || true;
    } catch {
      return true;
    }
  });

  const handleSplashComplete = useCallback(() => {
    const today = new Date().toISOString().split('T')[0];
    localStorage.setItem('ac_last_splash_date', today);
    setSkipSplash(true);
  }, []);

  const replaySplash = useCallback(() => {
    localStorage.removeItem('ac_last_splash_date');
    setSkipSplash(false);
  }, []);



  /** Registrar dispositivo automaticamente no backend (chamado após PasskeySetupModal) */
  const autoRegisterDevice = async (somenteMeu: boolean) => {
    try {
      const tk = localStorage.getItem('ac_token') || '';
      if (!tk) return;
      const ua = navigator.userAgent;
      const navegador = /Chrome/.test(ua) ? 'Chrome' : /Firefox/.test(ua) ? 'Firefox' : /Safari/.test(ua) ? 'Safari' : 'Outro';
      const sistemaOp = /Windows/.test(ua) ? 'Windows' : /Mac/.test(ua) ? 'macOS' : /Linux/.test(ua) ? 'Linux' : 'Outro';
      const nome = somenteMeu
        ? (sistemaOp + ' — ' + new Date().toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }))
        : ('Público — ' + navegador);
      await registerApprovalDevice(
        {
          fingerprint: getDeviceFingerprint(),
          nome,
          tipoLocal: 'CASA',
          somenteMeu,
          navegador,
          sistemaOp,
        },
        { bearerToken: tk },
      );
    } catch (e) {
      console.warn('[autoRegisterDevice] Falha (não-bloqueante):', e);
    }
  };

  /** Colar convite → sempre ir para AtivarPage (senha / wizard), sem activate-by-token aqui. */
  const abrirLinkAtivacao = () => {
    const raw = window.prompt('Cole aqui o link de ativação completo:');
    if (!raw) return;
    const text = raw.trim();
    setError('');

    const irParaAtivar = (tokenVal: string, codeVal: string) => {
      if (!tokenVal) {
        setError('Não foi possível identificar o token no link colado.');
        return;
      }
      const next = new URLSearchParams();
      next.set('token', tokenVal);
      if (codeVal) next.set('code', codeVal);
      navigate(`/ativar?${next.toString()}`, { replace: true });
    };

    try {
      const url = text.includes('://') ? new URL(text) : new URL(text, window.location.origin);
      const token =
        url.searchParams.get('token') ||
        url.searchParams.get('Token') ||
        url.searchParams.get('t');
      const code = url.searchParams.get('code') || url.searchParams.get('c');
      const pathParts = url.pathname.split('/').filter(Boolean);
      const tokenFromPath =
        !token && pathParts[0] === 'ativar' && pathParts[1] ? decodeURIComponent(pathParts[1]) : null;
      const codeFromPath =
        !code && pathParts[0] === 'ativar' && pathParts[2] ? decodeURIComponent(pathParts[2]) : null;
      irParaAtivar(token || tokenFromPath || '', (code || codeFromPath || '').trim());
    } catch {
      const tokenMatch = text.match(/[?&#](?:token|Token|t)=([^&#\s]+)/i);
      const codeMatch = text.match(/[?&#](?:code|c)=([^&#\s]+)/i);
      if (!tokenMatch?.[1]) {
        setError('Não foi possível identificar token no link colado.');
        return;
      }
      irParaAtivar(decodeURIComponent(tokenMatch[1]), codeMatch?.[1] ? decodeURIComponent(codeMatch[1]) : '');
    }
  };

  // Fetch passkey configuration on mount
  useEffect(() => {
    getPublicPasskeyConfig()
      .then(data => {
        if (data && typeof data.ativo === 'boolean') {
          setIsPasskeyEnabled(data.ativo);
        }
      })
      .catch(() => { });
  }, []);

  // Fail-safe: se link de ativação cair em /login, redireciona para /ativar.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token') || params.get('Token') || params.get('t');
    const code = params.get('code') || params.get('c');
    if (token) {
      const next = new URLSearchParams();
      next.set('token', token);
      if (code) next.set('code', code);
      navigate(`/ativar?${next.toString()}`, { replace: true });
    }
  }, [navigate]);

  // Magic Link Validation and Self-Service Redirect
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const autoLoginToken = params.get('autoLoginToken');
    const magicToken = params.get('magicToken') || params.get('magictoken');
    const selfService = params.get('selfService') || params.get('action') === 'self-service';

    if (params.get('promptNotification') === '1') {
      try {
        sessionStorage.setItem('ac_force_push', 'true');
        localStorage.setItem('ac_force_push', 'true');
      } catch {
        localStorage.setItem('ac_force_push', 'true');
      }
    }

    if (autoLoginToken) {
      const performAutoLogin = async () => {
        setIsLoading(true);
        setError('');
        try {
          // Em iframe do cockpit: sessionStorage isolado (evita PROF/RESP no mesmo localStorage)
          const embedCockpit =
            params.get('from_cockpit') === '1' ||
            sessionStorage.getItem('ac_cockpit_embed') === '1' ||
            window.parent !== window;
          if (embedCockpit) {
            sessionStorage.setItem('ac_impersonate', 'true');
            sessionStorage.setItem('ac_cockpit_embed', '1');
            sessionStorage.setItem('ac_token', autoLoginToken);
          } else {
            localStorage.setItem('ac_token', autoLoginToken);
          }
          const { data } = await api.get('/auth/me');
          const userData = data.user || data;
          const normalizedUser = userData
            ? {
                ...userData,
                role: userData.perfil || userData.role,
                roles: [userData.perfil || userData.role],
              }
            : null;
          if (normalizedUser) {
            localStorage.setItem('ac_force_push', 'true');
            login(autoLoginToken, normalizedUser);
          }
        } catch {
          setError('Falha no login automático.');
        } finally {
          setIsLoading(false);
        }
      };
      performAutoLogin();
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (magicToken) {
      const simulationSessionId =
        params.get('simulationSessionId') || params.get('sessionId') || '';
      const validateToken = async () => {
        setIsLoading(true);
        setError('');
        try {
          // Pareamento PWA da simulação: claim emite JWT com simulationSessionId
          if (simulationSessionId) {
            const { data } = await api.post('/simulation/device-pair/claim', {
              token: magicToken,
              simulationSessionId,
            });
            const accessToken = data.access_token as string;
            const userData = data.user || {};
            const normalizedUser = {
              ...userData,
              role: userData.perfil || userData.role,
              roles: [userData.perfil || userData.role].filter(Boolean),
              isSimulation: true,
              simulationSessionId:
                userData.simulationSessionId || simulationSessionId,
            };
            try {
              sessionStorage.setItem('ac_sim_session', simulationSessionId);
              localStorage.setItem('ac_force_push', 'true');
              sessionStorage.setItem('ac_force_push', 'true');
            } catch {
              /* ignore */
            }
            localStorage.setItem('ac_token', accessToken);
            login(accessToken, normalizedUser as any);
            return;
          }

          const { data } = await api.post('/auth/magic-link/validate', { token: magicToken });
          if (data.requiresDeviceApproval) {
            localStorage.setItem('ac_token', data.tempToken);

            const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || window.innerWidth < 768;
            if (isMobile) {
              sessionStorage.setItem('ac_pending_security', JSON.stringify(['passkey']));
              navigate('/security-compliance', { replace: true });
              return;
            }

            sessionStorage.setItem('ac_pending_security', JSON.stringify(['device_approval']));
            sessionStorage.setItem('ac_device_approval_data', JSON.stringify({
              solicitacaoId: data.solicitacaoId,
              dezenaCorreta: data.dezenaCorreta || null,
              dezenasOpcoes: data.dezenasOpcoes || null,
              isDeviceRegistered: false,
              pendingUserData: null,
              pendingMagicToken: magicToken,
            }));
            navigate('/security-compliance', { replace: true });
            return;
          }
          if (data.success) {
            if (data.user && data.tempToken) {
              const accessToken = data.tempToken;
              const userData = data.user;
              const normalizedUser = {
                ...userData,
                role: userData.perfil || userData.role,
                roles: [userData.perfil || userData.role].filter(Boolean),
              };
              localStorage.setItem('ac_token', accessToken);
              login(accessToken, normalizedUser as any);
              return;
            }

            setEmail(data.email);
            setProvisionalToken(data.tempToken);
            sessionStorage.setItem('ac_provisional_token', data.tempToken);
            setIsProvisionalPasswordFlow(true);
            setCardMode('recover-reset');
            setShowEmailForm(true);
          }
        } catch (err: any) {
          const msg = err.response?.data?.message || err.response?.data?.error?.message || 'Link de redefinição inválido ou expirado.';
          setError(msg);
        } finally {
          setIsLoading(false);
        }
      };
      validateToken();
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (params.get('recovery') === '1' || params.get('code')) {
      const emailParam = params.get('email') || '';
      setEmail(emailParam);
      if (
        emailParam.includes('demo') ||
        emailParam.includes('.mock') ||
        emailParam.endsWith('@alunoconectado.com') ||
        emailParam.endsWith('@alunoconectado.com.br')
      ) {
        setPassword('Demo@2026');
      } else {
        setCardMode('recover-code');
        setShowEmailForm(true);
      }
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (params.get('email')) {
      const emailParam = params.get('email') || '';
      setEmail(emailParam);
      if (
        emailParam.includes('demo') ||
        emailParam.includes('.mock') ||
        emailParam.endsWith('@alunoconectado.com') ||
        emailParam.endsWith('@alunoconectado.com.br')
      ) {
        setPassword('Demo@2026');
      }
    }
  }, [navigate]);

  // Fail-safe extra: se o navegador cair em /login, mas já temos token salvo,
  // força redirecionamento hard para /ativar.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('activated') === '1' || params.get('email')) {
      sessionStorage.removeItem('ac_activation_token');
      sessionStorage.removeItem('ac_activation_code');
      return;
    }
    const token = sessionStorage.getItem('ac_activation_token');
    if (!token) return;
    const code = sessionStorage.getItem('ac_activation_code') || '';
    const next = new URLSearchParams();
    next.set('token', token);
    if (code) next.set('code', code);
    window.location.replace(`${window.location.origin}/ativar?${next.toString()}`);
  }, []);

  // Resize listener
  useEffect(() => {
    const onResize = () => {
      setWindowWidth(window.innerWidth);
      setWindowHeight(window.innerHeight);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    const updateVisualViewportHeight = () => {
      setVisualViewportHeight(Math.floor(window.visualViewport?.height || window.innerHeight));
    };

    updateVisualViewportHeight();
    window.addEventListener('resize', updateVisualViewportHeight);
    window.visualViewport?.addEventListener('resize', updateVisualViewportHeight);
    window.visualViewport?.addEventListener('scroll', updateVisualViewportHeight);

    return () => {
      window.removeEventListener('resize', updateVisualViewportHeight);
      window.visualViewport?.removeEventListener('resize', updateVisualViewportHeight);
      window.visualViewport?.removeEventListener('scroll', updateVisualViewportHeight);
    };
  }, []);

  useEffect(() => {
    if (!isMobile) setMobileShowProfiles(false);
  }, [isMobile]);

  useEffect(() => {
    if (emailStep === 1) {
      setBiometriaButtonHidden(false);
    }
  }, [emailStep]);

  useEffect(() => {
    if (isAuthenticated && user && !authLoading) {
      const emailNormalized = user.email?.trim().toLowerCase();
      if (emailNormalized && sessionStorage.getItem(`ac_passkey_cleanup_pending_${emailNormalized}`) === 'true') {
        sessionStorage.removeItem(`ac_passkey_cleanup_pending_${emailNormalized}`);
        console.log(`[Passkey Cleanup] Executando revogação no servidor para ${emailNormalized} pós-login.`);
        revokeCurrentDevicePasskeyWithExplicitConsent({
          explicitConsent: true,
          reason: 'auto-cleanup-failed-login-biometry',
        }).catch((err) => {
          console.error('[Passkey Cleanup] Falha ao revogar passkey no servidor:', err);
        });
      }

      const redirect = sessionStorage.getItem('ac_redirect_after_login');
      if (redirect) {
        sessionStorage.removeItem('ac_redirect_after_login');
        navigate(redirect, { replace: true });
      } else {
        const path = getRedirectPath(user.perfil || '', user.cargo || undefined);
        navigate(path, { replace: true });
      }
    }
  }, [isAuthenticated, user, authLoading, navigate]);

  // Sincroniza hasPasskey: baseado estritamente na presença da chave no localStorage deste dispositivo/navegador
  useEffect(() => {
    const syncPasskeys = () => {
      try {
        const parsed = readSavedAccounts();
        if (parsed.length === 0) return;

        const updated = parsed.map((acc) => {
          const localFlag = isPasskeyAvailableOnThisDevice(acc.email);
          return { ...acc, hasPasskey: localFlag };
        });

        const changed = updated.some((a, i) => a.hasPasskey !== !!parsed[i]?.hasPasskey);
        if (changed) {
          writeSavedAccounts(updated);
          setSavedAccounts(normalizeSavedAccountsForLogin(updated));
        }
      } catch (e: any) {
        console.error(`Erro ao sincronizar passkeys: ${e?.message || e}`);
      }
    };
    syncPasskeys();
  }, []);

  // Reset do último password tentado quando muda o e-mail, etapa ou modo
  useEffect(() => {
    lastAttemptedPasswordRef.current = '';
    timerExecutedCountRef.current = 0;
    hasTypedRef.current = false;
    attemptedPasswordsRef.current.clear();
  }, [email, emailStep, cardMode]);

  const requestEmailAdvanceAfterCheck = useCallback((targetEmail: string) => {
    const normalizedEmail = targetEmail.trim().toLowerCase();
    if (!isValidLoginEmail(normalizedEmail)) {
      pendingEmailAdvanceRef.current = null;
      setError('Informe um e-mail valido para continuar.');
      return;
    }

    suppressEmailAutoAdvanceRef.current = false;

    if (emailStatus === 'exists' && emailStatusEmail === normalizedEmail) {
      pendingEmailAdvanceRef.current = null;
      goToPasswordStep();
      setError('');
      return;
    }

    if (emailStatus === 'checking' && emailStatusEmail === normalizedEmail) {
      pendingEmailAdvanceRef.current = normalizedEmail;
      setError('');
      return;
    }

    pendingEmailAdvanceRef.current = null;
  }, [emailStatus, emailStatusEmail, goToPasswordStep]);

  const { focusLoginInputForKeyboard, resetMobilePasswordStepHistory } = useMobileLoginFocus({
    isMobile,
    showEmailForm,
    cardMode,
    emailStep,
    setPassword,
    setError,
    setEmailStep,
    setEmailStatus,
  });

  // Cartao de erro recolhido para a tela inicial, login e recuperacao.
  useEffect(() => {
    const shouldShowLoginErrorCard = loginErrorDetailsOpen && Boolean(error);

    if (!shouldShowLoginErrorCard) {
      setLoginErrorCountdown(null);
      return;
    }

    setLoginErrorCountdown(LOGIN_ERROR_CARD_SECONDS);
    const targetStep = emailStep;
    const activeElement = document.activeElement;
    if (activeElement === emailInputRef.current || activeElement === passwordInputRef.current) {
      (activeElement as HTMLElement).blur();
    }
    const countdownTimer = window.setInterval(() => {
      setLoginErrorCountdown((current) => {
        if (current === null) return null;
        if (current <= 1) {
          window.clearInterval(countdownTimer);
          window.setTimeout(() => {
            setError('');
            const input =
              cardMode === 'login'
                ? (targetStep === 1 ? emailInputRef.current : passwordInputRef.current)
                : cardMode === 'recover-code'
                  ? recoverCodeInputRef.current
                  : cardMode === 'recover-reset'
                    ? recoverNewPasswordInputRef.current
                    : emailInputRef.current;
            if (input) {
              focusLoginInputForKeyboard(input, { requestVirtualKeyboard: isMobile });
            }
          }, 0);
          return null;
        }
        return current - 1;
      });
    }, 1000);

    return () => window.clearInterval(countdownTimer);
  }, [error, loginErrorDetailsOpen, cardMode, emailStep, isMobile, focusLoginInputForKeyboard]);

  useEffect(() => {
    setLoginErrorDetailsOpen(false);
  }, [error]);

  useEffect(() => {
    const isAttempted = attemptedPasswordsRef.current.has(password) || password === lastAttemptedPasswordRef.current;
    const shouldTrigger =
      cardMode === 'login' &&
      emailStep === 2 &&
      password.trim().length > 0 &&
      !isAttempted &&
      !isWaitingPushApproval &&
      !isLoading &&
      (timerExecutedCountRef.current === 0 || hasTypedRef.current);

    if (shouldTrigger) {
      if (autoLoginTimerRef.current) clearInterval(autoLoginTimerRef.current);

      setAutoLoginCountdown(3);
      let count = 3;

      // Reset typing flag since the timer is now active
      hasTypedRef.current = false;

      autoLoginTimerRef.current = setInterval(() => {
        count--;
        if (count <= 0) {
          clearInterval(autoLoginTimerRef.current);
          autoLoginTimerRef.current = null;
          setAutoLoginCountdown(null);
          // Increment execution count on auto-submit
          timerExecutedCountRef.current += 1;
          // Dispara a submissão do formulário de login
          const form = document.querySelector('form');
          if (form) {
            form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
          }
        } else {
          setAutoLoginCountdown(count);
        }
      }, 1000);
    } else {
      if (autoLoginTimerRef.current) {
        clearInterval(autoLoginTimerRef.current);
        autoLoginTimerRef.current = null;
      }
      setAutoLoginCountdown(null);
    }

    return () => {
      if (autoLoginTimerRef.current) clearInterval(autoLoginTimerRef.current);
    };
  }, [password, cardMode, emailStep, isWaitingPushApproval, isLoading]);

  // SMART LOGIN PROTOYPE: Auto-Discovery (Debounce)
  // ═══════════════════════════════════════════════════
  useEffect(() => {
    if (cardMode !== 'login' || !showEmailForm) return;

    const normalizedEmail = email.trim().toLowerCase();
    if (!isValidLoginEmail(normalizedEmail)) {
      setEmailStatus('idle');
      setEmailStatusEmail('');
      setServerHasPasskeys(false);
      setServerCanRequestMobileApproval(false);
      return;
    }

    setEmailStatus('checking');
    setEmailStatusEmail(normalizedEmail);
    setServerHasPasskeys(false);
    setServerCanRequestMobileApproval(false);
    const API = import.meta.env.VITE_API_URL || '/api/v1';

    const timer = setTimeout(async () => {
      try {
        const response = await fetch(`${API}/auth/check-email`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: normalizedEmail })
        });
        if (response.ok) {
          const data = await response.json();
          const emailState = normalizeLoginEmailCheckResponse(data);
          setEmailStatusEmail(normalizedEmail);
          setEmailStatus(emailState.exists ? 'exists' : 'not_found');
          setServerHasPasskeys(emailState.hasDesktopPasskeys);
          setServerCanRequestMobileApproval(emailState.canRequestMobileApproval);
          setCanSelfRegister(emailState.canSelfRegister === true);
          if (emailState.exists) {
            pendingEmailAdvanceRef.current = null;
            setError('');
            if (emailState.isPendingActivation) {
              if (emailState.tokenAtivacao) {
                navigate(`/ativar?token=${emailState.tokenAtivacao}`);
                return;
              } else {
                setError('Sua conta foi localizada no sistema! Ela está aguardando a criação da sua senha. Utilize o link enviado pela escola ou via QR Code para criar sua senha.');
                return;
              }
            }
          } else if (pendingEmailAdvanceRef.current === normalizedEmail) {
            pendingEmailAdvanceRef.current = null;
            setError('E-mail não localizado. Verifique se o endereço foi cadastrado corretamente na secretaria da escola.');
          } else {
            setError('');
          }
        } else {
          // 502/503/404 da API: nao confundir com "email nao cadastrado"
          setEmailStatus('api_unavailable');
          setEmailStatusEmail(normalizedEmail);
          setServerHasPasskeys(false);
          setServerCanRequestMobileApproval(false);
          if (pendingEmailAdvanceRef.current === normalizedEmail) {
            pendingEmailAdvanceRef.current = null;
            setError(EMAIL_VALIDATION_UNAVAILABLE_MESSAGE);
          } else {
            setError('');
          }
        }
      } catch (error) {
        console.error('Erro ao verificar email:', error);
        setEmailStatus('api_unavailable');
        setEmailStatusEmail(normalizedEmail);
        setServerHasPasskeys(false);
        setServerCanRequestMobileApproval(false);
        if (pendingEmailAdvanceRef.current === normalizedEmail) {
          pendingEmailAdvanceRef.current = null;
          setError(EMAIL_VALIDATION_UNAVAILABLE_MESSAGE);
        } else {
          setError('');
        }
      }
    }, 800);

    return () => clearTimeout(timer);
  }, [email, cardMode, showEmailForm]);

  useEffect(() => {
    const normalizedEmail = email.trim().toLowerCase();
    if (cardMode !== 'login' || !showEmailForm || emailStep !== 1) {
      return;
    }

    if (
      emailStatus === 'exists' &&
      emailStatusEmail === normalizedEmail &&
      isValidLoginEmail(normalizedEmail) &&
      !suppressEmailAutoAdvanceRef.current
    ) {
      pendingEmailAdvanceRef.current = null;
      goToPasswordStep();
      setError('');
      return;
    }

    if (emailStatusEmail === normalizedEmail && pendingEmailAdvanceRef.current === normalizedEmail) {
      if (emailStatus === 'not_found') {
        pendingEmailAdvanceRef.current = null;
        if (canSelfRegister) {
          handleStartSelfRegistration(normalizedEmail);
        } else {
          setError('E-mail não localizado. Verifique se o endereço foi cadastrado corretamente na secretaria da escola.');
        }
        return;
      }
      if (emailStatus === 'api_unavailable') {
        pendingEmailAdvanceRef.current = null;
        setError(EMAIL_VALIDATION_UNAVAILABLE_MESSAGE);
        return;
      }
    }

    if (emailStatus === 'idle') {
      pendingEmailAdvanceRef.current = null;
    }
  }, [emailStatus, emailStatusEmail, email, cardMode, showEmailForm, emailStep, goToPasswordStep]);

  //#region AC_BLOCK login.pwa-install-gate -- Gate de instalacao PWA
  // @ac-purpose Detecta PWA instalado no mobile e direciona para abertura standalone sem mexer no fluxo de senha.
  // @ac-related docs_secutiry/execucao-lote-45-biometria-mobile-login-ux.md#Execucao-realizada
  // @ac-related react-app/src/pages/public/InstallPage.tsx
  // @ac-routes /login
  // @ac-tests node tools/codex-kit.mjs check react-tsc
  // Detecta se o PWA está instalado no celular e redireciona automaticamente para abrir no app standalone
  useEffect(() => {
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || window.innerWidth < 768;
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || (window.navigator as any).standalone;
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.userAgent.includes('Mac') && 'ontouchend' in document);

    if (isMobile && !isStandalone && !isIOS) {
      const forceBrowser = sessionStorage.getItem('ac_force_browser') === 'true' ||
        new URLSearchParams(window.location.search).get('browser') === '1';

      if (!forceBrowser) {
        const localInstalled = localStorage.getItem('ac_pwa_installed') === 'true';

        const openApp = () => {
          console.log('[PWA] PWA detectado como instalado no celular. Redirecionando para abrir no standalone...');
          try {
            window.location.href = `web+alunoconectado://${window.location.host}${window.location.pathname}${window.location.search}`;
          } catch {
            // Ignora falhas de esquema personalizado
          }
        };

        if (localInstalled) {
          openApp();
          return;
        }

        if ('getInstalledRelatedApps' in navigator) {
          (navigator as any).getInstalledRelatedApps()
            .then((apps: any[]) => {
              if (apps && apps.length > 0) {
                openApp();
              }
            })
            .catch((e: any) => console.warn('[PWA] Erro ao verificar apps relacionados:', e));
        }
      }
    }
  }, []);
  //#endregion AC_BLOCK login.pwa-install-gate

  // Mantém /login estático: sem redirecionamento automático ao carregar.

  // O tratamento do '?impersonate=' é centralizado no AuthProvider.
  // Apenas mantemos o estado inicial para exibir o spinner correto caso a URL tenha impersonate.

  // Redirecionamento automático caso seja autenticado em background (ex: impersonate via AuthProvider)
  useEffect(() => {
    if (isAuthenticated && user && !authLoading) {
      const redirect = sessionStorage.getItem('ac_redirect_after_login');
      if (redirect) {
        sessionStorage.removeItem('ac_redirect_after_login');
        navigate(redirect, { replace: true });
      } else {
        const dashRoute = getRedirectPath(user.perfil || '', user.cargo || undefined);
        navigate(dashRoute, { replace: true });
      }
    }
  }, [isAuthenticated, user, authLoading, navigate]);

  // Handler para retorno do signInWithRedirect (fallback do Google Login quando popup falha no PWA)
  useEffect(() => {
    const handleRedirectResult = async () => {
      if (!sessionStorage.getItem('ac_google_login_pending')) return;

      setIsLoading(true);
      try {
        const { initializeApp, getApps } = await import('firebase/app');
        const firebaseAuth = await import('firebase/auth');
        const firebaseConfig = {
          apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
          authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
          projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
          storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
          messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
          appId: import.meta.env.VITE_FIREBASE_APP_ID,
        };
        const app = getApps().length ? getApps()[0] : initializeApp(firebaseConfig);
        const auth = firebaseAuth.getAuth(app);

        const result = await firebaseAuth.getRedirectResult(auth);

        if (result && result.user) {
          sessionStorage.removeItem('ac_google_login_pending');
          const firebaseToken = await result.user.getIdToken();
          const { data } = await api.post(ROUTES.auth.loginGoogle, { firebaseToken });

          if (data.requiresSetup && data.wizardRoute && !data.user?.acessoDireto) {
            const userSrc = data.user;
            if (userSrc) login(data.access_token, { ...userSrc, role: userSrc.perfil, roles: [userSrc.perfil] });
            localStorage.removeItem('sw_wizard_etapa');
            sessionStorage.removeItem('sw_intro_seen');
            sessionStorage.removeItem('bonding_session_id');
            localStorage.removeItem('bonding_session_id');
            navigate(data.wizardRoute, { replace: true });
            return;
          }

          const userData = { ...data.user, role: data.user.perfil, roles: [data.user.perfil] };
          if (data.refresh_token) localStorage.setItem('ac_refresh_token', data.refresh_token);

          prepareBrowserPasskeyStateBeforeMobileInstall(userData.email);
          saveAccountToLocal(userData, 'google');
          login(data.access_token, userData);

          // ── Security Compliance Check ─────────────────────────────────
          const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || window.innerWidth < 768;
          let pendingSteps = data.pendingSecuritySteps || [];
          if (isMobile && !pendingSteps.includes('passkey')) {
            pendingSteps = ['passkey', ...pendingSteps];
          }

          if (pendingSteps.length > 0) {
            sessionStorage.setItem('ac_pending_security', JSON.stringify(pendingSteps));
            navigate('/security-compliance', { replace: true });
            return;
          }

          const redirectPath = sessionStorage.getItem('ac_redirect_after_login');
          const dashRoute = redirectPath || getRedirectPath(userData.perfil || '', userData.cargo || undefined);
          sessionStorage.removeItem('ac_redirect_after_login');
          navigate(dashRoute, { replace: true });
        } else {
          // Apenas remove a flag se não houver erro, mas tbm não houver usuário
          sessionStorage.removeItem('ac_google_login_pending');
          setIsLoading(false);
        }
      } catch (err: any) {
        sessionStorage.removeItem('ac_google_login_pending');
        setError(err.message || 'Falha ao concluir login pelo Google (Redirect).');
        setIsLoading(false);
      }
    };
    handleRedirectResult();
  }, [navigate, login]);

  // Load saved email
  useEffect(() => {
    const savedEmail = localStorage.getItem('ac_remember_email');
    if (savedEmail) setEmail(savedEmail);
  }, []);

  // Suporte a ?email=xxx&activated=1 (vindo da ativação de conta) ou motivos de logout
  useEffect(() => {
    if (loginBootstrapHandledRef.current) return;
    loginBootstrapHandledRef.current = true;

    const params = new URLSearchParams(window.location.search);
    const redirectParam = normalizeLoginRedirect(params.get('redirect'));
    const emailParamRaw = params.get('email') || extractEmailFromLoginRedirect(redirectParam, window.location.origin);
    const emailParam = emailParamRaw?.trim().toLowerCase() || '';
    const activated = params.get('activated');
    const recovery = params.get('recovery');
    const recoveryCodeParam = params.get('code');
    const loginPairingMobileEntry = isMobileLoginPairingRequest();

    setLoginPairingMobileMode(loginPairingMobileEntry);

    const expired = params.get('expired');
    const replaced = params.get('replaced');
    const storedReason = sessionStorage.getItem('ac_logout_reason') || localStorage.getItem('ac_logout_reason');

    if (replaced === '1' || replaced === 'true' || storedReason === 'replaced') {
      try {
        const detailsStr = sessionStorage.getItem('ac_logout_details') || localStorage.getItem('ac_logout_details');
        if (detailsStr) {
          setReplacedDetails(JSON.parse(detailsStr));
        }
        sessionStorage.removeItem('ac_logout_reason');
        localStorage.removeItem('ac_logout_reason');
        sessionStorage.removeItem('ac_logout_details');
        localStorage.removeItem('ac_logout_details');
      } catch { /* ignore */ }
      setShowReplacedModal(true);
    } else if (expired === '1' || expired === 'true' || storedReason === 'expired') {
      setError('Sessão expirada ou inválida. Por favor, faça login novamente.');
      try {
        sessionStorage.removeItem('ac_logout_reason');
        localStorage.removeItem('ac_logout_reason');
      } catch { /* ignore */ }
    }

    if (redirectParam) {
      sessionStorage.setItem('ac_redirect_after_login', redirectParam);
    }

    if (emailParam) {
      const localAccount = findSavedAccountByEmail(savedAccounts, emailParam);
      const flowEmail = localAccount?.email || emailParam;
      setEmail(flowEmail);
      if (loginPairingMobileEntry) {
        setShowEmailForm(false);
        setShowAccountChooser(true);
        setMobileShowProfiles(true);
        return;
      }
      if (isMobile || activated === '1' || redirectParam) {
        openEmailLogin(flowEmail, Boolean(localAccount) || activated === '1', Boolean(redirectParam));
        setShowAccountChooser(false);
      }
    } else if (loginPairingMobileEntry) {
      setCardMode('login');
      setEmailStep(1);
      setEmailStatus('idle');
      setShowEmailForm(false);
      setShowAccountChooser(true);
      setMobileShowProfiles(true);
    } else if (isMobile && savedAccounts.length === 0) {
      setCardMode('login');
      setEmailStep(1);
      setEmailStatus('idle');
      setShowAccountChooser(false);
      openEmailLogin();
    }

    if (recovery === '1') {
      if (emailParam) setEmail(emailParam);
      if (recoveryCodeParam) {
        setRecoverCode(recoveryCodeParam.replace(/\D/g, '').slice(0, 6));
        setCardMode('recover-code');
      } else {
        setCardMode('recover-code');
      }
    }
  }, [isMobile, isMobileLoginPairingRequest, openEmailLogin, savedAccounts]);

  // Triple-click handler para abrir demo dialog
  const handleLogoClick = () => {
    const next = demoClickCount + 1;
    if (next >= 3) {
      setShowDemoDialog(true);
      setDemoClickCount(0);
    } else {
      setDemoClickCount(next);
      setTimeout(() => setDemoClickCount(0), 1500);
    }
  };

  const setDemo = (e: string, p: string, redirect?: string) => {
    setEmail(e);
    setPassword(p);
    setOverrideRedirect(redirect || '');
    setShowDemoDialog(false);
  };

  const dotSt = (color: string): React.CSSProperties => ({
    display: 'inline-block', width: 8, height: 8, borderRadius: '50%',
    background: color, marginRight: 6, flexShrink: 0,
  });

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    timerExecutedCountRef.current += 1; // Mark that login has been attempted
    stopPushPolling();
    const submittedEmail = email.trim();
    const submittedPassword = password;
    if (!submittedEmail) {
      emailInputRef.current?.reportValidity();
      setError('');
      return;
    }
    if (!submittedPassword.trim()) {
      passwordInputRef.current?.setCustomValidity('Digite sua senha.');
      passwordInputRef.current?.reportValidity();
      focusLoginInputForKeyboard(passwordInputRef.current, { requestVirtualKeyboard: isMobile });
      setError('');
      return;
    }
    passwordInputRef.current?.setCustomValidity('');

    lastAttemptedPasswordRef.current = submittedPassword;
    attemptedPasswordsRef.current.add(submittedPassword);
    setPassword('');

    setIsLoading(true);
    setError('');

    try {
      let recaptchaToken: string | undefined = undefined;
      try {
        if (typeof (window as any).grecaptcha?.enterprise?.ready === 'function') {
          recaptchaToken = await new Promise<string>((resolve, reject) => {
            (window as any).grecaptcha.enterprise.ready(async () => {
              try {
                const token = await (window as any).grecaptcha.enterprise.execute(
                  '6LccCS8tAAAAAPjkU_mjR8IqPclv5PYTMSIQ3Xcy',
                  { action: 'LOGIN' }
                );
                resolve(token);
              } catch (err) {
                reject(err);
              }
            });
          });
        }
      } catch (recaptchaErr) {
        console.warn('[reCAPTCHA] Erro ao obter token do Google, prosseguindo para o servidor tentar validar/resilir:', recaptchaErr);
      }

      const { data } = await api.post(
        ROUTES.auth.login,
        { email: submittedEmail, senha: submittedPassword, recaptchaToken },
        {
          headers: {
            'x-device-fingerprint': getDeviceFingerprint(),
          },
        }
      );

      // Intercept provisional password login
      if (data.requiresPasswordChange) {
        setProvisionalToken(data.tempToken);
        sessionStorage.setItem('ac_provisional_token', data.tempToken);
        setIsProvisionalPasswordFlow(true);
        setCardMode('recover-reset');
        setShowEmailForm(true);
        setIsLoading(false);
        return;
      }

      // ── Interceptar setup incompleto (ATIVANDO) ───────────────────────────
      if (data.requiresSetup && data.wizardRoute && !data.user?.acessoDireto) {
        const userSrc = data.user || data.usuario;
        if (userSrc) {
          login(data.access_token, {
            ...userSrc,
            role: userSrc.perfil,
            roles: [userSrc.perfil],
            acessoDireto: userSrc.acessoDireto ?? false,
          });
        }
        if (data.refresh_token) {
          localStorage.setItem('ac_refresh_token', data.refresh_token);
        }
        localStorage.removeItem('sw_wizard_etapa');
        sessionStorage.removeItem('sw_intro_seen');
        sessionStorage.removeItem('bonding_session_id');
        localStorage.removeItem('bonding_session_id');
        // Conta com senha já definida: concluir ativação aqui (evita loop dashboard ↔ wizard)
        try {
          await api.post(ROUTES.auth.completeSetup);
          await useAuthStore.getState().checkAuth();
          const u = useAuthStore.getState().user;
          const perfil = u?.perfil || userSrc?.perfil || 'ESCOLA';
          const cargo = u?.cargo || userSrc?.cargo;
          if (userSrc) {
            prepareBrowserPasskeyStateBeforeMobileInstall(userSrc.email);
            saveAccountToLocal({ ...userSrc, perfil }, 'email');
          }
          navigate(getRedirectPath(perfil, cargo), { replace: true });
          return;
        } catch {
          // Sem senha ainda → wizard pede definição de senha (sem auto-finalizar em loop)
          navigate(data.wizardRoute, { replace: true });
        }
        return;
      }

      // ── Interceptar necessidade de aprovação de dispositivo ──────────────
      if (data.requiresDeviceApproval) {
        const pendingUserData = data.user
          ? {
            ...data.user,
            role: data.user.perfil,
            roles: [data.user.perfil],
            acessoDireto: data.user.acessoDireto ?? false,
            statusConta: data.user.statusConta,
          }
          : null;

        if (data.user) {
          prepareBrowserPasskeyStateBeforeMobileInstall(pendingUserData.email);
          saveLocalAccountShortcut(pendingUserData, 'pending_security');
        }
        const token = data.access_token || data.tempToken;
        if (token) {
          localStorage.setItem('ac_token', token);
          if (pendingUserData) {
            login(token, pendingUserData);
          }
        } else {
          localStorage.removeItem('ac_token');
          sessionStorage.removeItem('ac_token');
        }
        const rToken = data.refresh_token || data.tempRefreshToken;
        if (rToken) {
          localStorage.setItem('ac_refresh_token', rToken);
        } else {
          localStorage.removeItem('ac_refresh_token');
          sessionStorage.removeItem('ac_refresh_token');
        }

        const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || window.innerWidth < 768;
        if (isMobile) {
          const mobilePendingSteps = data.access_token ? ['passkey'] : ['device_approval'];
          sessionStorage.setItem('ac_pending_security', JSON.stringify(mobilePendingSteps));
          sessionStorage.setItem('ac_device_approval_data', JSON.stringify({
            solicitacaoId: data.solicitacaoId || null,
            dezenaCorreta: data.dezenaCorreta || null,
            dezenasOpcoes: data.dezenasOpcoes || null,
            isDeviceRegistered: !!data.isDeviceRegistered,
            pendingUserData,
          }));
          navigate('/security-compliance', { replace: true });
          return;
        }

        sessionStorage.setItem('ac_pending_security', JSON.stringify(['device_approval']));
        sessionStorage.setItem('ac_device_approval_data', JSON.stringify({
          solicitacaoId: data.solicitacaoId,
          dezenaCorreta: data.dezenaCorreta || null,
          dezenasOpcoes: data.dezenasOpcoes || null,
          isDeviceRegistered: !!data.isDeviceRegistered,
          pendingUserData,
        }));
        navigate('/security-compliance', { replace: true });
        return;
      }

      const userData = {
        ...data.user,
        role: data.user.perfil,
        roles: [data.user.perfil],
        acessoDireto: data.user.acessoDireto ?? false,
        statusConta: data.user.statusConta,
      };
      prepareBrowserPasskeyStateBeforeMobileInstall(userData.email);
      saveAccountToLocal(userData, 'email');
      login(data.access_token, userData);
      sessionStorage.setItem('ac_auth_method', 'password');

      if (data.refresh_token) {
        localStorage.setItem('ac_refresh_token', data.refresh_token);
      }

      if (rememberMe) {
        localStorage.setItem('ac_remember_email', submittedEmail);
      } else {
        localStorage.removeItem('ac_remember_email');
      }

      // ── Security Compliance Check ─────────────────────────────────────
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || window.innerWidth < 768;
      let pendingSteps = data.pendingSecuritySteps || [];
      if (isMobile && !pendingSteps.includes('passkey')) {
        pendingSteps = ['passkey', ...pendingSteps];
      }

      if (pendingSteps.length > 0) {
        saveLocalAccountShortcut(userData, 'pending_security');
        sessionStorage.setItem('ac_pending_security', JSON.stringify(pendingSteps));
        navigate('/security-compliance', { replace: true });
        return;
      }

      await useAuthStore.getState().checkAuth();

      const redirectPath = sessionStorage.getItem('ac_redirect_after_login');
      const dashRoute = redirectPath || getRedirectPath(userData.perfil || '', userData.cargo || undefined);
      sessionStorage.removeItem('ac_redirect_after_login');
      navigate(dashRoute, { replace: true });

    } catch (err: any) {
      const msg = err.response?.data?.error?.message || err.response?.data?.message || '';
      if (msg) {
        setError(msg);
      } else {
        setError('Login falhou. Verifique suas credenciais.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  /** Tenta verificar ou cadastrar passkey inline, depois navega para o dashboard */
  const tryPasskeyInline = async (jwt: string, dashRoute: string, accountEmail?: string) => {
    const emailForDevice = (accountEmail || user?.email || email).trim().toLowerCase();
    const requestOptions = { bearerToken: jwt };
    try {
      const statusData = await getPasskeyStatus(requestOptions).catch(() => ({ temPasskey: false }));

      if (statusData.temPasskey) {
        // Já tem passkeys no servidor. Vamos tentar autenticar este dispositivo específico (biometria rápida).
        try {
          const optData = await requestPasskeyAuthOptions(requestOptions);
          const authOptions = unwrapPasskeyOptions(optData);
          const assertion = await startAuthentication({ optionsJSON: authOptions as any });
          const verifyResult = await verifyPasskeyAuthentication(assertion, requestOptions);
          if (verifyResult.ok === false) throw new Error('Falha na verificação biométrica.');
          sessionStorage.setItem('ac_accesskey_verified', 'true');
          markPasskeyOnThisDevice(emailForDevice);
          navigate(dashRoute, { replace: true });
          return;
        } catch {
          // Falhou ou cancelou biometria neste dispositivo -> apenas segue para o dashboard
          // Não forçamos cadastro aqui se já existem outras chaves (pode ser o dispositivo de outro usuário)
        }
      } else {
        // Usuário não tem passkey ALGUMA. Vamos cadastrar este dispositivo agora.
        try {
          const regData = await requestPasskeyRegistrationOptions(undefined, requestOptions);
          const opts = unwrapPasskeyOptions(regData);
          const attestation = await startRegistration({ optionsJSON: opts as any });
          const verifyResult = await verifyPasskeyRegistration(attestation, undefined, requestOptions);
          if (verifyResult.ok === false) throw new Error('Falha ao verificar assinatura biométrica.');
          sessionStorage.setItem('ac_accesskey_verified', 'true');
          markPasskeyOnThisDevice(emailForDevice);
        } catch {
          // Cadastro cancelado ou negado -> apenas segue
        }
      }

      navigate(dashRoute, { replace: true });
    } catch {
      navigate(dashRoute, { replace: true });
    }
  };

  const handleGoogleLogin = async () => {
    setIsLoading(true);
    setError('');
    try {
      const { initializeApp, getApps } = await import('firebase/app');
      const firebaseAuth = await import('firebase/auth');
      const firebaseConfig = {
        apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
        authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
        projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
        storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
        messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
        appId: import.meta.env.VITE_FIREBASE_APP_ID,
      };
      const app = getApps().length ? getApps()[0] : initializeApp(firebaseConfig);
      const auth = firebaseAuth.getAuth(app);
      const provider = new firebaseAuth.GoogleAuthProvider();
      provider.setCustomParameters({ prompt: 'select_account' });
      let result;
      try {
        result = await firebaseAuth.signInWithPopup(auth, provider);
      } catch (popupErr: any) {
        if (popupErr.code === 'auth/popup-blocked' || popupErr.code === 'auth/popup-closed-by-user') {
          sessionStorage.setItem('ac_google_login_pending', 'true');
          await firebaseAuth.signInWithRedirect(auth, provider);
          return;
        }
        throw popupErr;
      }
      const firebaseToken = await result.user.getIdToken();
      const { data } = await api.post(ROUTES.auth.loginGoogle, { firebaseToken });
      if (data.requiresSetup && data.wizardRoute && !data.user?.acessoDireto) {
        const userSrc = data.user;
        if (userSrc) login(data.access_token, { ...userSrc, role: userSrc.perfil, roles: [userSrc.perfil] });
        localStorage.removeItem('sw_wizard_etapa');
        sessionStorage.removeItem('sw_intro_seen');
        sessionStorage.removeItem('bonding_session_id');
        localStorage.removeItem('bonding_session_id');
        navigate(data.wizardRoute, { replace: true });
        return;
      }
      const userData = { ...data.user, role: data.user.perfil, roles: [data.user.perfil] };
      if (data.refresh_token) localStorage.setItem('ac_refresh_token', data.refresh_token);
      // Login Google OK
      prepareBrowserPasskeyStateBeforeMobileInstall(userData.email);
      saveAccountToLocal(userData, 'google');
      login(data.access_token, userData);

      // ── Security Compliance Check ─────────────────────────────────────
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || window.innerWidth < 768;
      let pendingSteps = data.pendingSecuritySteps || [];
      if (isMobile && !pendingSteps.includes('passkey')) {
        pendingSteps = ['passkey', ...pendingSteps];
      }

      if (pendingSteps.length > 0) {
        saveAccountToLocal(
          {
            ...userData,
            localStatus: 'pending_security',
            statusLabel: 'PENDENTE',
          },
          'google',
        );
        sessionStorage.setItem('ac_pending_security', JSON.stringify(pendingSteps));
        navigate('/security-compliance', { replace: true });
        return;
      }

      const redirectPath = sessionStorage.getItem('ac_redirect_after_login');
      const dashRoute = redirectPath || getRedirectPath(userData.perfil || '', userData.cargo || undefined);
      sessionStorage.removeItem('ac_redirect_after_login');
      navigate(dashRoute, { replace: true });
      return;
    } catch (err: any) {
      const msg =
        err?.response?.data?.error?.message ||
        err?.response?.data?.message ||
        'Falha no login com Google.';
      setError(msg);
    } finally {
      setIsLoading(false);
    }
  };

  //#region AC_BLOCK login.push-auth -- Push auth complementar
  // @ac-purpose Solicita e acompanha aprovacao remota por celular sem substituir a senha como fallback seguro.
  // @ac-related docs_secutiry/matriz-rotas-repetidas-reducao.md#4-push-e-fcm
  // @ac-related docs_secutiry/rotas-canonicas-seguranca.md#5-push-e-fcm
  // @ac-routes POST /auth/login-push-request; POST /auth/login-push-verify
  // @ac-tests node tools/codex-kit.mjs check react-tsc
  const stopPushPolling = useCallback(() => {
    setIsWaitingPushApproval(false);
    if (pushPollIntervalRef.current) {
      clearInterval(pushPollIntervalRef.current);
      pushPollIntervalRef.current = null;
    }
    if (pushCountdownIntervalRef.current) {
      clearInterval(pushCountdownIntervalRef.current);
      pushCountdownIntervalRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => {
      if (pushPollIntervalRef.current) {
        clearInterval(pushPollIntervalRef.current);
        pushPollIntervalRef.current = null;
      }
      if (pushCountdownIntervalRef.current) {
        clearInterval(pushCountdownIntervalRef.current);
        pushCountdownIntervalRef.current = null;
      }
    };
  }, []);

  const startPushLogin = async (targetEmail: string) => {
    setIsLoading(true);
    setError('');
    setIsWaitingPushApproval(true);
    setPushStatus('PENDENTE');
    setPushCountdown(60);

    const API = import.meta.env.VITE_API_URL || '/api/v1';
    try {
      const fingerprint = await getDeviceFingerprint();
      const res = await fetch(`${API}/auth/login-push-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: targetEmail, fingerprint })
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        const status = res.status;
        const msg = errData?.message || errData?.error?.message || 'Erro ao enviar notificação push.';

        if (status === 400 || msg.includes('biometria móvel') || msg.includes('não possui biometria')) {
          console.warn('[PushLogin] Usuário não possui biometria móvel configurada. Ocultando opção de biometria:', msg);
          stopPushPolling();
          setServerHasPasskeys(false);
          setError(msg);
          setPushStatus('IDLE');
          setIsLoading(false);
          return;
        }

        throw new Error(msg);
      }

      const data = await res.json();
      setPushSolicitacaoId(data.solicitacaoId);
      setPushTempToken(data.tempToken);

      // Iniciar countdown
      if (pushCountdownIntervalRef.current) clearInterval(pushCountdownIntervalRef.current);
      pushCountdownIntervalRef.current = setInterval(() => {
        setPushCountdown(prev => {
          if (prev <= 1) {
            stopPushPolling();
            setError('Tempo limite esgotado. Por favor, digite sua senha acima para acessar.');
            setBiometriaButtonHidden(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      // Iniciar polling
      if (pushPollIntervalRef.current) clearInterval(pushPollIntervalRef.current);
      pushPollIntervalRef.current = setInterval(async () => {
        try {
          const pollRes = await fetch(`${API}/auth/login-push-verify`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ solicitacaoId: data.solicitacaoId, tempToken: data.tempToken })
          });

          if (!pollRes.ok) return;

          const pollData = await pollRes.json();
          if (pollData.status !== 'PENDENTE') {
            stopPushPolling();
            setPushStatus(pollData.status);

            if (pollData.status === 'AUTORIZADO') {
              const userData = { ...pollData.user, role: pollData.user.perfil, roles: [pollData.user.perfil] };
              if (pollData.refresh_token) localStorage.setItem('ac_refresh_token', pollData.refresh_token);
              saveAccountToLocal(userData, 'email');
              // Não marcamos a passkey local neste dispositivo, pois a autenticação biométrica ocorreu remotamente no celular via push.
              login(pollData.access_token, userData);

              // ── Security Compliance Check ──
              sessionStorage.setItem('ac_accesskey_verified', 'true');
              const isPermanent = pollData.opcao === 'PERMANENTE' || pollData.opcao === 'PASSKEY';
              const remainingSteps = (pollData.pendingSecuritySteps || []).filter((s: string) => {
                if (s === 'passkey') {
                  // Se o usuário respondeu como PERMANENTE (Sempre), exige cadastrar a passkey local no desktop
                  return isPermanent;
                }
                return true;
              });

              if (remainingSteps.length > 0) {
                sessionStorage.setItem('ac_pending_security', JSON.stringify(remainingSteps));
                if (isPermanent) {
                  sessionStorage.setItem('ac_device_approval_data', JSON.stringify({
                    solicitacaoId: pollData.solicitacaoId || '',
                    isDeviceRegistered: true,
                    pendingUserData: userData,
                    opcao: pollData.opcao,
                  }));
                }
                navigate('/security-compliance', { replace: true });
                return;
              }
              sessionStorage.removeItem('ac_pending_security');
              sessionStorage.removeItem('ac_device_approval_data');

              const redirectPath = sessionStorage.getItem('ac_redirect_after_login');
              const dashRoute = redirectPath || getRedirectPath(userData.perfil || '', userData.cargo || undefined);
              sessionStorage.removeItem('ac_redirect_after_login');
              navigate(dashRoute, { replace: true });
            } else if (pollData.status === 'NEGADO') {
              setError('Acesso recusado no celular. Por favor, digite sua senha acima para acessar.');
              setBiometriaButtonHidden(true);
            } else if (pollData.status === 'BLOQUEADO') {
              setError('Dispositivo de aprovação bloqueado. Por favor, digite sua senha acima para acessar.');
              setBiometriaButtonHidden(true);
            } else if (pollData.status === 'EXPIRADO') {
              setError('A solicitação de login expirou. Por favor, digite sua senha acima para acessar.');
              setBiometriaButtonHidden(true);
            }
          }
        } catch (e) {
          // ignore network errors and retry
        }
      }, 2000);

    } catch (err: any) {
      stopPushPolling();
      setError(err?.message || 'Falha ao solicitar autorização pelo celular.');
      setBiometriaButtonHidden(true);
    } finally {
      setIsLoading(false);
    }
  };
  //#endregion AC_BLOCK login.push-auth

  //#region AC_BLOCK login.passkey-biometric -- Passkey e biometria local
  // @ac-purpose Executa login por passkey local e preserva senha como fallback sem liberar bypass de seguranca.
  // @ac-related docs_secutiry/matriz-rotas-repetidas-reducao.md#5-passkey-e-biometria
  // @ac-related react-app/src/security/passkey/passkeyApi.ts
  // @ac-routes POST /auth/login-passkey-options; POST /auth/login-passkey-verify
  // @ac-tests node tools/codex-kit.mjs check react-tsc
  /** 
   * Login direto por biometria (Passkey)
   * Usado ao clicar em um card que ja tem passkey ou no fluxo de email.
   */
  const MAX_RETRIES = 2; // Máximo de tentativas de passkey antes de desistir
  const handlePasskeyLogin = async (targetEmail: string, _retryCount = 0, options?: { suppressPasswordFallback?: boolean }) => {
    const suppressPasswordFallback = options?.suppressPasswordFallback === true;
    setIsLoading(true);
    setError('');
    const MAX_RETRIES = 3; // evitar loop infinito
    try {
      // 1. Perguntar ao servidor se há passkeys VÁLIDAS para este usuário
      let optionsPayload;
      try {
        optionsPayload = await requestLoginPasskeyOptions(targetEmail);
      } catch (requestErr: any) {
        const errorData = getPasskeyApiErrorData(requestErr);
        const message = getPasskeyFailureMessage(errorData, 'Não foi possível iniciar a biometria.');
        if (shouldClearLocalPasskeyFromServerFailure(getPasskeyApiStatus(requestErr) ?? 0, message)) {
          console.log('[Passkey] Servidor confirmou credencial inexistente/revogada. Limpando flag local.');
          clearLocalPasskeyFlag(targetEmail);
        } else {
          console.warn('[Passkey] Falha temporária ao obter opções. Preservando flag local:', message);
        }
        setError(message);
        openEmailLogin(targetEmail, true);
        return;
      }
      const options = unwrapPasskeyOptions(optionsPayload);

      // 2. Agora sim disparar o diálogo biométrico do Windows Hello
      const assertion = await startAuthentication({ optionsJSON: options as any });

      // 3. Verificar a assinatura no servidor
      let data;
      try {
        data = await verifyLoginPasskey(targetEmail, assertion);
      } catch (verifyErr: any) {
        // ── Backend já revogou a credencial inválida automaticamente ──
        const errorData = getPasskeyApiErrorData(verifyErr);
        console.log('[Passkey] Verificação falhou. Backend já revogou:', errorData);

        // Signal API: pedir ao browser para remover a credencial local
        const PKC = window.PublicKeyCredential as any;
        if (PKC?.signalUnknownCredential && errorData.revokedCredentialId) {
          try {
            await PKC.signalUnknownCredential({
              rpId: new URL(window.location.origin).hostname,
              credentialId: errorData.revokedCredentialId,
            });
            console.log('[Passkey] signalUnknownCredential → browser removeu credencial local.');
          } catch (sigErr: any) {
            console.warn('[Passkey] signalUnknownCredential falhou:', sigErr?.message);
          }
        }

        // Se ainda há chaves válidas no servidor, tentar novamente
        const remaining = errorData.remainingCredentialIds || [];
        if (remaining.length > 0 && _retryCount < MAX_RETRIES) {
          console.log(`[Passkey] Ainda há ${remaining.length} chave(s) válida(s). Tentando novamente...`);
          setIsLoading(false);
          return handlePasskeyLogin(targetEmail, _retryCount + 1);
        }

        // Sem mais chaves validas no servidor -> limpar apenas a flag local deste usuario.
        clearLocalPasskeyFlag(targetEmail);
        openEmailLogin(targetEmail, true);
        return;
      }

      const loginUser = data.user as any;
      if (!data.access_token || !loginUser) {
        throw new Error('Biometria inválida ou não reconhecida.');
      }
      const userData = { ...loginUser, role: loginUser.perfil, roles: [loginUser.perfil] };
      if (data.refresh_token) localStorage.setItem('ac_refresh_token', data.refresh_token);
      saveAccountToLocal(userData, 'email');
      markPasskeyOnThisDevice(targetEmail);
      login(data.access_token, userData);

      // Signal API: informar ao browser que SÓ esta credencial é válida
      // Isso faz o Windows Hello remover chaves locais extras → próximo login vai direto pro PIN
      try {
        const PKC = window.PublicKeyCredential as any;
        const rpId = new URL(window.location.origin).hostname;
        const usedCredId = assertion.id; // credentialId que acabou de funcionar
        const webauthnUserId = (options as any)?.userId; // SHA-256 determinístico do usuário

        if (PKC?.signalAllAcceptedCredentialIds && webauthnUserId) {
          await PKC.signalAllAcceptedCredentialIds({
            rpId,
            userId: webauthnUserId,
            allAcceptedCredentialIds: [usedCredId],
          });
          console.log('[Passkey] signalAllAcceptedCredentialIds → browser limpou credenciais extras.');
        }

        // Atualizar nome/displayName no browser cache
        if (PKC?.signalCurrentUserDetails && webauthnUserId) {
          await PKC.signalCurrentUserDetails({
            rpId,
            userId: webauthnUserId,
            name: targetEmail,
            displayName: userData.nome || targetEmail,
          });
        }
      } catch (sigErr: any) {
        console.warn('[Passkey] Signal API (cleanup pós-login):', sigErr?.message);
      }

      // ── Security Compliance Check ─────────────────────────────────────
      // Passkey step já está cumprido pois o login foi feito por biometria
      sessionStorage.setItem('ac_accesskey_verified', 'true');
      localStorage.removeItem('ac_security_onboarding_pending');
      const remainingSteps = ((data.pendingSecuritySteps as string[] | undefined) || []).filter((s: string) => s !== 'passkey');
      if (remainingSteps.length > 0) {
        saveLocalAccountShortcut(userData, 'pending_security');
        sessionStorage.setItem('ac_pending_security', JSON.stringify(remainingSteps));
        navigate('/security-compliance', { replace: true });
        return;
      }
      sessionStorage.removeItem('ac_pending_security');

      const redirectPath = sessionStorage.getItem('ac_redirect_after_login');
      const dashRoute = redirectPath || getRedirectPath(userData.perfil || '', userData.cargo || undefined);
      sessionStorage.removeItem('ac_redirect_after_login');
      navigate(dashRoute, { replace: true });
    } catch (err: any) {
      console.warn('[Passkey] Falha ou cancelamento na autenticação local:', err);

      const isCancelation = isPasskeyDialogCancel(err);
      const isNotFoundError = err.name === 'NotFoundError' || err.message?.includes('not found') || err.message?.includes('no credential');

      if (isNotFoundError) {
        const targetEmailNormalized = targetEmail.trim().toLowerCase();
        console.log(`[Passkey] Credencial não existe neste dispositivo para ${targetEmailNormalized}. Limpando flag local e agendando revogação no servidor.`);
        clearPasskeyOnThisDevice(targetEmailNormalized);
        sessionStorage.setItem(`ac_passkey_cleanup_pending_${targetEmailNormalized}`, 'true');
        setError('Nenhuma chave de acesso disponível para esta conta neste dispositivo. Faça login com sua senha.');
        openEmailLogin(targetEmail, true, true);
        return;
      }

      if (isCancelation) {
        // Se for apenas cancelamento de diálogo pelo usuário, NÃO limpa a passkey dele localmente!
        setError('Login por biometria cancelado. Use sua senha ou tente novamente.');
        openEmailLogin(targetEmail, true, true);
        return;
      }

      // Falha inesperada
      setServerHasPasskeys(false);
      setError('Biometria local indisponível nesta tentativa. Use sua senha ou tente novamente.');
      openEmailLogin(targetEmail, true, true);
    } finally {
      setIsLoading(false);
    }
  };
  //#endregion AC_BLOCK login.passkey-biometric

  // Auto-prompt Passkey or Password on mount if saved accounts exist
  // Desativado para evitar loop de biometria ao falhar/cancelar e dar controle ao usuário.
  /*
  useEffect(() => {
    // Não disparar auto-prompt se já está autenticado, carregando ou já tentou
    if (savedAccounts.length > 0 && !autoPromptAttempted && !isLoading && !isAuthenticated) {
      const loggedOut = sessionStorage.getItem('ac_logged_out') === 'true';
      if (loggedOut) {
        return;
      }
      setAutoPromptAttempted(true);
      const lastAcc = savedAccounts[0];
      // Só tenta auto-prompt de passkey se a chave de fato estiver presente no localStorage deste dispositivo/navegador
      if (isPasskeyEnabled && lastAcc.hasPasskey) {
        handlePasskeyLogin(lastAcc.email);
      } else {
        openEmailLogin(lastAcc.email, true);
      }
    }
  }, [savedAccounts, isPasskeyEnabled, autoPromptAttempted, openEmailLogin, handlePasskeyLogin, isLoading, isAuthenticated]);
  */

  // Auto-disparo do login biométrico/push quando o e-mail existe e tem passkeys cadastradas
  // Desativado para dar controle total ao usuário e evitar loop infinito.
  /*
  useEffect(() => {
    if (
      cardMode === 'login' &&
      emailStep === 2 &&
      serverHasPasskeys &&
      emailStatus === 'exists' &&
      email &&
      autoPromptAttemptedForEmail !== email.trim().toLowerCase() &&
      !isLoading &&
      !isWaitingPushApproval
    ) {
      setAutoPromptAttemptedForEmail(email.trim().toLowerCase());
    }
  }, [cardMode, emailStep, serverHasPasskeys, emailStatus, email, autoPromptAttemptedForEmail, isLoading, isWaitingPushApproval, handlePasskeyLogin]);
  */

  //#region AC_BLOCK login.recovery-step -- Recuperacao de senha
  // @ac-purpose Controla pedido por e-mail, codigo e redefinicao sem misturar e-mail e senha no mesmo passo de login.
  // @ac-related docs_secutiry/execucao-lote-45-biometria-mobile-login-ux.md#Execucao-realizada
  // @ac-related react-app/src/api/routes.ts#auth.passwordRecovery
  // @ac-routes POST /auth/password-recovery/request; POST /auth/password-recovery/verify; POST /auth/password-recovery/reset
  // @ac-tests node tools/codex-kit.mjs check react-tsc
  const getRecoveryApiMessage = (err: any, fallback: string) =>
    err?.response?.data?.error?.message ||
    err?.response?.data?.message ||
    fallback;

  //#region AC_BLOCK login.forgot-email -- Identificacao de e-mail por CPF/data
  // @ac-purpose Localiza o e-mail da conta ativa quando o usuario nao lembra qual e-mail usar no login.
  // @ac-related backend/src/auth/auth.controller.ts#lookupAccountEmailByCpfBirth
  // @ac-related react-app/src/api/routes.ts#auth.accountLookup
  // @ac-routes POST /auth/account-lookup/by-cpf-birth
  // @ac-tests node tools/codex-kit.mjs check react-tsc
  const startForgotEmailLookup = () => {
    if (isLoading || isWaitingPushApproval) return;
    clearForgotEmailLookup();
    setError('');
    setSuccessMessage('');
    setCardMode('forgot-email-cpf');
    window.setTimeout(() => {
      focusLoginInputForKeyboard(forgotEmailCpfInputRef.current, { requestVirtualKeyboard: isMobile });
    }, 30);
  };

  const handleForgotEmailCpfAdvance = () => {
    const cpfInput = forgotEmailCpfInputRef.current;
    const cpfClean = forgotEmailCpf.replace(/\D/g, '');
    if (cpfClean.length !== 11) {
      cpfInput?.setCustomValidity('Digite seu CPF com 11 numeros.');
      cpfInput?.reportValidity();
      setError('');
      return;
    }
    cpfInput?.setCustomValidity('');
    setError('');
    setCardMode('forgot-email-birth');
    window.setTimeout(() => {
      focusLoginInputForKeyboard(forgotEmailBirthInputRef.current, { requestVirtualKeyboard: isMobile });
    }, 30);
  };

  const handleForgotEmailLookup = async () => {
    const birthInput = forgotEmailBirthInputRef.current;
    const cpfClean = forgotEmailCpf.replace(/\D/g, '');
    const birthDateIso = birthDateToIsoForLoginLookup(forgotEmailBirthDate);
    if (cpfClean.length !== 11) {
      setCardMode('forgot-email-cpf');
      setError('Informe o CPF para continuar.');
      return;
    }
    if (!birthDateIso) {
      birthInput?.setCustomValidity('Informe a data no formato 00/00/0000.');
      birthInput?.reportValidity();
      setError('');
      return;
    }
    birthInput?.setCustomValidity('');
    setIsLoading(true);
    setError('');
    try {
      const { data } = await api.post(ROUTES.auth.accountLookup.byCpfBirth, {
        cpf: cpfClean,
        dataNascimento: birthDateIso,
      });
      const foundEmail = String(data?.email || '').trim().toLowerCase();
      const foundName = String(data?.nome || '').trim();
      if (!foundEmail) {
        throw new Error('Nao foi possivel identificar o e-mail da conta.');
      }
      setForgotEmailLookup({ nome: foundName || 'Conta encontrada', email: foundEmail });
      setCardMode('forgot-email-confirm');
    } catch (err: any) {
      setError(getRecoveryApiMessage(err, 'Nao foi possivel localizar uma conta ativa com esses dados.'));
    } finally {
      setIsLoading(false);
    }
  };

  const confirmForgotEmailLookup = async () => {
    const foundEmail = forgotEmailLookup?.email?.trim().toLowerCase();
    if (!foundEmail) {
      setError('Confirme a conta encontrada antes de continuar.');
      return;
    }
    setEmail(foundEmail);
    setPassword('');
    setError('');
    setSuccessMessage('');
    setCardMode('login');
    setEmailStep(2);
    setEmailStatus('checking');
    setEmailStatusEmail(foundEmail);
    clearForgotEmailLookup();
    await checkEmailInstant(foundEmail, true);
    window.setTimeout(() => {
      focusLoginInputForKeyboard(passwordInputRef.current, { requestVirtualKeyboard: isMobile });
    }, 30);
  };
  //#endregion AC_BLOCK login.forgot-email

  const handleRequestRecovery = async () => {
    const recoveryEmailInput = emailInputRef.current;
    if (!email.trim()) {
      recoveryEmailInput?.setCustomValidity('Digite seu e-mail.');
      recoveryEmailInput?.reportValidity();
      setError('');
      return;
    }
    if (recoveryEmailInput && !recoveryEmailInput.checkValidity()) {
      recoveryEmailInput.reportValidity();
      setError('');
      return;
    }
    recoveryEmailInput?.setCustomValidity('');
    setPendingRecoveryEmail(email.trim().toLowerCase());
    setRecoveryDebugCode('');
    setError('');
    setSuccessMessage('');
    setCardMode('recover-confirm-email');
  };

  const handleConfirmEmailRecovery = async () => {
    const targetEmail = pendingRecoveryEmail || email.trim().toLowerCase();
    if (!targetEmail) {
      setCardMode('recover-request');
      setError('Informe o e-mail antes de confirmar o envio.');
      return;
    }
    setIsLoading(true);
    setError('');
    try {
      const { data } = await api.post(ROUTES.auth.passwordRecovery.request, {
        email: targetEmail,
      });
      setEmail(targetEmail);
      if (data?.emailMasked) setRecoverEmailMasked(data.emailMasked);
      const cleanCode = String(data?.debugCode || data?.codigoTeste || '').replace(/\D/g, '').slice(0, 6);
      setRecoveryDebugCode(cleanCode);
      setRecoverCode(cleanCode);
      const expiry = Date.now() + 60000;
      sessionStorage.setItem('ac_resend_timer_expiry', expiry.toString());
      setResendTimer(60);
      setCardMode('recover-code');
    } catch (err: any) {
      setError(getRecoveryApiMessage(err, 'Não foi possível iniciar a recuperação.'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleStartSelfRegistration = async (targetEmail: string) => {
    setIsLoading(true);
    setError('');
    try {
      const { data } = await api.post('/auth/self-register/request', {
        email: targetEmail,
      });
      setEmail(targetEmail);

      const masked = targetEmail.replace(/(.{2}).+(@.+)/, '$1***$2');
      setRecoverEmailMasked(masked);

      if (data?.debugCode) {
        const cleanCode = String(data.debugCode).replace(/\D/g, '').slice(0, 6);
        setRecoveryDebugCode(cleanCode);
        setRecoverCode(cleanCode);
      } else {
        setRecoverCode('');
      }
      setIsSelfRegistering(true);
      const expiry = Date.now() + 60000;
      sessionStorage.setItem('ac_resend_timer_expiry', expiry.toString());
      setResendTimer(60);
      setCardMode('recover-code');
    } catch (err: any) {
      setError(getRecoveryApiMessage(err, 'Não foi possível iniciar o cadastro de visitante.'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyRecoveryCode = async () => {
    if (!recoverCode.trim()) {
      setError('Informe o código de recuperação.');
      return;
    }
    if (!email.trim()) {
      setError('Informe e-mail e código de recuperação.');
      return;
    }
    setIsLoading(true);
    setError('');
    try {
      if (isSelfRegistering) {
        await api.post('/auth/self-register/verify', {
          email: email.trim().toLowerCase(),
          code: recoverCode.trim(),
        });
      } else {
        await api.post(ROUTES.auth.passwordRecovery.verify, {
          email: email.trim().toLowerCase(),
          code: recoverCode.trim(),
        });
      }
      setCardMode('recover-reset');
    } catch (err: any) {
      setError(getRecoveryApiMessage(err, 'Código inválido ou expirado.'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (recoverNewPassword.length < 8) {
      setError('A nova senha deve ter pelo menos 8 caracteres.');
      return;
    }
    if (recoverNewPassword !== recoverConfirmPassword) {
      setError('As senhas não conferem.');
      return;
    }
    if (!recoverCode.trim() || !email.trim()) {
      setError('Informe e-mail e código de recuperação.');
      return;
    }
    setIsLoading(true);
    setError('');
    try {
      if (isSelfRegistering) {
        const { data } = await api.post('/auth/self-register/complete', {
          email: email.trim().toLowerCase(),
          code: recoverCode.trim(),
          senha: recoverNewPassword,
        });

        const userData = {
          ...data.user,
          role: data.user.perfil,
          roles: [data.user.perfil],
          acessoDireto: data.user.acessoDireto ?? false,
          statusConta: data.user.statusConta,
        };
        saveAccountToLocal(userData, 'email');
        login(data.access_token, userData);
        if (data.refresh_token) {
          localStorage.setItem('ac_refresh_token', data.refresh_token);
        }

        setIsSelfRegistering(false);
        setCardMode('login');
        setPassword('');
        setRecoverCode('');
        setRecoveryDebugCode('');
        setRecoverNewPassword('');
        setRecoverConfirmPassword('');
        setError('');

        await useAuthStore.getState().checkAuth();
        const redirectPath = sessionStorage.getItem('ac_redirect_after_login');
        const dashRoute = redirectPath || getRedirectPath(userData.perfil || '', userData.cargo || undefined);
        sessionStorage.removeItem('ac_redirect_after_login');
        navigate(dashRoute, { replace: true });
      } else {
        await api.post(ROUTES.auth.passwordRecovery.reset, {
          email: email.trim().toLowerCase(),
          code: recoverCode.trim(),
          senha: recoverNewPassword,
        });
        setCardMode('login');
        setPassword('');
        setRecoverCode('');
        setRecoveryDebugCode('');
        setRecoverNewPassword('');
        setRecoverConfirmPassword('');
        setError('');
        setSuccessMessage('Senha redefinida com sucesso! Conecte-se com sua nova senha.');
      }
    } catch (err: any) {
      setError(getRecoveryApiMessage(err, 'Não foi possível cadastrar a nova senha.'));
    } finally {
      setIsLoading(false);
    }
  };
  //#endregion AC_BLOCK login.recovery-step




  // Impersonation loading
  if (impersonating) {
    return (
      <div style={{ position: 'fixed', inset: 0, background: '#020617', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center', color: '#94a3b8' }}>
          <div style={{ width: 40, height: 40, border: '3px solid rgba(255,255,255,0.1)', borderTopColor: '#10b981', borderRadius: '50%', animation: 'lk-spin 0.8s linear infinite', margin: '0 auto 16px' }} />
          <p style={{ fontSize: '0.9rem', fontWeight: 500 }}>Abrindo sessao administrativa...</p>
        </div>
        <style>{`@keyframes lk-spin { to { transform: rotate(360deg) } }`}</style>
      </div>
    );
  }
  // ═══════════════════════════════════════════════════
  function renderSuccessDialog() {
    return (
      <div
        role="presentation"
        onClick={() => setSuccessMessage('')}
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 1000000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 20,
          background: 'rgba(15, 7, 35, 0.52)',
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
          animation: 'acm-fadeIn 0.18s ease both',
          boxSizing: 'border-box',
        }}
      >
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Mensagem de sucesso"
          onClick={(event) => event.stopPropagation()}
          style={{
            width: 'min(100%, 360px)',
            borderRadius: 22,
            padding: '24px 22px 20px',
            background: '#ffffff',
            color: '#0f172a',
            boxShadow: '0 24px 70px rgba(12, 4, 30, 0.36)',
            textAlign: 'center',
            animation: 'acm-slideUp 0.22s ease both',
          }}
        >
          <div
            style={{
              width: 58,
              height: 58,
              borderRadius: '50%',
              margin: '0 auto 14px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'rgba(34, 197, 94, 0.12)',
              color: '#16a34a',
              boxShadow: '0 0 0 8px rgba(34, 197, 94, 0.06)',
            }}
          >
            <CheckCircle2 size={34} strokeWidth={2.4} />
          </div>
          <h3 style={{ margin: '0 0 8px', fontSize: '1.18rem', fontWeight: 850, color: '#0f172a' }}>
            Tudo certo
          </h3>
          <p style={{ margin: '0 0 18px', color: '#475569', fontSize: '0.96rem', lineHeight: 1.45, fontWeight: 560 }}>
            {successMessage}
          </p>
          <button
            type="button"
            autoFocus
            onClick={() => setSuccessMessage('')}
            style={{
              width: '100%',
              height: 48,
              border: 'none',
              borderRadius: 14,
              background: '#16a34a',
              color: '#ffffff',
              fontSize: '0.96rem',
              fontWeight: 850,
              cursor: 'pointer',
              boxShadow: '0 10px 24px rgba(22, 163, 74, 0.24)',
            }}
          >
            OK
          </button>
        </div>
      </div>
    );
  }

  // Unified Sovereign Layout (Desktop & Mobile)
  // ═══════════════════════════════════════════════════
  const showActivationLinkButton = (!isMobile || savedAccounts.length > 0) && !showEmailForm && isServerReady;
  const activationLinkButton = showActivationLinkButton ? (
    <button
      type="button"
      className="activation-link-footer"
      onClick={abrirLinkAtivacao}
      style={{
        position: 'static',
        padding: isMobile ? '8px 13px' : '8px 15px',
        borderRadius: 999,
        border: '1px solid rgba(255,255,255,0.25)',
        background: 'rgba(255,255,255,0.08)',
        color: '#ddd6fe',
        fontSize: isMobile ? '.76rem' : '.78rem',
        fontWeight: 650,
        cursor: 'pointer',
        backdropFilter: 'blur(8px)',
        boxShadow: '0 10px 24px rgba(15, 23, 42, 0.16)',
      }}
    >
      Colar link de ativação
    </button>
  ) : null;
  const loginPairingDisplayName = loginPairingUserPreview?.nome?.trim() || 'Conta no celular';
  const loginPairingDisplayEmail = loginPairingUserPreview?.email?.trim() || 'Aguardando identidade segura';
  const loginPairingDisplayRole = loginPairingUserPreview?.perfil?.trim() || 'Acesso supervisionado';
  const loginPairingInitials = (loginPairingDisplayName || loginPairingDisplayEmail)
    .split(/[\s@._-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('') || 'AC';
  const loginPairingClockRadius = 52;
  const loginPairingClockCircumference = 2 * Math.PI * loginPairingClockRadius;
  const loginPairingSecondsSafe = Math.max(0, loginPairingSecondsLeft);
  const loginPairingClockProgress = Math.max(0, Math.min(1, loginPairingSecondsSafe / 30));
  const loginPairingMachineLabel = typeof window !== 'undefined' && window.location.host
    ? window.location.host
    : 'Esta maquina';

  return (
    <>

      <SovereignSplash
        skipIntro={skipSplash}
        onComplete={handleSplashComplete}
        settledScale={savedAccounts.length === 0 ? 1.12 : 1}
        subtitle={
          savedAccounts.length > 0
            ? 'Quem está acessando?'
            : 'Configure seu novo perfil'
        }
        brandFooter={activationLinkButton}>
        {/* Área clicável invisível no topo para acionar o painel demo (triplo clique) */}
        <div onClick={handleLogoClick} style={{ position: 'absolute', top: 0, left: 0, width: isMobile ? '100%' : '35%', height: '15vh', zIndex: 9999, cursor: 'pointer' }} />

        {/* Background Shapes for visual richness (Dynamic) */}
        <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none', zIndex: -1 }}>
          {Array.from({ length: 15 }).map((_, i) => {
            const size = Math.random() * 65 + 45; // Tamanhos 2x menores que os originais (45 a 110)
            const top = 50 + Math.random() * 45; // Nao passar de metade da tela (fica na metade inferior)
            const left = Math.random() * 90;
            const dur = 6 + Math.random() * 4; // Variando tempo de opacidade (6 a 10s)
            const floatDur = 30 + Math.random() * 20; // Mover mais lento
            const colors = ['#ffffff', '#a78bfa', '#4ade80', '#facc15', '#c084fc'];
            const color = colors[i % colors.length];
            const shapes = [
              <circle cx="50" cy="50" r="40" fill={color} />,
              <polygon points="50,10 90,90 10,90" fill={color} />,
              <rect x="20" y="20" width="60" height="60" rx="15" fill={color} transform="rotate(20 50 50)" />
            ];
            return (
              <svg key={i} style={{
                position: 'absolute', top: `${top}%`, left: `${left}%`,
                width: size, height: size, opacity: 0,
                animation: `acm-float1 ${floatDur}s infinite ease-in-out alternate, acm-pulseOpacity ${dur}s infinite ease-in-out ${Math.random() * 5}s`
              }} viewBox="0 0 100 100">
                {shapes[i % 3]}
              </svg>
            )
          })}
        </div>

        <div style={{
          display: 'flex', flexDirection: 'column',
          minHeight: isMobile ? 'auto' : 'calc(100vh - 120px)',
          width: '100%', alignItems: 'center',
          boxSizing: 'border-box',
        }}>
          {/* Main Content Centering Wrapper */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: isMobile ? 'flex-start' : 'center',
            flex: 1,
            width: '100%',
            gap: isMobile ? 20 : 28,
            paddingTop: isMobile ? 56 : 0,
            paddingBottom: isMobile ? 32 : 0,
          }}>
            {!isServerReady ? renderServerLoadingCard() : showMobileGate ? renderMobileGate() : (
              !showEmailForm && renderLoginForm()
            )}
          </div>


        </div>

        <style>{cssAnimations}</style>
        {isServerReady && showDemoDialog && renderDemoDialog()}
      </SovereignSplash>

      {isServerReady && (showEmailForm || cardMode !== 'login') && renderEmailOverlay()}
      {successMessage && renderSuccessDialog()}
      {showReplacedModal && renderReplacedModal()}

      {/*#region AC_BLOCK login.qr-auto-login-ui -- Bloqueio visual durante login por QR*/}
      {/* @ac-purpose Bloqueia interacao no login enquanto o desktop consome a autenticacao vinda do QR do celular. */}
      {loginPairingIsBlocking && (
        <div
          role="status"
          aria-live="polite"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 1000000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: isMobile ? 16 : 28,
            background:
              'radial-gradient(circle at 18% 18%, rgba(250,204,21,0.18), transparent 24%), radial-gradient(circle at 80% 20%, rgba(239,68,68,0.18), transparent 28%), linear-gradient(135deg, rgba(8,3,28,0.94), rgba(43,12,97,0.9) 48%, rgba(91,33,182,0.92))',
            backdropFilter: 'blur(18px) saturate(1.1)',
            WebkitBackdropFilter: 'blur(18px) saturate(1.1)',
            pointerEvents: 'auto',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              width: 'min(1040px, 100%)',
              minHeight: isMobile ? 'min(760px, calc(100vh - 32px))' : 'min(600px, calc(100vh - 56px))',
              borderRadius: isMobile ? 28 : 36,
              padding: isMobile ? 18 : 28,
              position: 'relative',
              overflow: 'hidden',
              background:
                'linear-gradient(145deg, rgba(76,29,149,0.92), rgba(24,8,54,0.96) 54%, rgba(15,7,35,0.98))',
              color: '#ffffff',
              boxShadow:
                '0 40px 120px rgba(4, 1, 18, 0.62), 0 0 0 1px rgba(221,214,254,0.2), inset 0 1px 0 rgba(255,255,255,0.18)',
              border: '1px solid rgba(221, 214, 254, 0.22)',
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : '1fr 1.05fr 1fr',
              alignItems: 'stretch',
              gap: isMobile ? 16 : 20,
              animation: 'acm-pairingPanelIn 0.35s cubic-bezier(0.2, 0.9, 0.2, 1) both',
            }}
          >
            <div style={{
              position: 'absolute',
              inset: 0,
              background:
                'radial-gradient(circle at 20% 22%, rgba(255,255,255,0.13), transparent 24%), radial-gradient(circle at 84% 76%, rgba(250,204,21,0.12), transparent 25%)',
              pointerEvents: 'none',
            }} />

            <div style={{
              position: 'relative',
              zIndex: 1,
              borderRadius: 28,
              padding: isMobile ? 18 : 22,
              background: 'linear-gradient(155deg, rgba(88,28,135,0.84), rgba(30,10,64,0.86))',
              border: '1px solid rgba(221,214,254,0.22)',
              boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.15)',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              minHeight: isMobile ? 210 : 'auto',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                <span style={{ fontSize: '.78rem', fontWeight: 900, letterSpacing: '0.12em', color: '#ddd6fe', textTransform: 'uppercase' }}>
                  Celular
                </span>
                <Smartphone size={22} color="#c4b5fd" />
              </div>
              <div style={{ display: 'grid', placeItems: 'center', gap: 14, textAlign: 'center' }}>
                <div style={{
                  width: isMobile ? 92 : 118,
                  height: isMobile ? 92 : 118,
                  borderRadius: '50%',
                  display: 'grid',
                  placeItems: 'center',
                  background: 'radial-gradient(circle at 34% 25%, rgba(255,255,255,0.34), rgba(124,58,237,0.9) 48%, rgba(49,10,101,1))',
                  border: '3px solid rgba(221,214,254,0.58)',
                  boxShadow: '0 20px 46px rgba(15,7,35,0.42), 0 0 0 10px rgba(255,255,255,0.08)',
                  overflow: 'hidden',
                }}>
                  {loginPairingUserPreview?.fotoUrl ? (
                    <img
                      src={loginPairingUserPreview.fotoUrl}
                      alt=""
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  ) : (
                    <span style={{ fontSize: isMobile ? '1.9rem' : '2.45rem', fontWeight: 950, color: '#ffffff' }}>
                      {loginPairingInitials}
                    </span>
                  )}
                </div>
                <div>
                  <div style={{ fontSize: isMobile ? '1.35rem' : '1.65rem', fontWeight: 950, lineHeight: 1.05 }}>
                    {loginPairingDisplayName}
                  </div>
                  <div style={{ marginTop: 8, fontSize: '.9rem', fontWeight: 750, color: '#c4b5fd', wordBreak: 'break-word' }}>
                    {loginPairingDisplayEmail}
                  </div>
                </div>
              </div>
              <div style={{
                borderRadius: 18,
                padding: '10px 12px',
                background: 'rgba(255,255,255,0.08)',
                border: '1px solid rgba(255,255,255,0.12)',
                fontSize: '.82rem',
                color: '#ddd6fe',
                fontWeight: 800,
                textAlign: 'center',
              }}>
                {loginPairingDisplayRole}
              </div>
            </div>

            <div style={{
              position: 'relative',
              zIndex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 18,
              textAlign: 'center',
              minHeight: isMobile ? 300 : 'auto',
            }}>
              <div style={{
                position: 'relative',
                width: 154,
                height: 154,
                display: 'grid',
                placeItems: 'center',
                animation: 'acm-pairingClock 1.8s ease-in-out infinite',
              }}>
                <svg width="154" height="154" viewBox="0 0 154 154" aria-hidden>
                  <circle cx="77" cy="77" r={loginPairingClockRadius} fill="rgba(255,255,255,0.08)" stroke="rgba(221,214,254,0.18)" strokeWidth="16" />
                  <circle
                    cx="77"
                    cy="77"
                    r={loginPairingClockRadius}
                    fill="none"
                    stroke="#facc15"
                    strokeWidth="16"
                    strokeLinecap="round"
                    strokeDasharray={loginPairingClockCircumference}
                    strokeDashoffset={loginPairingClockCircumference * (1 - loginPairingClockProgress)}
                    transform="rotate(-90 77 77)"
                  />
                </svg>
                <div style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center' }}>
                  <div>
                    <div style={{ fontSize: '2.55rem', fontWeight: 950, lineHeight: 1 }}>
                      {loginPairingSecondsSafe}
                    </div>
                    <div style={{ marginTop: 3, fontSize: '.75rem', color: '#fde68a', fontWeight: 900, letterSpacing: '0.12em' }}>
                      SEG
                    </div>
                  </div>
                </div>
              </div>

              <div style={{
                width: '100%',
                minHeight: 76,
                position: 'relative',
                display: 'grid',
                placeItems: 'center',
                overflow: 'hidden',
              }}>
                <div style={{
                  width: '86%',
                  height: 4,
                  borderRadius: 999,
                  background: 'linear-gradient(90deg, rgba(34,197,94,0), rgba(34,197,94,0.92), rgba(250,204,21,0.92), rgba(34,197,94,0))',
                  boxShadow: '0 0 28px rgba(34,197,94,0.55)',
                  animation: 'acm-pairingBeam 1.15s ease-in-out infinite',
                }} />
                <div style={{
                  position: 'absolute',
                  width: 70,
                  height: 70,
                  borderRadius: '50%',
                  border: '1px solid rgba(34,197,94,0.5)',
                  animation: 'acm-pairingSignal 1.35s ease-out infinite',
                }} />
                <div style={{
                  position: 'relative',
                  width: 62,
                  height: 62,
                  borderRadius: '50%',
                  display: 'grid',
                  placeItems: 'center',
                  background: 'linear-gradient(135deg, #22c55e, #7c3aed)',
                  boxShadow: '0 18px 44px rgba(34,197,94,0.28), 0 0 32px rgba(124,58,237,0.4)',
                  border: '1px solid rgba(255,255,255,0.32)',
                }}>
                  <Loader2 size={32} style={{ animation: 'lk-spin 0.9s linear infinite', color: '#ffffff' }} />
                </div>
              </div>

              <div>
                <h2 style={{ margin: 0, fontSize: isMobile ? '1.7rem' : '2.2rem', fontWeight: 950, letterSpacing: '-0.04em' }}>
                  Conectando acesso
                </h2>
                <p style={{ margin: '10px auto 0', maxWidth: 380, color: '#ddd6fe', fontSize: isMobile ? '.98rem' : '1.05rem', lineHeight: 1.45, fontWeight: 650 }}>
                  {loginPairingBlockingMessage || 'Aguardando aprovacao biometrica no celular para liberar esta maquina.'}
                </p>
              </div>

              <button
                type="button"
                onClick={cancelLoginPairing}
                style={{
                  border: '1px solid rgba(254, 202, 202, 0.5)',
                  background: 'linear-gradient(135deg, rgba(127,29,29,0.72), rgba(239,68,68,0.2))',
                  color: '#fee2e2',
                  borderRadius: 16,
                  padding: '13px 18px',
                  fontWeight: 950,
                  cursor: 'pointer',
                  boxShadow: '0 18px 38px rgba(127,29,29,0.22)',
                }}
              >
                Cancelar sessao
              </button>
            </div>

            <div style={{
              position: 'relative',
              zIndex: 1,
              borderRadius: 28,
              padding: isMobile ? 18 : 22,
              background: 'linear-gradient(155deg, rgba(30,10,64,0.88), rgba(76,29,149,0.82))',
              border: '1px solid rgba(221,214,254,0.22)',
              boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.15)',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              minHeight: isMobile ? 210 : 'auto',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                <span style={{ fontSize: '.78rem', fontWeight: 900, letterSpacing: '0.12em', color: '#ddd6fe', textTransform: 'uppercase' }}>
                  Maquina
                </span>
                <Monitor size={24} color="#fde68a" />
              </div>
              <div style={{ display: 'grid', placeItems: 'center', gap: 16, textAlign: 'center' }}>
                <div style={{
                  width: isMobile ? 118 : 148,
                  height: isMobile ? 82 : 104,
                  borderRadius: 20,
                  display: 'grid',
                  placeItems: 'center',
                  background: 'linear-gradient(180deg, rgba(255,255,255,0.18), rgba(255,255,255,0.05))',
                  border: '2px solid rgba(221,214,254,0.38)',
                  boxShadow: '0 24px 54px rgba(15,7,35,0.36), inset 0 1px 0 rgba(255,255,255,0.2)',
                }}>
                  <Monitor size={isMobile ? 54 : 68} color="#ffffff" strokeWidth={1.7} />
                </div>
                <div>
                  <div style={{ fontSize: isMobile ? '1.25rem' : '1.55rem', fontWeight: 950 }}>
                    Liberando desktop
                  </div>
                  <div style={{ marginTop: 8, color: '#ddd6fe', fontSize: '.88rem', fontWeight: 800, wordBreak: 'break-word' }}>
                    {loginPairingMachineLabel}
                  </div>
                </div>
              </div>
              <div style={{
                borderRadius: 18,
                padding: '10px 12px',
                background: 'rgba(34,197,94,0.12)',
                border: '1px solid rgba(74,222,128,0.25)',
                color: '#bbf7d0',
                fontSize: '.82rem',
                fontWeight: 900,
                textAlign: 'center',
              }}>
                Supervisao humana ativa
              </div>
            </div>
          </div>
        </div>
      )}
      {/*#endregion AC_BLOCK login.qr-auto-login-ui*/}




    </>);

  // ═══════════════════════════════════════════════════
  // ── Gate Mobile pré-login ───────────────────────────────────────
  function renderMobileGate() {
    const handleOpenInApp = () => {
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.userAgent.includes('Mac') && 'ontouchend' in document);
      if (isIOS) {
        setShowMobileGate(false);
        return;
      }
      try {
        window.location.href = `web+alunoconectado://${window.location.host}${window.location.pathname}${window.location.search}`;
      } catch {
        setShowMobileGate(false);
      }
    };

    return (
      <div style={{
        background: '#ffffff',
        borderRadius: 22,
        padding: '30px 24px',
        width: '100%',
        maxWidth: 400,
        boxShadow: '0 20px 60px rgba(0,0,0,0.12), 0 0 0 1px rgba(0,0,0,0.04)',
        textAlign: 'center',
        animation: 'acm-slideUp 0.5s ease',
        boxSizing: 'border-box'
      }}>
        <div style={{ position: 'relative', display: 'inline-block', marginBottom: 16 }}>
          <div style={{
            position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
            width: 90, height: 90, borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(108,92,231,0.08) 0%, transparent 70%)',
            pointerEvents: 'none',
          }} />
          <div style={{
            width: 56, height: 56, borderRadius: '50%', position: 'relative',
            background: 'linear-gradient(135deg, #f5f3ff, #ede9fe)',
            border: '1px solid #ddd6fe',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 0 20px rgba(108,92,231,0.06)',
            margin: '0 auto'
          }}>
            <Smartphone size={28} strokeWidth={1.5} style={{ color: '#6c5ce7' }} />
          </div>
        </div>

        <h2 style={{ fontSize: '1.25rem', fontWeight: 800, color: '#1e293b', margin: '0 0 8px', letterSpacing: '-0.02em' }}>
          Acesso via Aplicativo
        </h2>
        <p style={{ color: '#64748b', fontSize: '.84rem', lineHeight: 1.5, marginBottom: 24 }}>
          Por motivos de segurança e conformidade, a plataforma Aluno Conectado deve ser acessada pelo aplicativo oficial no seu celular.
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <button
            onClick={handleOpenInApp}
            style={{
              width: '100%', padding: '16px 20px', borderRadius: 14, border: 'none',
              background: 'linear-gradient(135deg, #7c3aed, #6d28d9)',
              color: '#ffffff', fontSize: '0.95rem', fontWeight: 800,
              cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              boxShadow: '0 4px 14px rgba(108,92,231,0.25)',
              transition: 'all .2s',
            }}
          >
            <ShieldCheck size={18} />
            Abrir no Aplicativo
          </button>

          <button
            onClick={() => setShowMobileGate(false)}
            style={{
              width: '100%', padding: '14px 20px', borderRadius: 14,
              border: '1.5px solid #e2e8f0', background: '#f8fafc',
              color: '#475569', fontSize: '0.9rem', fontWeight: 700,
              cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              transition: 'all .2s',
            }}
          >
            Fazer Login no Navegador
          </button>
        </div>

        <div style={{ marginTop: 24, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
          <ShieldCheck size={13} style={{ color: '#94a3b8' }} />
          <span style={{ fontSize: '.76rem', color: '#94a3b8', fontWeight: 500 }}>
            Conexão segura . Dados protegidos
          </span>
        </div>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════
  // Card de espera de inicialização do servidor
  // ═══════════════════════════════════════════════════
  function renderServerLoadingCard() {
    const soloAddCardWidth = Math.round(Math.min(Math.max(windowWidth * 0.34, 480), 720));
    const soloAddCardHeight = Math.round(soloAddCardWidth * 1.12);

    return (
      <div style={{
        width: '100%',
        maxWidth: 'min(96vw, 980px)',
        minHeight: 'min(80vh, 860px)',
        margin: '0 auto',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        justifyContent: 'center',
        padding: 0,
        boxSizing: 'border-box',
        animation: 'acm-fadeIn 0.5s ease',
      }}>
        <div
          style={{
            width: soloAddCardWidth,
            maxWidth: '100%',
            height: soloAddCardHeight,
            maxHeight: '80vh',
            alignSelf: 'center',
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: isMobile ? '24px 18px' : '40px 32px',
            borderRadius: 24,
            overflow: 'hidden',
            background: LOGIN_PREMIUM_WHITE_CARD,
            border: '1px solid rgba(255,255,255,0.92)',
            boxShadow: '0 30px 78px rgba(17, 7, 48, 0.48), 0 0 58px rgba(139, 92, 246, 0.28)',
            boxSizing: 'border-box',
          }}
        >
          {/* Glowing loader ring */}
          <div style={{
            position: 'relative',
            width: 100,
            height: 100,
            marginBottom: 28,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <div style={{
              position: 'absolute',
              width: '100%',
              height: '100%',
              borderRadius: '50%',
              border: '4px solid rgba(139, 92, 246, 0.1)',
              borderTop: `4px solid ${LOGIN_PURPLE}`,
              animation: 'acm-spin 1.2s cubic-bezier(0.5, 0, 0.5, 1) infinite'
            }} />
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke={LOGIN_PURPLE} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ animation: 'acm-pulseStatus 2s infinite ease-in-out' }}>
              <rect x="2" y="2" width="20" height="8" rx="2" ry="2" />
              <rect x="2" y="14" width="20" height="8" rx="2" ry="2" />
              <line x1="6" y1="6" x2="6.01" y2="6" />
              <line x1="6" y1="18" x2="6.01" y2="18" />
            </svg>
          </div>

          <h2 style={{
            fontSize: isMobile ? '1.4rem' : '1.7rem',
            fontWeight: 800,
            color: '#1e1b4b',
            marginBottom: 12,
            textAlign: 'center',
            fontFamily: 'var(--font-headline, sans-serif)',
            letterSpacing: '-0.02em',
          }}>
            Inicializando Conexão
          </h2>

          <p style={{
            fontSize: isMobile ? '0.86rem' : '0.94rem',
            color: '#475569',
            textAlign: 'center',
            lineHeight: 1.6,
            maxWidth: 380,
            margin: '0 0 28px 0',
            fontWeight: 500,
          }}>
            Aguardando a inicialização completa do servidor de dados e canais de segurança. Por favor, aguarde alguns instantes...
          </p>

          {/* Status info bar */}
          <div style={{
            background: 'rgba(139, 92, 246, 0.05)',
            border: '1.5px dashed rgba(139, 92, 246, 0.2)',
            borderRadius: 16,
            padding: '14px 20px',
            width: '100%',
            maxWidth: 400,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 12,
            boxSizing: 'border-box',
          }}>
            <span style={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              background: '#f59e0b',
              boxShadow: '0 0 10px #f59e0b',
              animation: 'acm-pulseStatus 1.2s infinite ease-in-out'
            }} />
            <span style={{
              fontSize: '0.84rem',
              fontWeight: 650,
              color: '#4f46e5',
              letterSpacing: '0.01em'
            }}>
              Aguardando inicialização do servidor...
            </span>
          </div>
        </div>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════
  // Formulario de login reutilizavel
  // ═══════════════════════════════════════════════════
  function renderLoginForm() {
    //#region AC_BLOCK login.saved-accounts-layout -- Area dos cards salvos no desktop
    // @ac-purpose Ajusta densidade dos cards quando ha muitas contas salvas sem alterar o login por etapas.
    const totalProfileCards = savedAccounts.length + 1;
    const isCompactDesktopProfileGrid = !isMobile && totalProfileCards >= 6;
    const { width: desktopCardWidth, height: desktopCardHeight, scale: desktopScale } = getDesktopProfileCardMetrics(totalProfileCards, windowWidth, windowHeight);
    //#endregion AC_BLOCK login.saved-accounts-layout
    const cardWidth = isMobile ? 'calc(50% - 6px)' : desktopCardWidth;
    const cardHeight = isMobile ? 202 : desktopCardHeight;
    const displayCardWidth = typeof cardWidth === 'number' ? cardWidth + 30 : cardWidth;
    const displayCardHeight = typeof cardHeight === 'number' ? cardHeight + 50 : cardHeight;
    const avatarSize = isMobile ? 118 : Math.round(Math.min(130 * desktopScale, 150));
    const avatarFrameSize = isMobile ? 136 : Math.round(Math.min(156 * desktopScale, 180));
    const menuButtonSize = isMobile ? 30 : Math.round(32 * desktopScale);
    const menuIconSize = isMobile ? 16 : Math.round(16 * desktopScale);
    const passkeyBadgeIconSize = isMobile ? 36 : Math.round(28 * desktopScale);
    const cardPad = isMobile ? 12 : Math.round(12 * desktopScale);
    const roleBadgePadding = isMobile ? '3px 7px' : `${Math.round(3 * desktopScale)}px ${Math.round(7 * desktopScale)}px`;
    const roleBadgeFontSize = isMobile ? '0.94rem' : `${(0.92 * Math.min(desktopScale, 1.25)).toFixed(2)}rem`;
    const profileNameFontSize = isMobile ? '1.24rem' : `${(1.30 * Math.min(desktopScale, 1.35)).toFixed(2)}rem`;
    const profileEmailFontSize = isMobile ? '0.6rem' : `${(0.66 * Math.min(desktopScale, 1.2)).toFixed(2)}rem`;
    const gapSize = isMobile
      ? 12
      : isCompactDesktopProfileGrid
        ? Math.round(Math.min(Math.max(12 * desktopScale, 14), 18))
        : savedAccounts.length >= 4
          ? Math.round(Math.min(Math.max(16 * desktopScale, 20), 26))
          : Math.round(Math.min(Math.max(24 * desktopScale, 24), 40));
    const containerMaxWidth = '100%'; // Liberou a largura total no desktop e mobile
    const onlyAddAccountCard = savedAccounts.length === 0;
    const soloAddCardWidth = Math.round(Math.min(Math.max(windowWidth * 0.34, 480), 720));
    const soloAddCardHeight = Math.round(soloAddCardWidth * 1.12);

    const getRoleColor = (role: string) => {
      const r = (role || '').toLowerCase();
      if (r.includes('diretor') || r.includes('admin')) {
        return { bg: '#ef4444', text: '#ffffff', border: '#ef4444' }; // Red for Admin/Diretor
      }
      if (r.includes('professor')) {
        return { bg: LOGIN_PURPLE, text: '#ffffff', border: LOGIN_PURPLE };
      }
      if (r.includes('aluno')) {
        return { bg: '#10b981', text: '#ffffff', border: '#10b981' }; // Green
      }
      if (r.includes('secretaria') || r.includes('coordena')) {
        return { bg: '#f97316', text: '#ffffff', border: '#f97316' }; // Orange
      }
      return { bg: '#6366f1', text: '#ffffff', border: '#6366f1' }; // Indigo for Rede, Estadual, Municipal, Responsavel
    };

    // ── Grid de Perfis (Estilo Chrome) ──
    if (!showEmailForm) {


      if (onlyAddAccountCard && !isMobile) {
        const isSoloAddHovered = hoveredAvatar === 'add-account';
        return (
          <div style={{
            width: '100%',
            maxWidth: 'min(96vw, 980px)',
            minHeight: 'min(80vh, 860px)',
            margin: '0 auto',
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'stretch',
            justifyContent: 'center',
            padding: 0,
            boxSizing: 'border-box',
            animation: 'acm-fadeIn 0.5s ease',
          }}>
            <div
              style={{
                width: soloAddCardWidth,
                maxWidth: '100%',
                height: soloAddCardHeight,
                maxHeight: '80vh',
                alignSelf: 'center',
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                padding: 0,
                borderRadius: 24,
                overflow: 'hidden',
                background: LOGIN_PREMIUM_WHITE_CARD,
                border: isSoloAddHovered ? '1.5px solid rgba(167, 139, 250, 0.98)' : '1px solid rgba(255,255,255,0.92)',
                boxShadow: isSoloAddHovered
                  ? '0 38px 96px rgba(17, 7, 48, 0.62), 0 0 0 1px rgba(196,181,253,0.48), 0 0 48px rgba(139, 92, 246, 0.58), 0 0 24px rgba(34, 197, 94, 0.24)'
                  : '0 30px 78px rgba(17, 7, 48, 0.48), 0 0 58px rgba(139, 92, 246, 0.28)',
                transform: isSoloAddHovered ? 'translateY(-10px) scale(1.035)' : 'translateY(0) scale(1)',
                transition: 'transform 0.32s cubic-bezier(0.2, 0.9, 0.2, 1), box-shadow 0.32s ease, border-color 0.32s ease',
                zIndex: isSoloAddHovered ? 4 : 1,
              }}
              onMouseEnter={() => {
                setHoveredAvatar('add-account');
              }}
              onMouseLeave={() => {
                setHoveredAvatar(null);
              }}
              onClick={() => openEmailLogin(undefined, false, false, true)}
            >
              <LoginPairingQrCard
                variant="solo"
                isMobile={false}
                isHovered={isSoloAddHovered}
                cardWidthPx={soloAddCardWidth}
                cardHeightPx={soloAddCardHeight}
                onEmailFallback={() => openEmailLogin(undefined, false, false, true)}
                onPaired={handleLoginPairingPaired}
                onSessionChange={handleLoginPairingSessionChange}
              />
            </div>
          </div>
        );
      }

      return (
        <div style={{ width: '100%', maxWidth: containerMaxWidth, margin: '0 auto', position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', padding: 0, animation: 'acm-fadeIn 0.5s ease' }}>
          <div style={{
            display: 'flex',
            gap: gapSize,
            justifyContent: 'center',
            flexWrap: 'wrap',
            width: '100%',
            paddingBottom: isMobile ? 16 : 0,
          }}>
            {[
              ...savedAccounts.filter((account) => account.localStatus !== 'pending_security'),
              { id: '__add-account-card__', kind: 'add-account' },
              ...savedAccounts.filter((account) => account.localStatus === 'pending_security'),
            ].map((item) => {
              if ('kind' in item) {
                return (
                  <div
                    key="add-account"
                    style={{
                      flexShrink: 0,
                      position: 'relative',
                      width: displayCardWidth,
                      height: displayCardHeight,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 0,
                      cursor: 'pointer',
                      transition: 'all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1)',
                      padding: 0,
                      borderRadius: 16,
                      overflow: 'hidden',
                      background: LOGIN_PREMIUM_WHITE_CARD,
                      backdropFilter: 'blur(20px)',
                      border: '1.5px dashed',
                      borderColor: hoveredAvatar === 'add-account' ? 'rgba(139, 92, 246, 0.8)' : 'rgba(148, 163, 184, 0.45)',
                      boxShadow: hoveredAvatar === 'add-account'
                        ? '0 0 25px rgba(139, 92, 246, 0.45), 0 20px 40px rgba(139, 92, 246, 0.2)'
                        : '0 4px 12px rgba(0,0,0,0.03)',
                      transform: hoveredAvatar === 'add-account' ? 'translateY(-10px)' : 'none',
                    }}
                    onClick={() => {
                      openEmailLogin(undefined, false, false, true);
                    }}
                    onMouseEnter={() => !isMobile && setHoveredAvatar('add-account')}
                    onMouseLeave={() => !isMobile && setHoveredAvatar(null)}
                  >
                    <LoginPairingQrCard
                      isMobile={isMobile}
                      isHovered={hoveredAvatar === 'add-account'}
                      cardWidthPx={typeof displayCardWidth === 'number' ? displayCardWidth : undefined}
                      cardHeightPx={typeof displayCardHeight === 'number' ? displayCardHeight : undefined}
                      onEmailFallback={() => openEmailLogin(undefined, false, false, true)}
                      onPaired={handleLoginPairingPaired}
                      onSessionChange={handleLoginPairingSessionChange}
                    />
                  </div>
                );
              }
              const acc = item as SavedAccount;
              const isHovered = hoveredAvatar === acc.id;
              const isMenuOpen = activeAvatarMenu === acc.id;
              const isPendingSecurity = acc.localStatus === 'pending_security';
              return (
                <SavedAccountCard
                  key={acc.id}
                  account={acc}
                  isMobile={isMobile}
                  desktopScale={desktopScale}
                  cardWidth={displayCardWidth}
                  cardHeight={displayCardHeight}
                  isHovered={isHovered}
                  isMenuOpen={isMenuOpen}
                  onMouseEnter={() => !isMobile && setHoveredAvatar(acc.id)}
                  onMouseLeave={() => {
                    if (!isMobile) {
                      setHoveredAvatar(null);
                      setActiveAvatarMenu(null);
                    }
                  }}
                  onClick={() => {
                    if (acc.authMethod === 'google') {
                      handleGoogleLogin();
                    } else if (acc.hasPasskey && isPasskeyEnabled) {
                      handlePasskeyLogin(acc.email);
                    } else {
                      openEmailLogin(acc.email, true);
                    }
                  }}
                  onToggleMenu={(e) => {
                    e.stopPropagation();
                    setActiveAvatarMenu(isMenuOpen ? null : acc.id);
                  }}
                  isPasskeyEnabled={isPasskeyEnabled}
                  handleGoogleLogin={handleGoogleLogin}
                  handlePasskeyLogin={handlePasskeyLogin}
                  openEmailLogin={openEmailLogin}
                  verifyPasskeyBeforeAction={verifyPasskeyBeforeAction}
                  clearLocalPasskeyFlag={clearLocalPasskeyFlag}
                  removeSavedAccount={removeSavedAccount}
                  onCloseMenu={() => setActiveAvatarMenu(null)}
                />
              );
            })}

          </div>
        </div>
      );
    }
  }

  function renderEmailOverlay() {
    const currentAccount = savedAccounts.find(a => a.email.toLowerCase() === email.toLowerCase());
    const userAvatar = currentAccount?.avatar || '';
    const userInitials = email ? email.substring(0, 2).toUpperCase() : 'AC';
    const showAvatar = (cardMode === 'login' && emailStep > 1) || (cardMode !== 'login' && email);
    const normalizedOverlayEmail = email.trim().toLowerCase();
    const emailLooksValid = isValidLoginEmail(normalizedOverlayEmail);
    const currentEmailStatus = emailStatusEmail === normalizedOverlayEmail ? emailStatus : 'idle';
    const isAssuranceChecking = cardMode === 'login' && emailStep === 2 && currentEmailStatus === 'checking';
    const blockEmailAdvance =
      cardMode === 'login' &&
      emailStep === 1 &&
      (!emailLooksValid || currentEmailStatus !== 'exists');
    const isEmailStepChecking = cardMode === 'login' && emailStep === 1 && currentEmailStatus === 'checking';
    const isEmailActionStep = cardMode === 'login' && emailStep === 1;
    const isPasswordActionStep = cardMode === 'login' && emailStep === 2;
    const isFixedLoginActionStep = isEmailActionStep || isPasswordActionStep;
    const primaryButtonDisabled =
      isLoading ||
      (currentEmailStatus === 'checking' && !isEmailStepChecking) ||
      (blockEmailAdvance && !isEmailActionStep && !isEmailStepChecking) ||
      (isPasswordActionStep && password.length < 8) ||
      (cardMode === 'login' && emailStep === 4 && newPassword.length < 8) ||
      (cardMode === 'recover-reset' && recoverNewPassword.length < 8);
    const primaryButtonVisualDisabled = primaryButtonDisabled || isEmailStepChecking;
    const isGreenButton = isEmailActionStep && canSelfRegister && currentEmailStatus === 'not_found';
    const activeBtnBg = isGreenButton ? '#10b981' : LOGIN_PURPLE;
    const hoverBtnBg = isGreenButton ? '#059669' : LOGIN_PURPLE_HOVER;
    const disabledBtnBg = isGreenButton ? '#a7f3d0' : LOGIN_PURPLE_DISABLED;
    const showEmailClearAction = cardMode === 'login' && emailStep === 1 && email.length > 0;
    const emailInputPaddingRight = emailLooksValid && currentEmailStatus === 'exists' ? 54 : undefined;
    const isRecoveryStepMode =
      cardMode === 'recover-request' ||
      cardMode === 'recover-confirm-email' ||
      cardMode === 'recover-code' ||
      cardMode === 'recover-reset';
    const isForgotEmailStepMode =
      cardMode === 'forgot-email-cpf' ||
      cardMode === 'forgot-email-birth' ||
      cardMode === 'forgot-email-confirm';
    const recoveryHeaderTitle =
      cardMode === 'forgot-email-cpf'
        ? 'Encontrar e-mail'
        : cardMode === 'forgot-email-birth'
          ? 'Data de nascimento'
          : cardMode === 'forgot-email-confirm'
            ? 'Confirmar conta'
            : cardMode === 'recover-code'
              ? 'Digite o código enviado para e-mail'
              : cardMode === 'recover-reset'
                ? 'Definir nova senha'
                : cardMode === 'recover-confirm-email'
                  ? 'Confirmar envio'
                  : 'Recuperar senha';
    const recoveryHeaderSubtitle =
      cardMode === 'forgot-email-cpf'
        ? 'Informe o CPF vinculado a sua conta'
        : cardMode === 'forgot-email-birth'
          ? 'Confirme sua data de nascimento'
          : cardMode === 'forgot-email-confirm'
            ? 'Encontramos esta conta para voce'
            : cardMode === 'recover-code'
              ? (recoverEmailMasked || email)
              : cardMode === 'recover-reset'
                ? (isProvisionalPasswordFlow ? 'Senha provisória validada' : 'Crie uma senha segura')
                : cardMode === 'recover-confirm-email'
                  ? 'Vamos enviar um código para este e-mail'
                  : 'Informe seu e-mail cadastrado';
    const getLoginStepCardAnimation = (step: 1 | 2): React.CSSProperties['animation'] => {
      if (step === 2 && loginStepMotion === 'back-out') return 'acm-loginStepBackOut 0.18s ease-in both';
      if (step === 1 && loginStepMotion === 'back-in') return 'acm-loginStepBackIn 0.26s ease-out both';
      if (step === 2 && loginStepMotion === 'forward-in') return 'acm-loginStepForwardIn 0.26s ease-out both';
      return undefined;
    };
    const loginStepCardAnimation =
      cardMode === 'login' && (emailStep === 1 || emailStep === 2)
        ? getLoginStepCardAnimation(emailStep)
        : undefined;
    const mobileOverlayTop = 'calc(env(safe-area-inset-top, 0px) + 18px)';
    const canShowRecoveryDebugCode = cardMode === 'recover-code' && shouldShowTestAccess(window.location.hostname) && Boolean(recoveryDebugCode);
    const accountDisplayName =
      currentAccount?.name ||
      (normalizedOverlayEmail ? normalizedOverlayEmail.split('@')[0] : 'Conta identificada');

    const returnToEmailStep = () => {
      if (isLoading || isWaitingPushApproval) return;
      resetMobilePasswordStepHistory();

      const emailNormalized = email.trim().toLowerCase();
      const hasSavedAccount = savedAccounts.some(
        (acc) => acc.email?.trim().toLowerCase() === emailNormalized
      );

      if (hasSavedAccount) {
        closeEmailLogin();
        if (isMobile) {
          setMobileShowProfiles(true);
        }
        setEmail('');
        setPassword('');
        setError('');
        setSuccessMessage('');
        setEmailStatus('idle');
        return;
      }

      playLoginStepMotion('back-out', 180);
      if (isMobile) {
        try {
          window.history.replaceState({ acLoginStep: 'email' }, '', window.location.href);
        } catch {
          // Navegadores podem bloquear replaceState em contextos restritos.
        }
      }
      window.setTimeout(() => {
        emailInputRef.current?.setCustomValidity('');
        pendingEmailAdvanceRef.current = null;
        suppressEmailAutoAdvanceRef.current = false;
        setEmail('');
        setPassword('');
        setNewName('');
        setNewPassword('');
        setConfirmPassword('');
        setError('');
        setSuccessMessage('');
        setEmailStatus('idle');
        setEmailStatusEmail('');
        setServerHasPasskeys(false);
        setServerCanRequestMobileApproval(false);
        setEmailStep(1);
        playLoginStepMotion('back-in');
        window.setTimeout(() => {
          focusLoginInputForKeyboard(emailInputRef.current, { requestVirtualKeyboard: isMobile });
        }, 30);
      }, 170);
    };

    const returnRecoveryToPasswordStep = () => {
      if (isLoading || isWaitingPushApproval) return;
      setCardMode('login');
      setRecoverCode('');
      setRecoveryDebugCode('');
      setRecoverNewPassword('');
      setRecoverConfirmPassword('');
      setError('');
      setSuccessMessage('');
      if (email.trim()) {
        setEmailStep(2);
        window.setTimeout(() => {
          focusLoginInputForKeyboard(passwordInputRef.current, { requestVirtualKeyboard: isMobile });
        }, 30);
      } else {
        setEmailStep(1);
        window.setTimeout(() => {
          focusLoginInputForKeyboard(emailInputRef.current, { requestVirtualKeyboard: isMobile });
        }, 30);
      }
    };

    const returnForgotEmailToEmailStep = () => {
      if (isLoading || isWaitingPushApproval) return;
      clearForgotEmailLookup();
      setCardMode('login');
      setPassword('');
      setError('');
      setSuccessMessage('');
      setEmailStep(1);
      window.setTimeout(() => {
        focusLoginInputForKeyboard(emailInputRef.current, { requestVirtualKeyboard: isMobile });
      }, 30);
    };

    const returnForgotEmailStep = () => {
      if (isLoading || isWaitingPushApproval) return;
      if (cardMode === 'forgot-email-birth') {
        setCardMode('forgot-email-cpf');
        setForgotEmailLookup(null);
        setError('');
        setSuccessMessage('');
        window.setTimeout(() => {
          focusLoginInputForKeyboard(forgotEmailCpfInputRef.current, { requestVirtualKeyboard: isMobile });
        }, 30);
        return;
      }
      if (cardMode === 'forgot-email-confirm') {
        setCardMode('forgot-email-birth');
        setError('');
        setSuccessMessage('');
        window.setTimeout(() => {
          focusLoginInputForKeyboard(forgotEmailBirthInputRef.current, { requestVirtualKeyboard: isMobile });
        }, 30);
        return;
      }
      returnForgotEmailToEmailStep();
    };

    const focusEmailStepFromUserGesture = (target: EventTarget | null) => {
      if (cardMode !== 'login' || emailStep !== 1 || isLoading || isWaitingPushApproval) return;
      if (email.trim().length > 0) return;

      const targetElement = target instanceof Element ? target : null;
      if (targetElement?.closest('button,a,select,textarea,[role="button"]')) return;

      const input = emailInputRef.current;
      if (targetElement === input && document.activeElement === input) return;

      focusLoginInputForKeyboard(input, {
        requestVirtualKeyboard: isMobile,
      });
    };

    const clearEmailStepField = () => {
      emailInputRef.current?.setCustomValidity('');
      pendingEmailAdvanceRef.current = null;
      suppressEmailAutoAdvanceRef.current = false;
      setEmail('');
      setError('');
      setSuccessMessage('');
      setEmailStatus('idle');
      setEmailStatusEmail('');
      setServerHasPasskeys(false);
      setServerCanRequestMobileApproval(false);
    };

    const reportEmailInputValidity = () => {
      const input = emailInputRef.current;
      if (!input) return;
      input.setCustomValidity(email.trim() ? '' : 'Digite seu e-mail.');
      input.reportValidity();
    };

    const reportPasswordInputValidity = () => {
      const input = passwordInputRef.current;
      if (!input) return;
      input.setCustomValidity(password.trim() ? '' : 'Digite sua senha.');
      input.reportValidity();
    };

    const handleLoginEmailChange = (value: string) => {
      pendingEmailAdvanceRef.current = null;
      suppressEmailAutoAdvanceRef.current = false;
      setEmail(value);
      setError('');
      setSuccessMessage('');
    };

    const handleLoginPasswordChange = (value: string) => {
      hasTypedRef.current = true;
      setPassword(value);
      setError('');
      if (isWaitingPushApproval) {
        stopPushPolling();
      }
    };

    const handleRecoverPasswordClick = () => {
      setCardMode('recover-request');
      setPendingRecoveryEmail(email.trim().toLowerCase());
      setRecoverCode('');
      setRecoveryDebugCode('');
      setError('');
      setSuccessMessage('');
    };

    //#region AC_BLOCK login.mobile-brand-footer -- Rodape mobile de marca
    // @ac-purpose Exibe escudo e marca centralizados no mobile, ocultando quando o teclado reduz o viewport.
    // @ac-related docs_secutiry/execucao-lote-45-biometria-mobile-login-ux.md#Execucao-realizada
    // @ac-related react-app/src/components/security/AcShieldLogo.tsx
    // @ac-routes /login; /
    // @ac-tests node tools/codex-kit.mjs check react-tsc
    const mobileKeyboardBaselineHeight = Math.max(windowHeight, window.screen?.height ?? 0);
    const mobileKeyboardLikelyOpen =
      isMobile &&
      mobileKeyboardBaselineHeight - visualViewportHeight > 180 &&
      visualViewportHeight < mobileKeyboardBaselineHeight * 0.78;

    const mobileLoginBrandFooter = isMobile && !mobileKeyboardLikelyOpen ? (
      <div style={{
        position: 'absolute',
        left: 16,
        right: 16,
        top: '50%',
        bottom: isStandaloneApp ? 18 : 24,
        zIndex: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        pointerEvents: 'none',
        animation: 'acm-fadeIn 0.5s ease 0.08s both',
      }}>
        <div style={{
          flex: 1,
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 0,
        }}>
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            opacity: 0.98,
          }}>
            <AcShieldLogo style={{ width: 112, filter: 'drop-shadow(0 18px 38px rgba(124, 58, 237, 0.40))' }} />
            <div style={{
              display: 'flex',
              alignItems: 'baseline',
              gap: 8,
              marginTop: 14,
              lineHeight: 1,
              fontFamily: 'system-ui, -apple-system, sans-serif',
              whiteSpace: 'nowrap',
              textShadow: '0 12px 26px rgba(32, 11, 78, 0.22)',
            }}>
              <span style={{ color: '#ffffff', fontSize: '2rem', fontWeight: 900 }}>Aluno</span>
              <span style={{ color: '#8b5cf6', fontSize: '2rem', fontWeight: 900 }}>Conectado</span>
            </div>
            <div style={{
              marginTop: 12,
              color: '#ddd6fe',
              fontSize: '1rem',
              fontWeight: 700,
              letterSpacing: '0.02em',
              textShadow: '0 10px 24px rgba(32, 11, 78, 0.20)',
            }}>
              Configure seu novo perfil
            </div>
            <div style={{
              marginTop: 18,
              width: 210,
              height: 2,
              borderRadius: 2,
              background: 'rgba(255,255,255,0.08)',
              overflow: 'hidden',
            }}>
              <div style={{
                width: '100%',
                height: '100%',
                background: 'linear-gradient(90deg, transparent, #8b5cf6, #3b82f6, transparent)',
                animation: 'acm-lineGrow 1.2s ease 0.1s both',
              }} />
            </div>
          </div>
        </div>
      </div>
    ) : null;
    //#endregion AC_BLOCK login.mobile-brand-footer

    return (
      <div
        onDoubleClick={() => {
          if (!isLoading && !isWaitingPushApproval) {
            closeEmailLogin();
            setEmail('');
            setPassword('');
            setNewName('');
            setNewPassword('');
            setConfirmPassword('');
            setError('');
            setEmailStatus('idle');
            setEmailStep(1);
          }
        }}
        style={{
          position: 'fixed', inset: 0, zIndex: 99999,
          background: isMobile
            ? MOBILE_LOGIN_GRADIENT
            : 'rgba(0, 0, 0, 0.45)',
          backdropFilter: isMobile ? undefined : 'blur(12px)',
          display: 'flex',
          alignItems: isMobile ? 'flex-start' : 'center',
          justifyContent: 'center',
          animation: 'acm-fadeIn 0.3s ease',
          padding: isMobile ? `${mobileOverlayTop} 16px 18px` : 20,
          boxSizing: 'border-box',
          height: isMobile ? visualViewportHeight : undefined,
          minHeight: isMobile ? visualViewportHeight : undefined,
          overflow: isMobile ? 'hidden' : undefined,
          cursor: (isLoading || isWaitingPushApproval) ? 'default' : 'pointer'
        }}
      >
        <div
          className={isMobile ? 'ac-login-overlay-mobile' : undefined}
          onPointerDownCapture={(event) => focusEmailStepFromUserGesture(event.target)}
          onDoubleClick={(e) => e.stopPropagation()}
          style={{
            background: LOGIN_PREMIUM_PANEL_BG,
            borderRadius: isMobile ? 24 : 24,
            padding: isMobile ? '22px 20px 20px' : '36px 36px 40px',
            width: '100%',
            maxWidth: isMobile ? 440 : 450,
            border: LOGIN_PREMIUM_PANEL_BORDER,
            boxShadow: LOGIN_PREMIUM_PANEL_SHADOW,
            backdropFilter: 'blur(22px)',
            WebkitBackdropFilter: 'blur(22px)',
            display: 'flex', flexDirection: 'column',
            position: 'relative',
            zIndex: 2,
            overflow: 'visible',
            transform: undefined,
            transition: isMobile ? 'transform 0.18s ease' : undefined,
            animation: loginStepCardAnimation,
            transformOrigin: 'center top',
            willChange: loginStepCardAnimation ? 'transform, opacity' : undefined,
            colorScheme: isMobile ? 'dark' : undefined,
            cursor: 'default'
          }}
        >

          <button
            type="button"
            disabled={isLoading || isWaitingPushApproval}
            onClick={() => {
              if (isLoading || isWaitingPushApproval) return;
              if (cardMode !== 'login') {
                setCardMode('login');
                setError('');
                setSuccessMessage('');
              } else if (emailStep > 1) {
                returnToEmailStep();
              } else {
                closeEmailLogin();
                setEmail('');
                setPassword('');
                setNewName('');
                setNewPassword('');
                setConfirmPassword('');
                setError('');
                setEmailStatus('idle');
                setEmailStep(1);
              }
            }}
            style={{
              position: 'absolute', top: 24, left: 24,
              display: isEmailActionStep || isPasswordActionStep || isRecoveryStepMode || isForgotEmailStepMode ? 'none' : 'flex',
              background: LOGIN_PURPLE, border: 'none',
              cursor: (isLoading || isWaitingPushApproval) ? 'not-allowed' : 'pointer',
              color: '#ffffff', alignItems: 'center', justifyContent: 'center',
              padding: 0, borderRadius: '50%',
              width: 40,
              height: 40,
              boxShadow: `0 10px 24px ${LOGIN_PURPLE_SHADOW}`,
              opacity: (isLoading || isWaitingPushApproval) ? 0.35 : 1,
              transition: 'all 0.2s'
            }}
            onMouseEnter={e => { if (!isLoading && !isWaitingPushApproval) e.currentTarget.style.background = LOGIN_PURPLE_HOVER; }}
            onMouseLeave={e => { e.currentTarget.style.background = LOGIN_PURPLE; }}
          >
            <ArrowLeft size={20} />
          </button>

          {cardMode === 'login' && emailStep === 1 ? (
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: isMobile ? 18 : 28 }}>
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                flexDirection: isMobile ? 'row' : 'column',
                gap: isMobile ? 12 : 14,
                textAlign: isMobile ? 'left' : 'center',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Mail size={isMobile ? 44 : 48} color="#ffffff" style={{ opacity: 0.8 }} />
                </div>
                <div>
                  <h2 style={{
                    fontSize: isMobile ? '1.28rem' : '1.46rem',
                    fontWeight: 650,
                    color: '#ffffff',
                    margin: 0,
                    lineHeight: 1.1,
                    fontFamily: 'system-ui, -apple-system, sans-serif',
                  }}>
                    Identificar conta
                  </h2>
                </div>
              </div>
            </div>
          ) : cardMode === 'login' && emailStep === 2 ? (
            <div style={{
              marginBottom: isMobile ? 18 : 28,
              animation: 'acm-fadeIn 0.3s ease',
              textAlign: 'center',
            }}>
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: isMobile ? 'row' : 'column',
                gap: isMobile ? 12 : 14,
                maxWidth: '100%',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Lock size={isMobile ? 42 : 48} color="#ffffff" style={{ opacity: 0.8 }} />
                </div>
                <div style={{ minWidth: 0, textAlign: isMobile ? 'left' : 'center' }}>
                  <h2 style={{
                    fontSize: isMobile ? '1.24rem' : '1.5rem',
                    fontWeight: 650,
                    color: '#ffffff',
                    margin: 0,
                    lineHeight: 1.1,
                    fontFamily: 'system-ui, -apple-system, sans-serif',
                    whiteSpace: 'nowrap',
                  }}>
                    Digite sua senha
                  </h2>
                  <div style={{
                    color: '#a78bfa',
                    opacity: 0.7,
                    fontSize: isMobile ? '0.86rem' : '0.92rem',
                    fontWeight: 650,
                    lineHeight: 1.25,
                    marginTop: 6,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: isMobile ? 240 : 340,
                  }}>
                    {email}
                  </div>
                </div>
              </div>
            </div>
          ) : (isRecoveryStepMode || isForgotEmailStepMode) ? (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              marginBottom: isMobile ? 18 : 28,
              animation: 'acm-fadeIn 0.3s ease',
            }}>
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                flexDirection: isMobile ? 'row' : 'column',
                gap: isMobile ? 12 : 14,
                textAlign: isMobile ? 'left' : 'center',
                minWidth: 0,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  {cardMode === 'recover-reset' ? (
                    <Lock size={isMobile ? 44 : 48} color="#ffffff" style={{ opacity: 0.8 }} />
                  ) : (
                    <Mail size={isMobile ? 44 : 48} color="#ffffff" style={{ opacity: 0.8 }} />
                  )}
                </div>
                <div style={{ minWidth: 0, textAlign: isMobile ? 'left' : 'center' }}>
                  <h2 style={{
                    fontSize: isMobile ? '1.24rem' : '1.5rem',
                    fontWeight: 650,
                    color: '#ffffff',
                    margin: 0,
                    lineHeight: 1.1,
                    fontFamily: 'system-ui, -apple-system, sans-serif',
                    whiteSpace: 'nowrap',
                  }}>
                    {recoveryHeaderTitle}
                  </h2>
                  <div style={{
                    color: '#ddd6fe',
                    fontSize: '0.86rem',
                    fontWeight: 650,
                    lineHeight: 1.25,
                    marginTop: 6,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: isMobile ? 240 : 340,
                  }}>
                    {recoveryHeaderSubtitle}
                  </div>
                </div>
              </div>
            </div>
          ) : isMobile && cardMode === 'login' && emailStep === 2 ? (
            <div style={{ marginBottom: 16, animation: 'acm-fadeIn 0.3s ease' }}>
              <div style={{
                display: 'grid',
                gridTemplateColumns: '40px minmax(0, 1fr) 40px',
                alignItems: 'center',
                gap: 10,
                marginBottom: 12,
              }}>
                <button
                  type="button"
                  aria-label="Voltar para alterar e-mail"
                  disabled={isLoading || isWaitingPushApproval}
                  onClick={returnToEmailStep}
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    border: '1px solid rgba(221,214,254,0.28)',
                    background: LOGIN_PURPLE,
                    color: '#ffffff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: `0 10px 24px ${LOGIN_PURPLE_SHADOW}`,
                    opacity: (isLoading || isWaitingPushApproval) ? 0.45 : 1,
                    cursor: (isLoading || isWaitingPushApproval) ? 'not-allowed' : 'pointer',
                    padding: 0,
                  }}
                >
                  <ArrowLeft size={20} />
                </button>
                <div style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 10,
                  minWidth: 0,
                }}>
                  <div style={{
                    width: 42,
                    height: 42,
                    borderRadius: '50%',
                    background: 'rgba(255, 255, 255, 0.14)',
                    border: '1px solid rgba(221, 214, 254, 0.24)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}>
                    <Lock size={21} color="#ffffff" />
                  </div>
                  <h2 style={{
                    fontSize: '1.22rem',
                    fontWeight: 650,
                    color: '#ffffff',
                    margin: 0,
                    lineHeight: 1.1,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}>
                    Digite sua senha
                  </h2>
                </div>
                <div aria-hidden="true" />
              </div>

              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '12px 14px',
                borderRadius: 16,
                background: LOGIN_PREMIUM_INSET_BG,
                border: '1px solid rgba(221, 214, 254, 0.18)',
                boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.10)',
                minWidth: 0,
              }}>
                {currentAccount?.avatar ? (
                  <img
                    src={currentAccount.avatar}
                    alt={accountDisplayName}
                    style={{
                      width: 42,
                      height: 42,
                      borderRadius: '50%',
                      objectFit: 'cover',
                      border: '2px solid rgba(221,214,254,0.28)',
                      flexShrink: 0,
                    }}
                  />
                ) : (
                  <div style={{
                    width: 42,
                    height: 42,
                    borderRadius: '50%',
                    background: LOGIN_PURPLE,
                    color: '#ffffff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 800,
                    fontSize: '0.95rem',
                    flexShrink: 0,
                  }}>
                    {userInitials}
                  </div>
                )}
                <div style={{ minWidth: 0, textAlign: 'left', flex: 1 }}>
                  <div style={{
                    color: '#ffffff',
                    fontWeight: 800,
                    fontSize: '0.98rem',
                    lineHeight: 1.2,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}>
                    {accountDisplayName}
                  </div>
                  <button
                    type="button"
                    onClick={returnToEmailStep}
                    style={{
                      margin: '4px 0 0',
                      padding: 0,
                      border: 'none',
                      background: 'transparent',
                      color: '#ddd6fe',
                      cursor: 'pointer',
                      fontSize: '0.8rem',
                      fontWeight: 650,
                      maxWidth: '100%',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      textDecoration: 'underline',
                      textUnderlineOffset: 3,
                    }}
                  >
                    {email}
                  </button>
                </div>
              </div>
            </div>
          ) : cardMode === 'login' && emailStep === 2 && currentAccount ? (
            <div style={{
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              alignItems: 'center',
              gap: isMobile ? 12 : 16,
              padding: isMobile ? '12px' : '16px 20px',
              borderRadius: 16,
              background: LOGIN_PREMIUM_INSET_BG,
              border: '1px solid rgba(221, 214, 254, 0.18)',
              marginTop: isMobile ? 42 : 20,
              marginBottom: isMobile ? 16 : 24,
              animation: 'acm-fadeIn 0.3s ease',
              textAlign: isMobile ? 'center' : 'left'
            }}>
              {/* Foto do Perfil (Mario) */}
              <img
                src={userAvatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(currentAccount.name)}&background=random&color=fff`}
                alt={currentAccount.name}
                style={{
                  width: isMobile ? 64 : 72,
                  height: isMobile ? 64 : 72,
                  borderRadius: '50%',
                  border: '2px solid rgba(221,214,254,0.28)',
                  boxShadow: '0 8px 22px rgba(17,7,45,0.34)',
                  objectFit: 'cover',
                  flexShrink: 0
                }}
              />
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: isMobile ? 'center' : 'flex-start',
                minWidth: 0,
                flexGrow: 1
              }}>
                {/* Nome do Perfil */}
                <div style={{
                  color: '#ffffff',
                  fontWeight: 700,
                  fontSize: isMobile ? '1.05rem' : '1.15rem',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  lineHeight: 1.2,
                  maxWidth: isMobile ? 220 : '100%',
                  textAlign: isMobile ? 'center' : 'left'
                }}>
                  {currentAccount.name}
                </div>

                {/* Badge do Perfil (Role) */}
                <div style={{ marginTop: 4, display: 'flex', justifyContent: isMobile ? 'center' : 'flex-start', width: '100%' }}>
                  <span style={{
                    display: 'inline-block',
                    padding: '2px 8px',
                    borderRadius: 8,
                    background: currentAccount.role.toLowerCase().includes('admin') || currentAccount.role.toLowerCase().includes('diretor') ? '#ef4444' : LOGIN_PURPLE,
                    color: '#ffffff',
                    fontSize: '0.62rem',
                    fontWeight: 800,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em'
                  }}>
                    {currentAccount.role}
                  </span>
                </div>

                {/* E-mail / Seletor clicável para voltar ao step 1 */}
                <div
                  onClick={() => { setEmailStep(1); setPassword(''); setNewName(''); setNewPassword(''); setConfirmPassword(''); setEmailStatus('idle'); }}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: isMobile ? 'center' : 'flex-start',
                    gap: 4,
                    marginTop: 6,
                    color: '#ddd6fe',
                    fontSize: '0.78rem',
                    cursor: 'pointer',
                    width: isMobile ? '100%' : 'fit-content'
                  }}
                  onMouseEnter={e => e.currentTarget.style.color = LOGIN_PURPLE}
                  onMouseLeave={e => e.currentTarget.style.color = '#ddd6fe'}
                >
                  <span style={{ textDecoration: 'underline', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: isMobile ? 220 : 180 }}>
                    {email}
                  </span>
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="m6 9 6 6 6-6" /></svg>
                </div>
              </div>
            </div>
          ) : (
            <>
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: isMobile ? 8 : 20 }}>
                {!showAvatar ? (
                  <div style={{
                    background: 'rgba(255, 255, 255, 0.14)',
                    padding: 12,
                    borderRadius: '50%',
                    border: '1px solid rgba(221, 214, 254, 0.24)',
                  }}>
                    <Mail size={24} color="#ffffff" />
                  </div>
                ) : (
                  <div style={{ position: 'relative' }}>
                    {userAvatar ? (
                      <img
                        src={userAvatar}
                        alt="Avatar"
                        style={{ width: 64, height: 64, borderRadius: '50%', border: '2px solid rgba(0,0,0,0.05)', boxShadow: '0 4px 12px rgba(0,0,0,0.08)', objectFit: 'cover' }}
                      />
                    ) : (
                      <div style={{
                        background: LOGIN_PURPLE, color: 'white', width: 64, height: 64, borderRadius: '50%',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontWeight: 700, fontSize: '1.25rem', boxShadow: `0 4px 12px ${LOGIN_PURPLE_SHADOW}`
                      }}>
                        {userInitials}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div style={{ textAlign: 'center', marginBottom: isMobile ? 12 : 32 }}>
                <h2 style={{
                  display: isMobile && cardMode === 'login' && emailStep === 2 ? 'none' : undefined,
                  fontSize: isMobile ? '1.25rem' : '1.5rem',
                  fontWeight: 400,
                  color: '#ffffff',
                  margin: isMobile ? '0' : '0 0 8px 0',
                  fontFamily: 'system-ui, -apple-system, sans-serif',
                }}>
                  {cardMode === 'login' ? (
                    emailStep === 1 ? 'Fazer login' : (emailStep === 3 ? 'Criar sua conta' : (emailStep === 4 ? 'Defina sua senha' : (emailStep === 5 ? 'Mais segurança' : 'Olá!')))
                  ) : cardMode === 'recover-request' ? (
                    'Recuperar senha'
                  ) : cardMode === 'recover-confirm-email' ? (
                    'Confirmar envio'
                  ) : cardMode === 'recover-code' ? (
                    'Digite o código enviado para e-mail'
                  ) : cardMode === 'recover-reset' ? (
                    'Definir nova senha'
                  ) : cardMode === 'forgot-email-cpf' ? (
                    'Encontrar e-mail'
                  ) : cardMode === 'forgot-email-birth' ? (
                    'Data de nascimento'
                  ) : cardMode === 'forgot-email-confirm' ? (
                    'Confirmar conta'
                  ) : 'Olá!'}
                </h2>
                {cardMode === 'login' ? (
                  emailStep === 1 ? (
                    null
                  ) : (
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '4px 8px 4px 12px', border: '1px solid rgba(221,214,254,0.24)', borderRadius: 16, cursor: 'pointer', transition: 'background 0.2s', background: 'rgba(255,255,255,0.12)' }} onClick={() => { setEmailStep(1); setPassword(''); setNewName(''); setNewPassword(''); setConfirmPassword(''); setEmailStatus('idle'); }} onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.18)'} onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.12)'}>
                      <span style={{ color: '#ffffff', fontSize: '0.85rem', fontWeight: 600 }}>{email}</span>
                      <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 20, height: 20, borderRadius: '50%', background: 'transparent' }}>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2"><path d="m6 9 6 6 6-6" /></svg>
                      </span>
                    </div>
                  )
                ) : cardMode === 'recover-request' ? (
                  <p style={{ fontSize: '1.05rem', color: '#64748b', margin: 0 }}>Informe seu e-mail para receber o código de recuperação</p>
                ) : cardMode === 'recover-confirm-email' ? (
                  <p style={{ fontSize: '1.05rem', color: '#64748b', margin: 0 }}>Vamos enviar um código para <strong style={{ color: '#1e293b' }}>{pendingRecoveryEmail || email}</strong></p>
                ) : cardMode === 'recover-code' ? (
                  <p style={{ fontSize: '1.05rem', color: '#64748b', margin: 0 }}>Enviamos um código para o e-mail: <strong style={{ color: '#1e293b' }}>{recoverEmailMasked || email}</strong></p>
                ) : cardMode === 'recover-reset' ? (
                  <p style={{ fontSize: '1.05rem', color: '#64748b', margin: 0 }}>
                    {isProvisionalPasswordFlow
                      ? 'Sua senha provisória foi autenticada. Defina uma nova senha permanente.'
                      : 'Crie uma nova senha forte para acessar sua conta.'}
                  </p>
                ) : cardMode === 'forgot-email-cpf' ? (
                  <p style={{ fontSize: '1.05rem', color: '#64748b', margin: 0 }}>Informe o CPF vinculado ao seu cadastro</p>
                ) : cardMode === 'forgot-email-birth' ? (
                  <p style={{ fontSize: '1.05rem', color: '#64748b', margin: 0 }}>Confirme sua data de nascimento</p>
                ) : cardMode === 'forgot-email-confirm' ? (
                  <p style={{ fontSize: '1.05rem', color: '#64748b', margin: 0 }}>Confira se esta é a sua conta</p>
                ) : null}
              </div>
            </>
          )}

          <form onSubmit={async (e) => {
            e.preventDefault();
            if (cardMode === 'recover-request') {
              await handleRequestRecovery();
            } else if (cardMode === 'recover-confirm-email') {
              await handleConfirmEmailRecovery();
            } else if (cardMode === 'recover-code') {
              await handleVerifyRecoveryCode();
            } else if (cardMode === 'recover-reset') {
              if (isProvisionalPasswordFlow) {
                if (recoverNewPassword.length < 8) {
                  setError('A nova senha deve ter pelo menos 8 caracteres.');
                  return;
                }
                if (recoverNewPassword !== recoverConfirmPassword) {
                  setError('As senhas não conferem.');
                  return;
                }
                setIsLoading(true);
                setError('');
                try {
                  const { data } = await api.post(
                    '/auth/change-provisional-password',
                    { novaSenha: recoverNewPassword },
                    {
                      headers: {
                        Authorization: `Bearer ${provisionalToken}`,
                      },
                    }
                  );
                  const userData = {
                    ...data.user,
                    role: data.user.perfil,
                    roles: [data.user.perfil],
                    acessoDireto: data.user.acessoDireto ?? false,
                    statusConta: data.user.statusConta,
                  };
                  saveAccountToLocal(userData, 'email');
                  login(data.access_token, userData);
                  sessionStorage.setItem('ac_auth_method', 'password');
                  if (data.refresh_token) {
                    localStorage.setItem('ac_refresh_token', data.refresh_token);
                  }

                  setIsProvisionalPasswordFlow(false);
                  setProvisionalToken('');
                  sessionStorage.removeItem('ac_provisional_token');
                  setCardMode('login');
                  closeEmailLogin();

                  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || window.innerWidth < 768;
                  let pendingSteps = data.pendingSecuritySteps || [];
                  if (isMobile && !pendingSteps.includes('passkey')) {
                    pendingSteps = ['passkey', ...pendingSteps];
                  }

                  if (pendingSteps.length > 0) {
                    saveLocalAccountShortcut(userData, 'pending_security');
                    sessionStorage.setItem('ac_pending_security', JSON.stringify(pendingSteps));
                    navigate('/security-compliance', { replace: true });
                    return;
                  }

                  await useAuthStore.getState().checkAuth();
                  const redirectPath = sessionStorage.getItem('ac_redirect_after_login');
                  const dashRoute = redirectPath || getRedirectPath(userData.perfil || '', userData.cargo || undefined);
                  sessionStorage.removeItem('ac_redirect_after_login');
                  navigate(dashRoute, { replace: true });
                } catch (err: any) {
                  const msg = err.response?.data?.message || err.response?.data?.error?.message || 'Não foi possível atualizar a senha.';
                  setError(msg);
                } finally {
                  setIsLoading(false);
                }
              } else {
                await handleResetPassword();
              }
            } else if (cardMode === 'forgot-email-cpf') {
              handleForgotEmailCpfAdvance();
            } else if (cardMode === 'forgot-email-birth') {
              await handleForgotEmailLookup();
            } else if (cardMode === 'forgot-email-confirm') {
              await confirmForgotEmailLookup();
            } else if (cardMode === 'login') {
              if (emailStep === 1) {
                if (!emailLooksValid) {
                  reportEmailInputValidity();
                  setError('');
                  return;
                  setError('Informe um e-mail válido para continuar.');
                  if (!email.trim()) {
                    setError('');
                  }
                  return;
                }
                emailInputRef.current?.setCustomValidity('');
                if (currentEmailStatus === 'checking') {
                  requestEmailAdvanceAfterCheck(email);
                  return;
                }
                if (String(currentEmailStatus) !== 'exists') {
                  const verifiedStatus = await checkEmailInstant(email, true);
                  if (verifiedStatus === 'exists') {
                    suppressEmailAutoAdvanceRef.current = false;
                    goToPasswordStep();
                    setError('');
                  } else if (verifiedStatus === 'not_found') {
                    if (canSelfRegister) {
                      await handleStartSelfRegistration(email);
                    } else {
                      setError(EMAIL_NOT_AUTHORIZED_MESSAGE);
                    }
                  } else {
                    setError(EMAIL_LOCAL_API_UNAVAILABLE_MESSAGE);
                  }
                  return;
                }
                if (currentEmailStatus === 'exists') {
                  suppressEmailAutoAdvanceRef.current = false;
                  goToPasswordStep();
                  setError('');
                } else if (currentEmailStatus === 'api_unavailable') {
                  setError(
                    'Nao foi possivel falar com a API local. Inicie o backend (porta 3334), o Postgres (Docker) e abra o app pelo Vite (ex. :3443) para o proxy /api/v1 funcionar.',
                  );
                } else if (currentEmailStatus === 'not_found') {
                  if (canSelfRegister) {
                    await handleStartSelfRegistration(email);
                  } else {
                    setError(
                      'Este e-mail ainda não está autorizado para login. Entre em contato com nossa equipe para apresentarmos o produto e enviarmos um convite oficial de acesso.',
                    );
                  }
                } else {
                  setError('Aguarde a validação do e-mail antes de continuar.');
                }
              } else if (emailStep === 3) {
                if (!newName.trim()) {
                  setError('Por favor, informe seu nome.');
                  return;
                }
                setEmailStep(4);
                setError('');
              } else if (emailStep === 4) {
                if (!newPassword || newPassword.length < 8) {
                  setError('A senha deve ter no mínimo 8 caracteres.');
                  return;
                }
                if (newPassword !== confirmPassword) {
                  setError('As senhas não coincidem.');
                  return;
                }
                setIsLoading(true);
                setError('');
                const API = import.meta.env.VITE_API_URL || '/api/v1';
                try {
                  const res = await fetch(`${API}/auth/register-public`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ nome: newName.trim(), email: email.trim(), senha: newPassword }),
                  });
                  if (!res.ok) {
                    const errData = await res.json().catch(() => ({}));
                    throw new Error(errData.message || 'Erro ao criar conta.');
                  }
                  const data = await res.json();
                  const userData = { ...data.user, role: data.user.perfil, roles: [data.user.perfil] };
                  if (data.refresh_token) localStorage.setItem('ac_refresh_token', data.refresh_token);
                  saveAccountToLocal(userData, 'email');
                  login(data.access_token, userData);
                  sessionStorage.setItem('ac_auth_method', 'password');
                  const dashRoute = getRedirectPath(userData.perfil || '', userData.cargo || undefined);
                  setTempAuthData(null);
                  navigate(dashRoute, { replace: true });
                } catch (err: any) {
                  setError(err.message || 'Erro ao criar conta.');
                } finally {
                  setIsLoading(false);
                }
                return;
              } else if (emailStep === 5) {
                return;
              } else {
                handleLogin(e);
              }
            }
          }} autoComplete="on" style={{
            display: 'flex',
            flexDirection: 'column',
            flex: isMobile ? '0 0 auto' : 1,
            justifyContent: isMobile ? 'flex-start' : 'space-between',
          }}>

            <div style={{ minHeight: isMobile ? 0 : 80 }}>
              {cardMode === 'login' && emailStep === 1 && (
                <LoginEmailStep
                  emailInputRef={emailInputRef}
                  email={email}
                  onEmailChange={handleLoginEmailChange}
                  containerStyle={{ margin: isMobile ? 0 : undefined }}
                  loginStepErrorCard={loginStepErrorCard}
                  showLoginStepErrorCard={showLoginStepErrorCard}
                  showLegacyInlineLoginError={showLegacyInlineLoginError}
                  error={error}
                  overlayInputStyle={overlayInputStyle}
                  loginFieldShellStyle={loginFieldShellStyle}
                  emailInputPaddingRight={emailInputPaddingRight}
                  emailLooksValid={emailLooksValid}
                  currentEmailStatus={currentEmailStatus}
                  onForgotEmailLookup={startForgotEmailLookup}
                />
              )}

              {cardMode === 'login' && emailStep === 2 && (
                <LoginPasswordStep
                  passwordInputRef={passwordInputRef}
                  password={password}
                  onPasswordChange={handleLoginPasswordChange}
                  containerStyle={{ margin: isMobile ? 0 : undefined }}
                  showPassword={showPassword}
                  onToggleShowPassword={() => setShowPassword((value) => !value)}
                  capsLockOn={capsLockOn}
                  onCapsLockChange={(value) => setCapsLockOn(value)}
                  isAssuranceChecking={isAssuranceChecking}
                  loginStepErrorCard={loginStepErrorCard}
                  showLoginStepErrorCard={showLoginStepErrorCard}
                  showLegacyInlineLoginError={showLegacyInlineLoginError}
                  error={error}
                  overlayInputStyle={overlayInputStyle}
                  loginFieldShellStyle={loginFieldShellStyle}
                  onInputFocus={handleOverlayInputFocus}
                  onInputBlur={handleOverlayInputBlur}
                  onRecoverPassword={handleRecoverPasswordClick}
                />
              )}

              {/*#region AC_BLOCK login.forgot-email-ui -- UI para recuperar e-mail*/}
              {/* @ac-purpose Renderiza o fluxo de descoberta do e-mail por CPF/data sem autenticar nem redefinir senha. */}
              {/* @ac-related backend/src/auth/auth.controller.ts#lookupAccountEmailByCpfBirth */}
              {/* @ac-related react-app/src/api/routes.ts#auth.accountLookup */}
              {/* @ac-routes /login; POST /auth/account-lookup/by-cpf-birth */}
              {/* @ac-tests node tools/codex-kit.mjs check react-tsc */}
              {cardMode === 'forgot-email-cpf' && (
                <div style={{ position: 'relative', animation: 'acm-slideRight 0.3s ease' }}>
                  {loginStepErrorCard}
                  <div style={loginFieldShellStyle} aria-hidden={showLoginStepErrorCard}>
                    <input
                      ref={forgotEmailCpfInputRef}
                      type="text" placeholder="CPF" name="forgot-email-cpf"
                      inputMode="numeric" autoComplete="off"
                      required value={forgotEmailCpf} autoFocus
                      onChange={(e) => {
                        e.currentTarget.setCustomValidity('');
                        setForgotEmailCpf(formatCpfForLoginLookup(e.target.value));
                        setError('');
                      }}
                      style={overlayInputStyle}
                      onFocus={handleOverlayInputFocus}
                      onBlur={handleOverlayInputBlur}
                    />
                  </div>
                  {showLegacyInlineLoginError && !showLoginStepErrorCard && error && (
                    <div style={overlayErrorStyle}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" /></svg>
                      {error}
                    </div>
                  )}
                </div>
              )}

              {cardMode === 'forgot-email-birth' && (
                <div style={{ position: 'relative', animation: 'acm-slideRight 0.3s ease' }}>
                  {loginStepErrorCard}
                  <div style={loginFieldShellStyle} aria-hidden={showLoginStepErrorCard}>
                    <input
                      ref={forgotEmailBirthInputRef}
                      type="text" placeholder="00/00/0000" name="forgot-email-birth"
                      inputMode="numeric" autoComplete="bday"
                      required value={forgotEmailBirthDate} autoFocus
                      maxLength={10}
                      onChange={(e) => {
                        e.currentTarget.setCustomValidity('');
                        setForgotEmailBirthDate(formatBirthDateForLoginLookup(e.target.value));
                        setError('');
                      }}
                      style={overlayInputStyle}
                      onFocus={handleOverlayInputFocus}
                      onBlur={handleOverlayInputBlur}
                    />
                  </div>
                  {showLegacyInlineLoginError && !showLoginStepErrorCard && error && (
                    <div style={overlayErrorStyle}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" /></svg>
                      {error}
                    </div>
                  )}
                </div>
              )}

              {cardMode === 'forgot-email-confirm' && (
                <div style={{ position: 'relative', animation: 'acm-slideRight 0.3s ease' }}>
                  {loginStepErrorCard}
                  <div style={loginFieldShellStyle} aria-hidden={showLoginStepErrorCard}>
                    <div style={{
                      background: '#ffffff',
                      borderRadius: 14,
                      border: `1.5px solid ${LOGIN_PURPLE_BORDER}`,
                      boxShadow: overlayIsMobile ? '0 8px 18px rgba(15, 23, 42, 0.06)' : undefined,
                      padding: '16px 16px',
                      color: '#0f172a',
                    }}>
                      <div style={{ fontSize: '0.82rem', fontWeight: 700, color: '#64748b', marginBottom: 6 }}>
                        Conta encontrada
                      </div>
                      <div style={{ fontSize: '1.02rem', fontWeight: 800, color: '#111827', wordBreak: 'break-word' }}>
                        {forgotEmailLookup?.nome || 'Conta'}
                      </div>
                      <div style={{ fontSize: '0.95rem', fontWeight: 650, color: LOGIN_PURPLE_HOVER, wordBreak: 'break-word', marginTop: 4 }}>
                        {forgotEmailLookup?.email}
                      </div>
                      <div style={{ fontSize: '0.8rem', color: '#64748b', lineHeight: 1.4, marginTop: 8 }}>
                        Ao confirmar, este e-mail sera preenchido no login e voce seguira para a senha.
                      </div>
                    </div>
                  </div>
                  {showLegacyInlineLoginError && !showLoginStepErrorCard && error && (
                    <div style={overlayErrorStyle}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" /></svg>
                      {error}
                    </div>
                  )}
                </div>
              )}
              {/*#endregion AC_BLOCK login.forgot-email-ui*/}

              {cardMode === 'login' && emailStep === 3 && (
                <div style={{ position: 'relative', animation: 'acm-slideRight 0.3s ease' }}>
                  {loginStepErrorCard}
                  <input
                    type="text" id="name-google" placeholder="Como devemos chamar você?"
                    autoComplete="name" required value={newName} autoFocus
                    onChange={(e) => { setNewName(e.target.value); setError(''); }}
                    style={overlayInputStyle}
                    onFocus={handleOverlayInputFocus}
                    onBlur={handleOverlayInputBlur}
                  />
                  {showLegacyInlineLoginError && !showLoginStepErrorCard && error && (
                    <div style={overlayErrorStyle}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" /></svg>
                      {error}
                    </div>
                  )}
                </div>
              )}

              {cardMode === 'login' && emailStep === 4 && (
                <div style={{ position: 'relative', animation: 'acm-slideRight 0.3s ease', display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {loginStepErrorCard}
                  <input
                    type="password" id="new-password" placeholder="Senha (mín. 8 caracteres)" name="new-password"
                    autoComplete="new-password" required value={newPassword} autoFocus
                    onKeyUp={(e) => setCapsLockOn(e.getModifierState('CapsLock'))}
                    onKeyDown={(e) => setCapsLockOn(e.getModifierState('CapsLock'))}
                    onChange={(e) => { setNewPassword(e.target.value); setError(''); }}
                    style={overlayInputStyle}
                    onFocus={handleOverlayInputFocus}
                    onBlur={handleOverlayInputBlur}
                  />
                  <input
                    type="password" id="confirm-password" placeholder="Confirmar senha" name="confirm-password"
                    autoComplete="new-password" required value={confirmPassword}
                    onKeyUp={(e) => setCapsLockOn(e.getModifierState('CapsLock'))}
                    onKeyDown={(e) => setCapsLockOn(e.getModifierState('CapsLock'))}
                    onChange={(e) => { setConfirmPassword(e.target.value); setError(''); }}
                    style={overlayInputStyle}
                    onFocus={handleOverlayInputFocus}
                    onBlur={handleOverlayInputBlur}
                  />
                  {capsLockOn && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 10px', borderRadius: 6, background: 'rgba(245, 158, 11, 0.08)', border: '1px solid rgba(245, 158, 11, 0.2)' }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#d97706" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2l7 10H5l7-10z" /><rect x="9" y="14" width="6" height="6" rx="1" /></svg>
                      <span style={{ color: '#d97706', fontSize: '0.78rem', fontWeight: 500 }}>Caps Lock ativado — letras em maiúsculo</span>
                    </div>
                  )}
                  {newPassword.length > 0 && (() => {
                    const hasUpper = /[A-Z]/.test(newPassword);
                    const hasNumber = /[0-9]/.test(newPassword);
                    const hasSpecial = /[^A-Za-z0-9]/.test(newPassword);
                    const criteria = [hasUpper, hasNumber, hasSpecial].filter(Boolean).length;
                    const strength = newPassword.length < 8 ? 0 : criteria <= 0 ? 1 : criteria === 1 ? 2 : criteria === 2 ? 3 : 4;
                    const labels = ['', 'Fraca', 'Média', 'Boa', 'Forte'];
                    const colors = ['', '#ef4444', '#f59e0b', LOGIN_PURPLE, '#22c55e'];
                    const pct = [0, 25, 50, 75, 100];
                    return (
                      <div>
                        <div style={{ height: 4, background: 'rgba(0,0,0,0.05)', borderRadius: 2, overflow: 'hidden' }}>
                          <div style={{ height: '100%', width: `${pct[strength]}%`, background: colors[strength], borderRadius: 2, transition: 'all 0.3s' }} />
                        </div>
                        <div style={{ fontSize: '0.75rem', color: colors[strength], marginTop: 4, textAlign: 'right' }}>{labels[strength]}</div>
                      </div>
                    );
                  })()}
                  {showLegacyInlineLoginError && !showLoginStepErrorCard && error && (
                    <div style={overlayErrorStyle}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" /></svg>
                      {error}
                    </div>
                  )}
                </div>
              )}

              {false && cardMode === 'login' && emailStep === 5 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16, alignItems: 'center', justifyContent: 'center', minHeight: 180, animation: 'acm-fadeIn 0.3s ease' }}>
                  <div style={{ width: 64, height: 64, borderRadius: 22, background: 'rgba(16, 185, 129, 0.1)', border: '2px solid rgba(16, 185, 129, 0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 8 }}>
                    <ShieldCheck size={32} style={{ color: '#10b981' }} />
                  </div>
                  <h3 style={{ fontSize: '1.1rem', fontWeight: 700, color: '#1e293b', margin: 0 }}>Cadastro Concluído!</h3>
                  <p style={{ fontSize: '0.8rem', color: '#64748b', margin: 0, textAlign: 'center', maxWidth: 300, lineHeight: 1.5 }}>
                    Etapa de seguranca removida deste formulario.
                  </p>
                  <div style={{ display: 'flex', gap: 12, width: '100%', marginTop: 8 }}>
                    <button type="button" onClick={() => {
                      if (tempAuthData) {
                        const currentUser = useAuthStore.getState().user;
                        if (currentUser) {
                          login(tempAuthData.jwt, currentUser);
                          sessionStorage.setItem('ac_auth_method', 'password');
                        }
                        navigate(tempAuthData.route, { replace: true });
                      }
                    }} style={{ flex: 1, background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: 20, padding: '10px 16px', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer', transition: 'background 0.2s' }} onMouseEnter={e => e.currentTarget.style.background = '#e2e8f0'} onMouseLeave={e => e.currentTarget.style.background = '#f1f5f9'}>
                      Pular
                    </button>
                    <button type="button" onClick={async () => {
                      if (!tempAuthData) return;
                      setIsLoading(true);
                      setError('');
                      try {
                        const requestOptions = { bearerToken: tempAuthData.jwt };
                        const regData = await requestPasskeyRegistrationOptions(undefined, requestOptions);
                        const opts = unwrapPasskeyOptions(regData);
                        const attestation = await startRegistration({ optionsJSON: opts as any });
                        const verifyResult = await verifyPasskeyRegistration(attestation, undefined, requestOptions);
                        if (verifyResult.ok === false) throw new Error('Falha ao verificar assinatura biométrica.');
                        markPasskeyOnThisDevice(email);
                        sessionStorage.setItem('ac_accesskey_verified', 'true');
                      } catch (err: any) {
                        console.error('Falha ao cadastrar biometria:', err);
                      } finally {
                        setIsLoading(false);
                        const currentUser = useAuthStore.getState().user;
                        if (currentUser) {
                          login(tempAuthData.jwt, currentUser);
                          sessionStorage.setItem('ac_auth_method', 'password');
                        }
                        navigate(tempAuthData.route, { replace: true });
                      }
                    }} style={{ flex: 1, background: LOGIN_PURPLE, color: '#fff', border: 'none', borderRadius: 20, padding: '10px 16px', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer', transition: 'background 0.2s' }} onMouseEnter={e => e.currentTarget.style.background = LOGIN_PURPLE_HOVER} onMouseLeave={e => e.currentTarget.style.background = LOGIN_PURPLE}>
                      Cadastrar
                    </button>
                  </div>
                </div>
              )}

              {/*#region AC_BLOCK login.recovery-step-ui -- UI de recuperacao de senha*/}
              {/* @ac-purpose Renderiza os campos dos cards de recuperacao seguindo o padrao visual do login por etapas. */}
              {/* @ac-related docs_secutiry/execucao-lote-45-biometria-mobile-login-ux.md#Execucao-realizada */}
              {/* @ac-related react-app/src/pages/auth/LoginPage.tsx#renderEmailOverlay */}
              {/* @ac-routes /login */}
              {/* @ac-tests node tools/codex-kit.mjs check react-tsc */}
              {cardMode === 'recover-request' && (
                <div style={{ position: 'relative', animation: 'acm-slideRight 0.3s ease' }}>
                  {loginStepErrorCard}
                  <div style={loginFieldShellStyle} aria-hidden={showLoginStepErrorCard}>
                    <input
                      ref={emailInputRef}
                      type="email" placeholder="E-mail cadastrado" name="recover-email"
                      required value={email} autoFocus
                      onChange={(e) => { e.currentTarget.setCustomValidity(''); setEmail(e.target.value); setError(''); }}
                      style={overlayInputStyle}
                      onFocus={handleOverlayInputFocus}
                      onBlur={handleOverlayInputBlur}
                    />
                  </div>
                  {showLegacyInlineLoginError && !showLoginStepErrorCard && error && (
                    <div style={overlayErrorStyle}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" /></svg>
                      {error}
                    </div>
                  )}
                </div>
              )}

              {cardMode === 'recover-confirm-email' && (
                <div style={{ position: 'relative', animation: 'acm-slideRight 0.3s ease' }}>
                  {loginStepErrorCard}
                  <div style={loginFieldShellStyle} aria-hidden={showLoginStepErrorCard}>
                    <div style={{
                      background: '#ffffff',
                      borderRadius: 14,
                      border: `1.5px solid ${LOGIN_PURPLE_BORDER}`,
                      boxShadow: overlayIsMobile ? '0 8px 18px rgba(15, 23, 42, 0.06)' : undefined,
                      padding: '16px 16px',
                      color: '#0f172a',
                    }}>
                      <div style={{ fontSize: '0.82rem', fontWeight: 700, color: '#64748b', marginBottom: 6 }}>
                        Código de recuperação
                      </div>
                      <div style={{ fontSize: '1rem', fontWeight: 750, color: '#1e293b', wordBreak: 'break-word' }}>
                        {pendingRecoveryEmail || email}
                      </div>
                      <div style={{ fontSize: '0.8rem', color: '#64748b', lineHeight: 1.4, marginTop: 8 }}>
                        Enviaremos uma mensagem com o código para este e-mail. Confirme somente se ele estiver correto.
                      </div>
                    </div>
                  </div>
                  {showLegacyInlineLoginError && !showLoginStepErrorCard && error && (
                    <div style={overlayErrorStyle}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" /></svg>
                      {error}
                    </div>
                  )}
                </div>
              )}

              {cardMode === 'recover-code' && (
                <div style={{ position: 'relative', animation: 'acm-slideRight 0.3s ease' }}>
                  {loginStepErrorCard}
                  <div style={loginFieldShellStyle} aria-hidden={showLoginStepErrorCard}>
                    <div style={{ display: 'flex', gap: 8, justifyContent: 'center', position: 'relative', minHeight: 48 }}>
                      <input
                        ref={recoverCodeInputRef}
                        type="text"
                        name="recover-code"
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        maxLength={6}
                        value={recoverCode}
                        autoFocus
                        onChange={(e) => {
                          setRecoverCode(e.target.value.replace(/\D/g, '').slice(0, 6));
                          setError('');
                        }}
                        style={{
                          position: 'absolute',
                          inset: 0,
                          opacity: 0,
                          cursor: 'pointer',
                          width: '100%',
                          height: '100%',
                          zIndex: 10,
                        }}
                        onFocus={handleOverlayInputFocus}
                        onBlur={handleOverlayInputBlur}
                      />
                      {[0, 1, 2, 3, 4, 5].map((index) => {
                        const digit = recoverCode[index] || '';
                        const isFocused = recoverCode.length === index;
                        return (
                          <div
                            key={index}
                            style={{
                              width: 44,
                              height: 48,
                              borderRadius: 8,
                              border: `2px solid ${isFocused ? '#a78bfa' : 'rgba(255,255,255,0.15)'}`,
                              background: 'rgba(255,255,255,0.06)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '1.5rem',
                              fontWeight: 700,
                              color: '#ffffff',
                              boxShadow: isFocused ? '0 0 10px rgba(167,139,250,0.3)' : 'none',
                              transition: 'all 0.15s ease',
                            }}
                          >
                            {digit}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Resend button / timer */}
                  <div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    marginTop: 16,
                  }}>
                    {resendTimer > 0 ? (
                      <span style={{ fontSize: '0.82rem', color: '#94a3b8', opacity: 0.8 }}>
                        Reenviar código em <strong style={{ color: '#ffffff' }}>{resendTimer}s</strong>
                      </span>
                    ) : (
                      <button
                        type="button"
                        onClick={async () => {
                          if (isSelfRegistering) {
                            await handleStartSelfRegistration(email);
                          } else {
                            await handleConfirmEmailRecovery();
                          }
                        }}
                        style={{
                          background: 'transparent',
                          border: 'none',
                          color: '#a78bfa',
                          cursor: 'pointer',
                          fontSize: '0.85rem',
                          fontWeight: 600,
                          padding: '4px 8px',
                          textShadow: '0 0 10px rgba(167,139,250,0.3)',
                          transition: 'opacity 0.2s',
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
                        onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
                      >
                        Reenviar código
                      </button>
                    )}
                  </div>

                  {showLegacyInlineLoginError && !showLoginStepErrorCard && error && (
                    <div style={overlayErrorStyle}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" /></svg>
                      {error}
                    </div>
                  )}
                </div>
              )}

              {cardMode === 'recover-reset' && (
                <div style={{ position: 'relative', animation: 'acm-slideRight 0.3s ease', display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {loginStepErrorCard}
                  <div style={{ ...loginFieldShellStyle, display: 'flex', flexDirection: 'column', gap: 12 }} aria-hidden={showLoginStepErrorCard}>
                    <input
                      ref={recoverNewPasswordInputRef}
                      type="password" placeholder="Nova senha" name="recover-new-password"
                      autoComplete="new-password"
                      required value={recoverNewPassword} autoFocus
                      onChange={(e) => { setRecoverNewPassword(e.target.value); setError(''); }}
                      style={overlayInputStyle}
                      onFocus={handleOverlayInputFocus}
                      onBlur={handleOverlayInputBlur}
                    />
                    <input
                      type="password" placeholder="Confirmar nova senha" name="recover-confirm-password"
                      autoComplete="new-password"
                      required value={recoverConfirmPassword}
                      onChange={(e) => { setRecoverConfirmPassword(e.target.value); setError(''); }}
                      style={overlayInputStyle}
                      onFocus={handleOverlayInputFocus}
                      onBlur={handleOverlayInputBlur}
                    />
                  </div>
                  {showLegacyInlineLoginError && !showLoginStepErrorCard && error && (
                    <div style={overlayErrorStyle}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" /></svg>
                      {error}
                    </div>
                  )}
                </div>
              )}
              {/*#endregion AC_BLOCK login.recovery-step-ui*/}
            </div>

            {cardMode === 'login' && emailStep !== 5 && (
              <div style={{
                display: 'flex',
                flexDirection: isMobile && isFixedLoginActionStep ? 'row' : (isMobile ? 'column' : 'row'),
                justifyContent: isFixedLoginActionStep ? 'flex-start' : (isMobile ? 'flex-start' : 'flex-end'),
                marginTop: isMobile ? 20 : 40,
                alignItems: isMobile && isFixedLoginActionStep ? 'center' : (isMobile ? 'stretch' : 'center'),
                gap: isMobile ? 10 : 16,
              }}>
                {isEmailActionStep && (
                  <button
                    type="button"
                    aria-label="Fechar identificacao de conta"
                    disabled={isLoading}
                    onClick={() => {
                      closeEmailLogin();
                      setEmail('');
                      setPassword('');
                      setNewName('');
                      setNewPassword('');
                      setConfirmPassword('');
                      setError('');
                      setEmailStatus('idle');
                      setEmailStep(1);
                    }}
                    style={{
                      width: 44,
                      height: 44,
                      border: 'none',
                      background: 'transparent',
                      color: '#a78bfa',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: isLoading ? 'not-allowed' : 'pointer',
                      opacity: isLoading ? 0.35 : 0.6,
                      padding: 0,
                      flex: '0 0 auto',
                      transition: 'opacity 0.2s',
                    }}
                    onMouseEnter={e => { if (!isLoading) e.currentTarget.style.opacity = '0.9'; }}
                    onMouseLeave={e => { if (!isLoading) e.currentTarget.style.opacity = '0.6'; }}
                  >
                    <ArrowLeft size={24} />
                  </button>
                )}
                {isPasswordActionStep && (
                  <button
                    type="button"
                    aria-label="Voltar para alterar e-mail"
                    disabled={isLoading || isWaitingPushApproval}
                    onClick={returnToEmailStep}
                    style={{
                      width: 44,
                      height: 44,
                      border: 'none',
                      background: 'transparent',
                      color: '#a78bfa',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: (isLoading || isWaitingPushApproval) ? 'not-allowed' : 'pointer',
                      opacity: (isLoading || isWaitingPushApproval) ? 0.35 : 0.6,
                      padding: 0,
                      flex: '0 0 auto',
                      transition: 'opacity 0.2s',
                    }}
                    onMouseEnter={e => { if (!isLoading && !isWaitingPushApproval) e.currentTarget.style.opacity = '0.9'; }}
                    onMouseLeave={e => { if (!isLoading && !isWaitingPushApproval) e.currentTarget.style.opacity = '0.6'; }}
                  >
                    <ArrowLeft size={24} />
                  </button>
                )}
                <button data-testid="login-submit-button" type="submit" disabled={primaryButtonDisabled} aria-disabled={primaryButtonVisualDisabled} style={{
                  background: primaryButtonVisualDisabled ? disabledBtnBg : activeBtnBg,
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: isMobile ? 14 : 20,
                  padding: isMobile ? '14px 18px' : '10px 24px',
                  width: isFixedLoginActionStep
                    ? (isMobile ? 154 : 148)
                    : (isMobile ? '100%' : undefined),
                  minWidth: isFixedLoginActionStep
                    ? (isMobile ? 154 : 148)
                    : undefined,
                  flex: '0 0 auto',
                  fontSize: '0.9rem',
                  fontWeight: isMobile ? 700 : 500,
                  cursor: primaryButtonDisabled ? 'not-allowed' : 'pointer',
                  transition: 'background 0.2s',
                  opacity: primaryButtonVisualDisabled ? 0.76 : 1,
                  order: isEmailActionStep ? 3 : undefined,
                  marginLeft: isFixedLoginActionStep ? 'auto' : undefined,
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8
                }}
                  onClick={() => {
                    if (isEmailActionStep && !emailLooksValid) {
                      reportEmailInputValidity();
                    }
                    if (isPasswordActionStep && !password.trim()) {
                      reportPasswordInputValidity();
                    }
                  }}
                  onMouseEnter={e => { if (!primaryButtonVisualDisabled) e.currentTarget.style.background = hoverBtnBg }}
                  onMouseLeave={e => { if (!primaryButtonVisualDisabled) e.currentTarget.style.background = activeBtnBg }}>
                  {currentEmailStatus === 'checking' || isLoading ? (
                    <>
                      <Loader2 size={16} style={{ animation: 'lk-spin 1s linear infinite' }} />
                      <span>{isGreenButton ? 'Cadastrar' : 'Avançar'}</span>
                    </>
                  ) : (
                    isGreenButton ? 'Cadastrar' : 'Avançar'
                  )}
                </button>
                {showEmailClearAction && (
                  <button
                    type="button"
                    onClick={clearEmailStepField}
                    style={{
                      background: 'transparent',
                      color: '#ffffff',
                      border: 'none',
                      padding: isMobile ? '14px 8px' : '10px 8px',
                      fontSize: '0.9rem',
                      fontWeight: 700,
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      order: isEmailActionStep ? 2 : undefined,
                      flexShrink: 0,
                      textShadow: '0 0 12px rgba(196,181,253,0.42)',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.textDecoration = 'underline'; }}
                    onMouseLeave={e => { e.currentTarget.style.textDecoration = 'none'; }}
                  >
                    Limpar
                  </button>
                )}
              </div>
            )}

            {cardMode !== 'login' && (
              <div style={{
                display: 'flex',
                justifyContent: 'flex-start',
                alignItems: 'center',
                marginTop: isMobile ? 20 : 40,
                gap: isMobile ? 10 : 16,
              }}>
                <button
                  type="button"
                  aria-label={isForgotEmailStepMode ? 'Voltar para e-mail' : 'Voltar para senha'}
                  disabled={isLoading || isWaitingPushApproval}
                  onClick={isForgotEmailStepMode ? returnForgotEmailStep : returnRecoveryToPasswordStep}
                  style={{
                    width: 44,
                    height: 44,
                    border: 'none',
                    background: 'transparent',
                    color: '#a78bfa',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: (isLoading || isWaitingPushApproval) ? 'not-allowed' : 'pointer',
                    opacity: (isLoading || isWaitingPushApproval) ? 0.35 : 0.6,
                    padding: 0,
                    flex: '0 0 auto',
                    transition: 'opacity 0.2s',
                  }}
                  onMouseEnter={e => { if (!isLoading && !isWaitingPushApproval) e.currentTarget.style.opacity = '0.9'; }}
                  onMouseLeave={e => { if (!isLoading && !isWaitingPushApproval) e.currentTarget.style.opacity = '0.6'; }}
                >
                  <ArrowLeft size={24} />
                </button>
                {isForgotEmailStepMode ? (
                  <div />
                ) : cardMode === 'recover-confirm-email' ? (
                  <button
                    type="button"
                    onClick={() => {
                      setCardMode('recover-request');
                      setRecoveryDebugCode('');
                      setError('');
                      setSuccessMessage('');
                    }}
                    style={{ background: 'transparent', border: 'none', color: isMobile ? '#ffffff' : LOGIN_PURPLE, cursor: 'pointer', fontSize: '0.85rem', fontWeight: isMobile ? 750 : 500, padding: 0, textShadow: isMobile ? '0 0 14px rgba(196,181,253,0.42)' : undefined }}
                  >
                    Alterar email
                  </button>
                ) : <div />}

                {canShowRecoveryDebugCode && (
                  <span
                    aria-label="Codigo gerado em ambiente de teste"
                    style={{
                      marginLeft: 'auto',
                      height: 40,
                      padding: '0 12px',
                      borderRadius: 12,
                      border: '1px solid rgba(221,214,254,0.32)',
                      background: 'rgba(255,255,255,0.12)',
                      color: '#ffffff',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 6,
                      fontSize: '0.78rem',
                      fontWeight: 800,
                      letterSpacing: '0.02em',
                      boxShadow: '0 10px 22px rgba(17,7,45,0.18)',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    <span style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace' }}>{recoveryDebugCode}</span>
                  </span>
                )}

                <button type="submit" disabled={isLoading} style={{
                  background: isLoading ? LOGIN_PURPLE_DISABLED : LOGIN_PURPLE, color: '#ffffff', border: 'none', borderRadius: isMobile ? 14 : 20, padding: isMobile ? '14px 18px' : '10px 24px',
                  width: isMobile ? 154 : undefined,
                  minWidth: isMobile ? 154 : undefined,
                  marginLeft: canShowRecoveryDebugCode ? 0 : 'auto',
                  fontSize: '0.9rem', fontWeight: 500, cursor: isLoading ? 'wait' : 'pointer', transition: 'background 0.2s',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                  opacity: isLoading ? 0.7 : 1
                }}
                  onMouseEnter={e => { if (!isLoading) e.currentTarget.style.background = LOGIN_PURPLE_HOVER }}
                  onMouseLeave={e => { if (!isLoading) e.currentTarget.style.background = LOGIN_PURPLE }}>
                  {isLoading ? (
                    <>
                      <Loader2 size={16} style={{ animation: 'lk-spin 1s linear infinite' }} />
                      <span>Avançar</span>
                    </>
                  ) : cardMode === 'recover-confirm-email' ? 'Confirmar envio' : cardMode === 'forgot-email-confirm' ? 'Usar este e-mail' : 'Avançar'}
                </button>
              </div>
            )}
          </form>
        </div>
        {mobileLoginBrandFooter}
      </div>
    );
  }

  // ═══════════════════════════════════════════════════
  // Modal de Sessão Substituída (Acesso Duplicado)
  // ═══════════════════════════════════════════════════
  function renderReplacedModal() {
    const formatDate = (isoStr?: string) => {
      if (!isoStr) return '';
      try {
        return new Date(isoStr).toLocaleString('pt-BR', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit'
        });
      } catch {
        return isoStr;
      }
    };

    return (
      <Modal
        open={showReplacedModal}
        onClose={() => setShowReplacedModal(false)}
        title="Acesso Duplicado Detectado"
        size="default"
        footer={
          <div style={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
            <button
              type="button"
              style={{
                background: 'linear-gradient(135deg, #f59e0b, #ef4444)',
                border: 'none',
                color: '#fff',
                padding: '8px 24px',
                borderRadius: 10,
                fontWeight: 700,
                fontSize: '0.8rem',
                cursor: 'pointer',
                boxShadow: '0 8px 20px rgba(239, 68, 68, 0.28)'
              }}
              onClick={() => setShowReplacedModal(false)}
            >
              Entendido e Prosseguir
            </button>
          </div>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16, padding: '10px 0', textAlign: 'center' }}>
          <div style={{
            width: 56,
            height: 56,
            borderRadius: '50%',
            background: 'rgba(239, 68, 68, 0.1)',
            border: '2px solid rgba(239, 68, 68, 0.3)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#ef4444',
            boxShadow: '0 8px 24px rgba(239, 68, 68, 0.15)'
          }}>
            <Monitor size={28} strokeWidth={2.2} />
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <h4 style={{ fontSize: '1rem', fontWeight: 800, color: '#0f172a', margin: 0 }}>
              Sua sessão foi substituída
            </h4>
            <p style={{ fontSize: '0.82rem', color: '#64748b', margin: 0, lineHeight: 1.45 }}>
              Outro login foi realizado utilizando as suas credenciais, o que encerrou a sua conexão ativa neste dispositivo.
            </p>
          </div>

          {replacedDetails && (
            <div style={{
              width: '100%',
              background: '#f8fafc',
              border: '1px solid #e2e8f0',
              borderRadius: 12,
              padding: 12,
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
              textAlign: 'left',
              boxSizing: 'border-box'
            }}>
              <span style={{ fontSize: '0.68rem', fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Detalhes do Novo Acesso:
              </span>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: '0.78rem', color: '#334155' }}>
                {replacedDetails.device && (
                  <div>
                    <strong>Dispositivo:</strong> <span style={{ color: '#475569' }}>{replacedDetails.device}</span>
                  </div>
                )}
                {replacedDetails.ip && (
                  <div>
                    <strong>Endereço IP:</strong> <code style={{ background: '#e2e8f0', padding: '1px 5px', borderRadius: 4, fontSize: '0.72rem' }}>{replacedDetails.ip}</code>
                  </div>
                )}
                {replacedDetails.at && (
                  <div>
                    <strong>Data/Hora:</strong> <span style={{ color: '#475569' }}>{formatDate(replacedDetails.at)}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          <div style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 10,
            padding: 12,
            background: '#fffbeb',
            border: '1px solid #fde68a',
            borderRadius: 12,
            textAlign: 'left',
            boxSizing: 'border-box',
            width: '100%'
          }}>
            <AlertTriangle size={18} style={{ color: '#d97706', flexShrink: 0, marginTop: 1 }} />
            <p style={{ margin: 0, fontSize: '0.75rem', color: '#b45309', lineHeight: 1.4 }}>
              <strong>Atenção:</strong> Se você não realizou esse acesso, sua senha pode ter sido exposta. Recomendamos que você altere sua senha e revise os dispositivos autorizados imediatamente.
            </p>
          </div>
        </div>
      </Modal>
    );
  }

  // ═══════════════════════════════════════════════════
  // Dialog de perfis demo (ativado por triple-click no logo)
  // ═══════════════════════════════════════════════════
  function renderDemoDialog() {
    // Desativado por seguranca em producao
    return null;
  }
}
